import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ScrollArea from "../ScrollArea.jsx";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { shouldResetFilesScrollSnapshot } from "../../lib/filesScrollRestoration.js";
import Sheet from "../Sheet.jsx";
import TabStrip from "../TabStrip.jsx";
import TraceTable from "../TraceTable.jsx";
import ScanWindowScrubber, { useScanWindow } from "../ScanWindowScrubber.jsx";
import Select from "../Select.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { llmErrorCopy } from "../../lib/llmErrorCopy.js";
import { formatCompact } from "../../lib/renderers.js";
import { scopeStepLabel, encodeScopePath, useLabels } from "../../lib/dimensionDefs.js";
import { buildTermRank, summaryHasTerm } from "../../lib/termRank.js";
import { archFrame, buildArch, cooccurrence, archRibbonPath } from "../../lib/termArch.js";
import { observeSettledSize } from "../../lib/settledResize.js";
import { useQuery, query, useDataEpoch } from "../../lib/dataClient.js";
import { hasPermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";
import { pickDeepSessions, windowsFromBodies, windowExtentNote as windowExtentNoteOf } from "../../lib/xrayFactoryCorpus.js";
import { judgeClusters, refineRuntimeLens, draftBehaviourArtifact } from "../../lib/xrayFactoryRun.js";
import { collectWindowOps, buildFileTree, reconstructFile, classifyLine, extractFileOps, extractSummaryFileOps } from "../../lib/traceFileTree.js";
import { extractLinearOps, collectWindowLinearOps, buildLinearActivity, issueDescDoc, issueCommentDocs } from "../../lib/traceLinearOps.js";
import { extractWebResearchOps, collectWindowWebResearchOps, buildWebResearchActivity, pageDoc } from "../../lib/traceWebResearch.js";
import { extractForgeOps, collectWindowForgeOps, buildForgeActivity, targetDescDoc, targetCommentDocs, targetDiffDocs, logObsDoc, opObservedDoc } from "../../lib/traceForgeOps.js";
import { collectWindowSkillActivity } from "../../lib/traceSkillRuns.js";
import { xraySubstrates, substrateStepFor } from "../../lib/xraySubstrates.js";
import ManifestXrayPanel from "./ManifestXrayPanel.jsx";
import StructuredXrayPanel from "./StructuredXrayPanel.jsx";
import { loadClusterTree } from "../../lib/clusterTree.js";
import {
  buildTopicFlows, indexTopicPairs, sessionEndpoints, sessionSteer,
  steeredPairRows, topicEvidenceSessions, topicScopePlan,
  STEER_SESSION_CAP, TOPIC_EVIDENCE_SESSION_CAP, TOPIC_FLOW_DEFAULTS,
} from "../../lib/topicFlows.js";
import { fetchEndpointHostnames, fetchSessionTopicPairs } from "../../lib/topicFlowsData.js";
import { AnalyticsDenialError } from "../../lib/analyticsClient.js";
import { HOST_WIRED } from "../../../xray-factory/xrays/index.js";
import { xrayRegistry, useXrayRegistry } from "../../lib/xrayRegistry.js";
import {
  sampleWhen, FilterChip, PLUS_STYLE, MINUS_STYLE, DiffBlock, HoverCard,
  ProvenanceLegend, timeAndSummary, AnnotatedFileBody, ResponseBlock,
  RecallConsultationDetail,
} from "../tracePayloads.jsx";

// The catalog comes from lib/xrayRegistry, resolved lazily because runtime lenses
// are tenant-scoped and cannot be read at module load. It is rebuilt on a mount —
// eagerly, so every consumer in one paint reads the same object, since a catalog
// two components disagree about makes residual incomparable between them.

// What the catalog already reads, handed to the factory's judge so a candidate an
// existing extractor recovers comes back as `already-covered` rather than as a
// lens worth building twice. Read off the registry plus the host's own list —
// NOT a third hand-maintained array, which is the shape that rots.
const coveredLensIdsOf = (reg) => [...HOST_WIRED, ...reg.lenses.map((m) => m.id)];
import { extractArtifactSignals, buildArtifactActivity, artifactBackingOps, extractShellOps, resolveArtifactSources, describeArtifactSource, recSourceOps } from "../../lib/traceArtifacts.js";
import { extractGrafanaOps, extractDataQueryOps, collectWindowQueryOps, buildQueryActivity, parseGrafanaResult } from "../../lib/traceQueryOps.js";
import { extractRecallOps, collectWindowRecallOps, buildRecallActivity } from "../../lib/traceRecallOps.js";
import {
    fileNodes, linearNodes, webResearchNodes, forgeNodes, queryNodes, recallNodes, artifactNodes,
    factoryNodes, buildProvenance,
} from "../../lib/traceProvenance.js";
import { commonPathPrefix, relOf, nodeShortLabel, computeFlowLayout } from "../../lib/lineageView.js";
import { LineageArc, LineageArch, LineageLanes, LineageMatrix, LineageDepth } from "./LineageViews.jsx";
import HoverTooltip from "../HoverTooltip.jsx";
import { hydratePrompt } from "../../lib/triggers/triggerHarness.js";
import { blameViewKind } from "../../lib/traceLineBlame.js";
import { rpath, fsRowsToPromptEvents, pickFsAuthor } from "../../lib/fsProvenance.js";
import { runSubAgent } from "../../lib/subAgent.js";
import { mineWorkflows } from "../../lib/workflowMining.js";
import LaunchCard from "../LaunchCard.jsx";
import WorkflowsPanel from "./WorkflowsPanel.jsx";
import XrayFactoryPanel from "./XrayFactoryPanel.jsx";
import WikiPanel from "./WikiPanel.jsx";
import { runWikiGeneration, describeRange } from "../../lib/wikiXray.js";
import { applyGeneration } from "../../lib/wikiStore.js";
import { getChatCreds, isChatConfigured } from "../../lib/chatCreds.js";
import { getRankedModels, defaultTaskModelFor } from "../../lib/modelCatalog.js";

const SCAN_LIMIT = 120;
// Line blame reads the file's fs.write history over the signal's retention
// window (signal_tags has a 30-day TTL), not the UI range — the author of a
// line is usually older than the scan window you're looking at.
const FS_BLAME_SECONDS = 30 * 86400;
// Cap the summaries handed to the describe agent — the window can be
// scrubbed up to 1000 prompts and one-liners add up.
const DESCRIBE_CAP = 400;
const HYDRATE_CONCURRENCY = 6;

// event_data arrives pre-parsed from the gateway; tolerate the string shape too, in
// case that changes. A body that will not parse yields no ref, and the scan falls
// back to per-prompt hydration for that row rather than dropping it.



// X-ray SCOPE lens — available at EVERY scope as a toggle beside the
// overview: what is this slice of traffic actually ABOUT? The surface
// fills the lens below the hero; every read is a tab that scrolls its
// own content, and the scan-window scrubber rides the Sheet's foot
// (it selects the window for all of them). The reads:
//
//   • Terms — the window's vocabulary as a chord ARCH: terms selected by
//     contrast (lift) against the org-wide window (lib/termRank) run
//     alphabetically as segments along a half-ellipse — length = prompt
//     volume, tint = lift — with co-occurrence ribbons through the dome
//     connecting terms that share prompts (lib/termArch). Rides the getTraces
//     page, no per-prompt hydration. Hovering focuses a term's
//     neighborhood; clicking filters the trace log.
//   • Behaviours — the X-ray discovery pipeline, run against this scope
//     rather than an exported corpus (lib/xrayFactoryCorpus +
//     xrayFactoryRun, over the xray-factory package's instruments).
//     The ONE tab that does not scan the shared window: it re-selects
//     the DEEPEST sessions in it, because the newest-N sample every
//     other tab wants is what the factory's discover gate refuses.
//     Reads what agents were OFFERED (tool definitions, straight out
//     of the raw bodies, plus the offered-never-called gap), clusters
//     shell work into recurring shapes, and — run-gated, one model
//     call — ranks those against each other into verdicts that each
//     name a reader, a decision, an axis and a falsifier. Nothing is
//     built here; the output is a judgement about what would be worth
//     building, and "nothing was" is a valid one.
//   • Files — the file tree the window's agents actually touched,
//     reconstructed from the same hydrated bodies: tool calls are
//     extracted per prompt, de-duplicated across each session's
//     cumulative bodies (the same call re-appears in every later
//     request), and folded into per-endpoint path trees with effective
//     ± diff stats per file (lib/traceFileTree). Rendered as a PR-style
//     split view: nav tree left (click = jump), every file's captured
//     ops directly exposed right — reads as the observed content at its
//     true line offsets, Write = whole new body, Edit = LCS-diffed
//     hunks. Hovering a file header lists the traces that touched it
//     (each opens in the detail panel); clicking it opens the FULL
//     reconstruction — the replayed content with unobserved gaps.
//   • Linear — who reads and writes Linear issues: MCP Linear tool
//     calls (get_issue / save_issue / save_comment / …, any server
//     alias) extracted from the same hydrated bodies and de-duplicated
//     the same way, aggregated per PROJECT → issue (the project each
//     issue resolves to, gleaned from the full-issue results) and per
//     actor endpoint (lib/traceLinearOps). Rendered like Files — a
//     project → issue nav tree left (click = jump), the full content
//     stream right: every op's captured body (comment bodies,
//     descriptions, state flips) in full, each linking to its trace;
//     clicking an issue header opens its timeline in the detail panel.
//   • Describe — when the tenant has a chat LLM configured, a sub-agent
//     reads every summary in the window and writes a natural-language
//     account (dominant activity, sub-threads, anomalies). Run-gated
//     (never auto-fires), cached per scope+window, with a model picker
//     for windows too big for the task model's context.
//   • Prompts — the raw trace listing behind all of the above, toggled
//     from the SCRUBBER BAR rather than the tab strip: the listing is
//     exactly what the brush selects, so its entry point sits beside
//     the window count. The analyses are the front door, the base
//     prompts are the receipts. Term/template clicks filter the
//     listing; the active filter surfaces as a chip ON the bar (beside
//     the toggle's count), so it's visible and clearable from any tab
//     rather than silently narrowing the window from inside one.

// The Terms tab body — the ranked terms as a chord ARCH (lib/termArch):
// each term is a segment along a half-ellipse whose LENGTH is its prompt
// volume, ribbons through the dome connect co-occurring terms with width
// = shared prompts, and the segment tint carries lift. Hovering a term
// focuses its ribbons and partners; clicking filters the trace log. The
// arch flexes with the panel — shallow when short, rounder with room —
// so there is no aspect ratio it can letterbox.
// Pre-measure fallback only — the panel fills the lens and the measured
// height drives the real render.
const ARCH_HEIGHT = 320;
// A short arch trims the low-count tail — its sliver segments crowd their
// radial labels; a tall one has room for the full set.
const ARCH_INLINE_TERMS = 30;
const ARCH_FULL_SET_MIN_H = 480;

// Lift → how green the term's stretch of arch reads. Hard steps via
// color-mix; no baseline (org scope) sits on the neutral middle step.
function archTint(lift) {
  const pct = lift == null ? 42 : lift >= 8 ? 100 : lift >= 4 ? 80 : lift >= 2 ? 60 : lift >= 1 ? 42 : 30;
  return `color-mix(in srgb, var(--green) ${pct}%, var(--line-soft))`;
}

const liftLabel = (lift) => `×${lift >= 10 ? Math.round(lift) : lift.toFixed(1)}`;

function TermArchPanel({ ranked, summaries, selected, onSelect, pending }) {
  const hostRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null);
  // The host div only exists once the window has loaded AND terms exist —
  // the pending/empty branches below render without it, so the observer
  // re-arms when it appears (a mount-only effect would strand `size`).
  const hasHost = !pending && ranked.terms.length > 0;
  useEffect(() => {
    if (!hasHost) return undefined;
    const node = hostRef.current;
    if (!node) return undefined;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setSize({ w: Math.max(320, Math.round(rect.width)), h: Math.round(rect.height) });
    };
    measure();
    return observeSettledSize(node, measure);
  }, [hasHost]);

  const showAllTerms = (size.h || 0) >= ARCH_FULL_SET_MIN_H;
  const terms = useMemo(
    () => (showAllTerms ? ranked.terms : ranked.terms.slice(0, ARCH_INLINE_TERMS)),
    [ranked, showAllTerms],
  );
  const arch = useMemo(
    () => buildArch(terms, cooccurrence(summaries, terms.map((t) => t.term))),
    [summaries, terms],
  );
  const focus = hover || selected;
  const focusRibbons = useMemo(
    () => arch.ribbons.filter((r) => r.a === focus || r.b === focus),
    [arch, focus],
  );
  const neighbors = useMemo(
    () => new Set(focusRibbons.map((r) => (r.a === focus ? r.b : r.a))),
    [focusRibbons, focus],
  );

  if (pending) {
    return (
      <div className="grid gap-[6px] p-[12px]">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[18px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!ranked.terms.length) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>No summary text in this window yet.</span>;
  }

  const W = size.w || 760;
  const H = Math.max(280, size.h || ARCH_HEIGHT);
  const { cx, cy, rx, ry, psi } = archFrame(W, H);
  // buildArch hands out order parameters in [π, 0]; the frame's over-sweep
  // stretches them onto [π+ψ, −ψ] so tall panels close toward a chord
  // circle instead of stretching the dome.
  const sweep = Math.PI + 2 * psi;
  const P = (t, dr = 0) => {
    const th = -psi + (t / Math.PI) * sweep;
    const px = cx + rx * Math.cos(th);
    const py = cy - ry * Math.sin(th);
    if (!dr) return [px, py];
    const vx = px - cx;
    const vy = py - cy;
    const n = Math.sqrt(vx * vx + vy * vy) || 1;
    return [px + (vx / n) * dr, py + (vy / n) * dr];
  };
  const segPath = (a, b, dr = 0) => {
    const steps = Math.max(2, Math.ceil((a - b) * (sweep / Math.PI) * 24));
    let d = "";
    for (let i = 0; i <= steps; i++) {
      const [x, y] = P(a + (b - a) * (i / steps), dr);
      d += `${i ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)} `;
    }
    return d;
  };
  const byTerm = new Map(terms.map((t) => [t.term, t]));
  // Not terms[0].count — the ranking is score-ordered (count·log2 lift when
  // a baseline exists), so the top-ranked term need not be the most common.
  const maxCount = Math.max(...terms.map((t) => t.count));
  const focusTerm = focus ? byTerm.get(focus) : null;
  const focusPartners = focusRibbons
    .slice()
    .sort((x, y) => y.weight - x.weight)
    .map((r) => (r.a === focus ? r.b : r.a));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-w-0 border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={NOTE_CLASS}>
          {terms.length < ranked.terms.length ? `top ${terms.length} of ${ranked.terms.length} terms` : `${terms.length} terms`}
          {" · segment length: prompt volume · ribbons: shared prompts"}
          {terms.some((t) => t.lift != null) ? " · greener: more distinctive than the organization" : ""}
        </span>
      </div>
      <div ref={hostRef} className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          role="img"
          aria-label="Term co-occurrence arch. Select a term to filter the log."
          onMouseLeave={() => setHover(null)}
        >
          {arch.ribbons
            .slice()
            .sort((x, y) => x.weight - y.weight)
            .map((r) => {
              if (!r.spanA || !r.spanB) return null;
              const ma = P((r.spanA[0] + r.spanA[1]) / 2);
              const mb = P((r.spanB[0] + r.spanB[1]) / 2);
              const k = 0.58;
              const ctrl = [((ma[0] + mb[0]) / 2) * (1 - k) + cx * k, ((ma[1] + mb[1]) / 2) * (1 - k) + cy * k];
              const t = r.weight / arch.maxWeight;
              const incident = focus && (r.a === focus || r.b === focus);
              const opacity = incident ? 0.5 + 0.25 * t : focus ? 0.04 : 0.1 + 0.26 * t;
              return (
                <path
                  key={`${r.a}|${r.b}`}
                  d={archRibbonPath(P, r.spanA, r.spanB, ctrl)}
                  fill="var(--green)"
                  opacity={opacity}
                />
              );
            })}
          {arch.order.map((term) => {
            const t = byTerm.get(term);
            const { a, b } = arch.segments.get(term);
            const isFocus = term === focus;
            const isSelected = term === selected;
            const isNeighbor = neighbors.has(term);
            const [lx, ly] = P((a + b) / 2, 14);
            // Radial labels — each term owns its outward ray, so labels
            // cannot collide however round the arch gets; left-half labels
            // flip to stay right-side-up.
            const phi = (Math.atan2(ly - cy, lx - cx) * 180) / Math.PI;
            const flip = phi > 90 || phi < -90;
            const rot = flip ? phi + 180 : phi;
            const big = t.count >= maxCount * 0.4;
            const labelFill = isFocus || isSelected
              ? "var(--green)"
              : focus
                ? (isNeighbor ? "var(--ink)" : "var(--muted-soft)")
                : (big ? "var(--ink)" : "var(--muted)");
            return (
              <g
                key={term}
                style={{ cursor: "pointer" }}
                opacity={focus && !isFocus && !isNeighbor && !isSelected ? 0.4 : 1}
                onMouseEnter={() => setHover(term)}
                onClick={() => onSelect(isSelected ? null : term)}
              >
                <path d={segPath(a, b)} fill="none" stroke={archTint(t.lift)} strokeWidth={isFocus || isSelected ? 12 : 9} />
                {isSelected ? (
                  <path d={segPath(a, b, 9)} fill="none" stroke="var(--green)" strokeWidth={1.4} opacity={0.9} />
                ) : null}
                <text
                  x={lx.toFixed(1)}
                  y={(ly + 3.5).toFixed(1)}
                  textAnchor={flip ? "end" : "start"}
                  fontSize={big ? 11.5 : 10}
                  fill={labelFill}
                  fontWeight={isFocus || isSelected || big ? 500 : 400}
                  transform={`rotate(${rot.toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)})`}
                >
                  {term}
                  <tspan fill="var(--muted-soft)" fontSize="8.5">{` ${t.count}`}</tspan>
                </text>
                {/* Forgiving hit band over the segment. */}
                <path d={segPath(a, b)} fill="none" stroke="transparent" strokeWidth={24} />
              </g>
            );
          })}
        </svg>
      </div>
      <div className="border-t border-[color:var(--line-soft)] px-[12px] py-[5px]">
        {focusTerm ? (
          <span className={NOTE_CLASS}>
            <span className="text-[color:var(--ink)]">{focus}</span>
            {` · ${focusTerm.count} prompt${focusTerm.count === 1 ? "" : "s"}`}
            {focusTerm.lift != null ? ` · ${liftLabel(focusTerm.lift)} vs org` : ""}
            {focusPartners.length ? ` · shares prompts with ${focusPartners.slice(0, 4).join(", ")}${focusPartners.length > 4 ? ` +${focusPartners.length - 4}` : ""}` : " · no recurring co-occurrence"}
            {selected === focus ? " · filtering the log" : ""}
          </span>
        ) : (
          <span className={NOTE_CLASS}>Hover to trace links. Select a term to filter the log.</span>
        )}
      </div>
    </div>
  );
}

// Right-edge meta for one file row: op counts, then the effective ±.
function FileMeta({ file }) {
  const parts = [];
  if (file.reads) parts.push(`R${file.reads}`);
  if (file.edits) parts.push(`E${file.edits}`);
  if (file.writes) parts.push(`W${file.writes}`);
  if (file.errored) parts.push(`!${file.errored}`);
  return (
    <span className="flex shrink-0 items-baseline gap-[8px] text-[10px] tabular-nums text-[color:var(--muted)]">
      <span>{parts.join(" · ")}</span>
      {file.plus || file.minus ? (
        <span className="font-mono">
          {file.plus ? <span style={PLUS_STYLE}>+{file.plus}</span> : null}
          {file.plus && file.minus ? " " : ""}
          {file.minus ? <span style={MINUS_STYLE}>−{file.minus}</span> : null}
        </span>
      ) : null}
    </span>
  );
}

// The left rail of the split view — a pure navigation tree (GitHub's PR
// file-filter treatment): clicking a file scrolls the diff stream to its
// section. Dirs are already chain-compressed by buildFileTree.
function NavDirRows({ node, depth, epId, focusKey, onJump }) {
  const pad = (d) => ({ paddingLeft: `${10 + d * 12}px` });
  return (
    <>
      {[...node.dirs.values()].map((dir) => (
        <div key={dir.path} className="min-w-0">
          <div
            className="truncate px-[10px] py-[2px] font-mono text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]"
            style={pad(depth)}
          >
            {dir.name}/
          </div>
          <NavDirRows node={dir} depth={depth + 1} epId={epId} focusKey={focusKey} onJump={onJump} />
        </div>
      ))}
      {node.files.map((file) => {
        const key = `${epId}|${file.path}`;
        const modified = file.edits > 0 || file.writes > 0;
        return (
          <button
            key={file.path}
            type="button"
            onClick={() => onJump(key)}
            className={[
              "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
              focusKey === key ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
            ].join(" ")}
            style={pad(depth)}
          >
            <span
              className={[
                "min-w-0 truncate font-mono text-[11px] leading-[1.4]",
                modified ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
              ].join(" ")}
            >
              {file.name}
            </span>
            {file.plus || file.minus ? (
              <span className="shrink-0 font-mono text-[10px] tabular-nums">
                {file.plus ? <span style={PLUS_STYLE}>+{file.plus}</span> : null}
                {file.plus && file.minus ? " " : ""}
                {file.minus ? <span style={MINUS_STYLE}>−{file.minus}</span> : null}
              </span>
            ) : null}
          </button>
        );
      })}
    </>
  );
}

// Files of one endpoint tree in the same order the nav rail lists them
// (dirs first, then files, per level).
function flattenFiles(node, out = []) {
  for (const dir of node.dirs.values()) flattenFiles(dir, out);
  for (const file of node.files) out.push(file);
  return out;
}

const TRACE_CARD_ROWS = 8;

// The hover card for one file: the traces whose bodies first carried its
// ops (clickable → trace detail), plus the reconstruction shortcut.
function FileTraceCard({ file, traceOf, onSelectTrace, onOpenReconstruction }) {
  const promptIds = [];
  const seen = new Set();
  for (const op of file.ops) {
    if (!seen.has(op.promptId)) { seen.add(op.promptId); promptIds.push({ id: op.promptId, ts: op.ts }); }
  }
  const shown = promptIds.slice(0, TRACE_CARD_ROWS);
  return (
    <div className="grid min-w-0">
      <span className="border-b border-[color:var(--line-soft)] px-[10px] py-[6px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
        Touched by {promptIds.length} prompt{promptIds.length === 1 ? "" : "s"} · {file.sessions.size} session{file.sessions.size === 1 ? "" : "s"}
      </span>
      {shown.map(({ id, ts }) => {
        const row = traceOf.get(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelectTrace(id)}
            className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-baseline gap-[8px] px-[10px] py-[4px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
          >
            <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">{sampleWhen(row?.timestamp ?? ts)}</span>
            <span className="truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">{row?.summary || id}</span>
          </button>
        );
      })}
      {promptIds.length > shown.length ? (
        <span className={`${NOTE_CLASS} px-[10px] py-[3px]`}>+{promptIds.length - shown.length} more in this window</span>
      ) : null}
      <button
        type="button"
        onClick={() => onOpenReconstruction(file)}
        className="border-t border-[color:var(--line-soft)] px-[10px] py-[5px] text-left text-[11px] text-[color:var(--green)] cursor-pointer hover:bg-[color:var(--control-bg)]"
      >
        Open reconstructed file →
      </button>
    </div>
  );
}

// One file's section in the diff stream: a sticky header (hover = the
// trace card, click = the reconstruction detail) over the ANNOTATED
// reconstruction — every line carries its provenance inline — plus any
// edits that couldn't be anchored in the observed content.
function FileSection({ file, recon, refFn, traceOf, onSelectTrace, onOpenReconstruction, onBlameLine }) {
  const dirPath = file.path.endsWith(file.name) ? file.path.slice(0, file.path.length - file.name.length) : "";
  return (
    <div ref={refFn} className="min-w-0 border-b border-[color:var(--line-soft)]">
      <div className="sticky top-0 z-[2] flex items-baseline gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px]">
        <HoverCard
          card={
            <FileTraceCard
              file={file}
              traceOf={traceOf}
              onSelectTrace={onSelectTrace}
              onOpenReconstruction={onOpenReconstruction}
            />
          }
        >
          <button
            type="button"
            onClick={() => onOpenReconstruction(file)}
            className="block w-full min-w-0 truncate text-left font-mono text-[11px] leading-[1.5] cursor-pointer"
          >
            <span className="text-[color:var(--muted-soft)]">{dirPath}</span>
            <span className="font-medium text-[color:var(--ink)]">{file.name}</span>
          </button>
        </HoverCard>
        {recon?.known ? (
          <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted-soft)]">
            {recon.known}{recon.lengthKnown ? `/${recon.total}` : ""} lines
          </span>
        ) : null}
        <FileMeta file={file} />
      </div>
      {/* grid-cols-[minmax(0,1fr)] on this chain is LOAD-BEARING: a bare
          single-column grid's auto track sizes from content, and the
          blind-edit header rows (percentage-width flex rows with long trace
          summaries) contribute their full untruncated max-content width —
          the track then overflows the pane and every descendant
          overflow-x-auto is sized to the OVERSIZED track, so horizontal
          scrolling escapes to the whole stream pane (sticky headers slide
          sideways with it). min-w-0 on the grid CONTAINER cannot cap a
          track; only the explicit minmax(0,1fr) template does. */}
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-[6px] py-[6px]">
        {recon ? (
          <AnnotatedFileBody rec={recon} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={160} filePath={file.path} onBlameLine={onBlameLine} />
        ) : null}
        {recon?.blind?.length ? (
          <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-[4px]">
            <span className={`${NOTE_CLASS} px-[12px]`}>
              {recon.blindEdits} edit{recon.blindEdits === 1 ? "" : "s"} could not be anchored. Their target text was
              absent from this window or changed through the Bash channel:
            </span>
            {recon.blind.map(({ op, hunks }, i) => {
              const m = timeAndSummary(op, traceOf);
              return (
                <div key={`${op.promptId}:${i}`} className="grid min-w-0 grid-cols-[minmax(0,1fr)] gap-[2px]">
                  <button
                    type="button"
                    onClick={() => onSelectTrace(op.promptId)}
                    className="flex w-full items-baseline gap-[8px] px-[12px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
                  >
                    <span className="text-[10px] tabular-nums leading-[1.4] text-[color:var(--muted)]">{m.when}</span>
                    <span className="truncate text-[11px] leading-[1.4] text-[color:var(--ink)]">{m.summary}</span>
                    <span className="ml-auto shrink-0 text-[10px] leading-[1.4] text-[color:var(--muted-soft)]">open trace</span>
                  </button>
                  <DiffBlock hunks={hunks} op={op} traceOf={traceOf} onSelectTrace={onSelectTrace} />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// The Files tab body — GitHub-PR-style split view: nav tree on the left,
// every file's annotated reconstruction directly exposed on the right.
const scrollViewportOf = (instance) => instance?.elements?.().viewport ?? null;

function FilesPanel({
  scan,
  built,
  traceOf,
  labelOf,
  onSelectTrace,
  onOpenReconstruction,
  onBlameLine,
  navScrollInstanceRef,
  contentScrollInstanceRef,
}) {
  const fileRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  // Replay every file once so the header stats and the window total say how
  // much CONTENT is reconstructable — reads included, not just the ± churn.
  const reconOf = useMemo(() => {
    const m = new Map();
    if (!built) return m;
    for (const ep of built.endpoints) {
      for (const file of flattenFiles(ep.tree)) m.set(`${ep.id}|${file.path}`, reconstructFile(file.ops));
    }
    return m;
  }, [built]);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.status === "scanning" ? "Reading the fs signal…" : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  if (!built.totals.files) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No file activity was recorded by the fs signal in this window. Shell and external changes
        are not captured.
      </span>
    );
  }
  const t = built.totals;
  const multiEndpoint = built.endpoints.length > 1;
  const totalKnown = [...reconOf.values()].reduce((a, r) => a + r.known, 0);
  const jump = (key) => {
    fileRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocusKey(key);
  };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="grid min-w-0 gap-[6px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={NOTE_CLASS}>
          {t.files} file{t.files === 1 ? "" : "s"} touched
          {multiEndpoint ? ` across ${t.endpoints} endpoints` : ""} · {formatCompact(totalKnown)} lines of content
          reconstructable · {t.modified} modified ·{" "}
          <span style={PLUS_STYLE}>+{t.plus}</span> <span style={MINUS_STYLE}>−{t.minus}</span> effective
          {" · fs signal only"} · select a line chip to view its history
        </span>
        <ProvenanceLegend />
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea
          className="min-h-0 h-full border-r border-[color:var(--line-soft)]"
          contentClassName="py-[4px]"
          instanceRef={navScrollInstanceRef}
        >
          {built.endpoints.map((ep) => (
            <div key={ep.id || "none"} className="min-w-0 pb-[4px]">
              {multiEndpoint ? (
                <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                  {labelOf(ep)}
                </div>
              ) : null}
              <NavDirRows node={ep.tree} depth={0} epId={ep.id} focusKey={focusKey} onJump={jump} />
            </div>
          ))}
        </ScrollArea>
        <ScrollArea
          className="min-h-0 h-full"
          contentClassName="min-w-0"
          instanceRef={contentScrollInstanceRef}
        >
          {built.endpoints.map((ep) => (
            <div key={ep.id || "none"} className="min-w-0">
              {multiEndpoint ? (
                <div className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[12px] py-[5px]">
                  <span className="truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{labelOf(ep)}</span>
                  <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                    {ep.stats.files} files · <span style={PLUS_STYLE}>+{ep.stats.plus}</span>{" "}
                    <span style={MINUS_STYLE}>−{ep.stats.minus}</span>
                  </span>
                </div>
              ) : null}
              {flattenFiles(ep.tree).map((file) => (
                <FileSection
                  key={file.path}
                  file={file}
                  recon={reconOf.get(`${ep.id}|${file.path}`)}
                  refFn={(el) => {
                    const key = `${ep.id}|${file.path}`;
                    if (el) fileRefs.current.set(key, el);
                    else fileRefs.current.delete(key);
                  }}
                  traceOf={traceOf}
                  onSelectTrace={onSelectTrace}
                  onOpenReconstruction={(f) => onOpenReconstruction(f, labelOf(ep))}
                  onBlameLine={onBlameLine}
                />
              ))}
            </div>
          ))}
        </ScrollArea>
      </div>
    </div>
  );
}

// The right-panel detail for one file: its FULL annotated reconstruction —
// every line the window's traces ever put in view, replayed through writes
// and edits, each line carrying its per-line history (gutter chip → the
// version stepper), deletions in place, unobserved regions as gaps. No row
// cap here (unlike the in-stream section) — this is the deep-dive.

// Structured field chips for a Linear op — the non-prose slice of a write
// (state flip, reassignment, priority) or a read's observed status.
function LinearFieldChips({ fields }) {
  const entries = Object.entries(fields || {});
  if (!entries.length) return null;
  return (
    <span className="flex flex-wrap gap-x-[10px] gap-y-[2px]">
      {entries.map(([k, v]) => (
        <span key={k} className="font-mono text-[10px] text-[color:var(--muted)]">
          {k} → <span className="text-[color:var(--ink)]">{v}</span>
        </span>
      ))}
    </span>
  );
}

// One Linear op row — the thin activity-log line: when · action · actor ·
// field chips · open trace. Content bodies are NOT here; they render below
// through the same annotated line renderer the Files tab uses.
function LinearOpRow({ op, traceOf, labelOf, onSelectTrace }) {
  const row = traceOf.get(op.promptId);
  const actor = labelOf({ id: op.endpointId, label: op.endpointName || "" });
  return (
    <div className="grid gap-[2px] border-l-2 pl-[8px]" style={{ borderColor: op.kind === "write" ? "var(--green)" : "var(--line-soft)" }}>
      <button
        type="button"
        onClick={() => onSelectTrace(op.promptId)}
        className="flex items-baseline gap-[8px] rounded-[2px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
      >
        <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">{sampleWhen(row?.timestamp ?? op.ts)}</span>
        <span className="shrink-0 font-mono text-[10px]" style={op.kind === "write" ? PLUS_STYLE : undefined}>
          {op.create ? "create" : op.action.replace(/_/g, " ")}
        </span>
        {actor ? <span className="truncate text-[10px] text-[color:var(--muted-soft)]">{actor}</span> : null}
        {!op.ok ? <span className="shrink-0 font-mono text-[10px]" style={MINUS_STYLE}>errored</span> : null}
        <span className="ml-auto shrink-0 text-[10px] text-[color:var(--muted-soft)]">open trace →</span>
      </button>
      <LinearFieldChips fields={op.fields} />
    </div>
  );
}

// An issue's content through the SAME renderer as the Files tab: the
// description as a reconstructed document (per-line provenance chips, version
// history on hover, drift when a read disagrees with the replayed state —
// i.e. the issue changed in Linear's web UI between agent touches), and each
// comment as a one-version mini-document (written in-window vs observed).
function LinearIssueBody({ issue, traceOf, labelOf, onSelectTrace, descCap = 0 }) {
  const { descRec, comments } = useMemo(() => {
    const doc = issueDescDoc(issue.ops);
    return {
      descRec: doc.length ? reconstructFile(doc) : null,
      comments: issueCommentDocs(issue.ops).map((c) => ({ ...c, rec: reconstructFile([c.op]) })),
    };
  }, [issue]);
  return (
    <>
      <div className="grid gap-[4px]">
        {issue.ops.map((op, i) => (
          <LinearOpRow key={`${op.promptId}:${i}`} op={op} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
        ))}
      </div>
      {descRec && descRec.rows.length ? (
        <div className="grid gap-[3px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>Description</span>
          <AnnotatedFileBody rec={descRec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={descCap} bordered />
        </div>
      ) : null}
      {comments.map((c, i) => (
        <div key={i} className="grid gap-[3px]">
          <span className={NOTE_CLASS}>
            {c.written ? "comment written" : "comment observed"}
            {c.author ? ` · ${c.author}` : ""} · {sampleWhen(traceOf.get(c.op.promptId)?.timestamp ?? c.op.ts)}
          </span>
          <AnnotatedFileBody rec={c.rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={0} bordered />
        </div>
      ))}
    </>
  );
}

// One issue's full activity, for the detail panel — the Linear analog of the
// Files tab's full reconstruction: the complete description document + every
// comment, uncapped.
function LinearIssueDetail({ issue, traceOf, labelOf, onSelectTrace }) {
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]">
      <div className="grid gap-[3px]">
        <span className="flex items-baseline gap-[8px]">
          <span className="font-mono text-[12px] text-[color:var(--ink)]">{issue.id}</span>
          {issue.project ? <span className={NOTE_CLASS}>{issue.project}</span> : null}
        </span>
        {issue.title ? <span className="text-[12px] leading-[1.4] text-[color:var(--ink)]">{issue.title}</span> : null}
        <span className={NOTE_CLASS}>
          {issue.reads} read{issue.reads === 1 ? "" : "s"} · <span style={issue.writes ? PLUS_STYLE : undefined}>{issue.writes} write{issue.writes === 1 ? "" : "s"}</span>
          {issue.errored ? ` · ${issue.errored} errored` : ""} · {issue.sessions} session{issue.sessions === 1 ? "" : "s"}
        </span>
      </div>
      <LinearIssueBody issue={issue} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} descCap={0} />
    </ScrollArea>
  );
}

// One issue's section in the Linear stream — header (click = full timeline in
// the detail panel), readers/writers note, then every op with its FULL
// captured content. The stream analog of FileSection.
function LinearIssueSection({ issue, refFn, traceOf, labelOf, onSelectTrace, onOpenIssue, actorName }) {
  return (
    <div ref={refFn} className="min-w-0 border-b border-[color:var(--line-soft)]">
      {/* Sticky within its section (same mechanic as FileSection): the issue's
          identity pins to the top of the scroll viewport while its ops and
          documents scroll, then the next section's header takes over. The bar's
          background stays the OPAQUE plot-bg even on hover — control-bg is a
          translucent tint, and a translucent hover on a stuck header lets the
          content scrolling beneath bleed through it. Hover feedback lives on
          the inner text instead, like FileSection. */}
      <button
        type="button"
        onClick={() => onOpenIssue(issue)}
        className="group sticky top-0 z-[2] flex w-full items-baseline gap-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px] text-left cursor-pointer"
      >
        <span className="shrink-0 font-mono text-[11.5px] text-[color:var(--ink)]">{issue.id}</span>
        {issue.title ? <span className="truncate text-[11.5px] text-[color:var(--muted)] group-hover:text-[color:var(--ink)]">{issue.title}</span> : null}
        <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
          {issue.reads} read{issue.reads === 1 ? "" : "s"} · <span style={issue.writes ? PLUS_STYLE : undefined}>{issue.writes} write{issue.writes === 1 ? "" : "s"}</span>
          {issue.errored ? ` · ${issue.errored} errored` : ""}
        </span>
        <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]">open →</span>
      </button>
      {(issue.readers.length || issue.writers.length) ? (
        <span className={`${NOTE_CLASS} block px-[12px] pb-[4px]`}>
          {issue.writers.length ? <>written by {issue.writers.map((w) => actorName(w)).join(", ")}</> : null}
          {issue.writers.length && issue.readers.length ? " · " : null}
          {issue.readers.length ? <>read by {issue.readers.map((r) => actorName(r)).join(", ")}</> : null}
        </span>
      ) : null}
      <div className="grid gap-[8px] px-[12px] pb-[10px] pt-[2px]">
        <LinearIssueBody issue={issue} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} descCap={40} />
      </div>
    </div>
  );
}

// The Linear tab body — who reads and writes Linear issues in this window,
// laid out like the Files tab: a project → issue navigation tree on the left
// (click = jump), the full content stream on the right (every op's captured
// body, unclamped — the tree is how you skim). The Who rail at the top is a
// FILTER: clicking an actor narrows the tree + stream to that endpoint's own
// ops ("what did this machine read and write"); click again to clear. Actor
// rows keep their full-window counts while filtered, so switching actors
// never loses the map. Clicking an issue header opens its full timeline in
// the detail panel (the Files reconstruction analog).
function LinearPanel({ scan, built, actors, actorFilter, onFilterActor, traceOf, labelOf, onSelectTrace, onOpenIssue }) {
  const issueRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  if (!t.issues && !t.browses && !actorFilter) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No Linear activity was recorded in this window.
      </span>
    );
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  const jump = (key) => {
    issueRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocusKey(key);
  };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.issues} issue{t.issues === 1 ? "" : "s"}{t.projects ? ` across ${t.projects} project${t.projects === 1 ? "" : "s"}` : ""}
          {actorFilter ? "" : ` touched by ${t.actors} endpoint${t.actors === 1 ? "" : "s"}`} ·{" "}
          {t.reads} read{t.reads === 1 ? "" : "s"} · <span style={PLUS_STYLE}>{t.writes} write{t.writes === 1 ? "" : "s"}</span>
          {t.created ? ` (${t.created} created)` : ""}
          {t.browses ? ` · ${t.browses} browse call${t.browses === 1 ? "" : "s"} (lists, projects, teams)` : ""}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.reads}r{" "}<span style={a.writes ? PLUS_STYLE : undefined}>{a.writes}w</span>
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {built.projects.map((proj) => (
            <div key={proj.name || "(none)"} className="min-w-0 pb-[4px]">
              <div className="flex min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[4px]">
                <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{proj.name || "No project"}</span>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                  {proj.reads}r <span style={proj.writes ? PLUS_STYLE : undefined}>{proj.writes}w</span>
                </span>
              </div>
              {proj.issues.map((issue) => (
                <button
                  key={issue.id}
                  type="button"
                  onClick={() => jump(issue.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left cursor-pointer",
                    focusKey === issue.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                  style={{ paddingLeft: "18px" }}
                >
                  <span className={[
                    "shrink-0 font-mono text-[11px] leading-[1.4]",
                    issue.writes ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {issue.id}
                  </span>
                  {issue.title ? <span className="min-w-0 truncate text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]">{issue.title}</span> : null}
                  <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {issue.reads}r <span style={issue.writes ? PLUS_STYLE : undefined}>{issue.writes}w</span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {built.projects.length ? built.projects.map((proj) => (
            <div key={proj.name || "(none)"} className="min-w-0">
              <div className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[12px] py-[5px]">
                <span className="truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{proj.name || "No project"}</span>
                <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                  {proj.issues.length} issue{proj.issues.length === 1 ? "" : "s"} · {proj.reads}r · <span style={proj.writes ? PLUS_STYLE : undefined}>{proj.writes}w</span>
                </span>
              </div>
              {proj.issues.map((issue) => (
                <LinearIssueSection
                  key={issue.id}
                  issue={issue}
                  refFn={(el) => {
                    if (el) issueRefs.current.set(issue.id, el);
                    else issueRefs.current.delete(issue.id);
                  }}
                  traceOf={traceOf}
                  labelOf={labelOf}
                  onSelectTrace={onSelectTrace}
                  onOpenIssue={onOpenIssue}
                  actorName={actorName}
                />
              ))}
            </div>
          )) : (
            <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
              Only issue lists, projects, and teams were accessed. No specific issue was read or changed.
            </span>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

// One page's full reconstruction for the detail panel — the web-research analog
// of the Files reconstruction / Linear issue timeline. A page has no writes (the
// agent never authors it), so it's rendered from READ observations: the latest
// fetched content, a "changed between reads" banner when the page drifted, the
// extraction prompts the agent passed (what it was looking for), the queries
// that led here, and a per-fetch trace link.
function WebPageReconstruction({ page, labelOf, onSelectTrace }) {
  const recon = useMemo(() => reconstructFile(pageDoc(page.ops)), [page]);
  const prompts = [...new Set((page.ops || []).map((o) => o.prompt).filter(Boolean))];
  const okFetches = (page.ops || []).filter((o) => o.ok);
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px] min-w-0">
      <div className="min-w-0">
        <a
          href={page.url}
          target="_blank"
          rel="noreferrer noopener"
          className="block min-w-0 truncate font-mono text-[11px] text-[color:var(--muted)] hover:text-[color:var(--ink)]"
        >
          {page.url}
        </a>
      </div>
      <div className="flex flex-wrap items-baseline gap-[8px]">
        <span className={NOTE_CLASS}>
          fetched {page.fetches}× · {page.sessions} session{page.sessions === 1 ? "" : "s"}
        </span>
        {page.readers.map((r) => (
          <span key={r.id || r.name} className="font-mono text-[10px] text-[color:var(--muted-soft)]">
            {labelOf({ id: r.id, label: r.name })} ·{r.n}
          </span>
        ))}
      </div>
      {page.drift ? (
        <div className="border-l-2 border-[color:var(--line)] pl-[8px] text-[11px] font-medium text-[color:var(--ink)]">
          This page changed between reads. The latest observed content appears below.
        </div>
      ) : null}
      {page.viaQueries.length ? (
        <div className="flex flex-wrap items-center gap-[6px]">
          <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">via search</span>
          {page.viaQueries.map((q) => (
            <span key={q} className="rounded-[2px] bg-[color:var(--control-bg)] px-[6px] py-[1px] font-mono text-[10px] text-[color:var(--muted)]">{q}</span>
          ))}
        </div>
      ) : null}
      {prompts.length ? (
        <div className="grid gap-[2px]">
          <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">looking for</span>
          {prompts.map((p, i) => (
            <span key={i} className="text-[11px] leading-[1.5] text-[color:var(--muted)]">{p}</span>
          ))}
        </div>
      ) : null}
      <div className="min-w-0 rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)]">
        <div className="border-b border-[color:var(--line-soft)] px-[8px] py-[3px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
          what the agent saw
        </div>
        {/* No nested scroll: content flows into the panel's single ScrollArea
            (a competing inner scroll is the overflow anti-pattern). Wrap long
            lines so a wide URL/token never forces horizontal overflow. */}
        <pre className="m-0 min-w-0 px-[8px] py-[6px] font-mono text-[11px] leading-[1.5] text-[color:var(--ink)] whitespace-pre-wrap break-words">
          {recon.rows.map((r, i) => {
            if (r.gap) return <div key={i} className="text-[color:var(--muted-soft)]">{`⋯ ${r.gap[0] === r.gap[1] ? `line ${r.gap[0]}` : `lines ${r.gap[0]}–${r.gap[1]}`}`}</div>;
            if (r.del) return null;
            const drift = r.entry && classifyLine(r.entry) === "drift";
            return <div key={i} className={drift ? "font-medium" : undefined}>{r.text || " "}</div>;
          })}
        </pre>
      </div>
      {okFetches.length ? (
        <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
          {okFetches.length === 1 ? "trace" : `${okFetches.length} fetches`}
        </span>
      ) : null}
      <div className="grid gap-[4px]">
        {okFetches.map((o, i) => (
          <button
            key={`${o.promptId}:${i}`}
            type="button"
            onClick={() => onSelectTrace?.(o.promptId)}
            className="grid w-full gap-[2px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[5px] text-left cursor-pointer transition-colors hover:border-[color:var(--muted-soft)]"
          >
            <div className="flex min-w-0 items-center gap-[6px]">
              <span className="min-w-0 flex-1 truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">
                {labelOf({ id: o.endpointId, label: o.endpointName })}
              </span>
              <span className="shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted)]">{sampleWhen(o.ts)}</span>
            </div>
            <span className="text-[10px] leading-[1.2] text-[color:var(--muted-soft)]">{o.engine}</span>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
}

// The Research tab body — what this window's agents READ from the open web,
// laid out like the Linear tab: a host → page navigation tree on the left, the
// content stream on the right. The Who rail is a FILTER (an actor is an
// endpoint). Clicking a page header opens its full reconstruction (the content
// the agent saw) in the detail panel. A trailing Searches section lists the
// queries and which of their results were actually fetched (the search → page
// provenance edge).
function ResearchPanel({ scan, built, actors, actorFilter, onFilterActor, labelOf, onOpenPage }) {
  const pageRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  if (!t.pages && !t.searches && !actorFilter) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No web research was recorded in this window.
      </span>
    );
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  const hostLabel = (h) => h || "Direct / unknown host";
  const pageByUrl = new Map(built.pages.map((p) => [p.url, p]));
  // built.hosts includes hosts that appeared ONLY in search results (no
  // fetched page). This tree/stream is page-oriented, so render just the
  // fetched hosts — search-only domains would be empty sections and can be
  // numerous. They still surface in the Searches section and the totals.
  const fetchedHosts = built.hosts.filter((h) => h.pages.length);
  const jump = (key) => {
    pageRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocusKey(key);
  };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.pages} page{t.pages === 1 ? "" : "s"}{t.hosts ? ` across ${t.hosts} host${t.hosts === 1 ? "" : "s"}` : ""}
          {actorFilter ? "" : ` read by ${t.actors} endpoint${t.actors === 1 ? "" : "s"}`} ·{" "}
          {t.fetches} fetch{t.fetches === 1 ? "" : "es"} · {t.searches} search{t.searches === 1 ? "" : "es"}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.fetches}f {a.searches}s
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {fetchedHosts.map((h) => (
            <div key={h.host || "(none)"} className="min-w-0 pb-[4px]">
              <div className="flex min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[4px]">
                <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{hostLabel(h.host)}</span>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{h.fetches}f</span>
              </div>
              {h.pages.map((url) => {
                const p = pageByUrl.get(url);
                if (!p) return null;
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => jump(url)}
                    className={[
                      "flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left cursor-pointer",
                      focusKey === url ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                    ].join(" ")}
                    style={{ paddingLeft: "18px" }}
                  >
                    <span className="min-w-0 truncate text-[11px] leading-[1.4] text-[color:var(--muted)]">
                      {p.title || url.replace(/^https?:\/\//, "")}
                    </span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                      {p.fetches}f
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {fetchedHosts.map((h) => (
            <div key={h.host || "(none)"} className="min-w-0">
              <div className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[12px] py-[5px]">
                <span className="truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">{hostLabel(h.host)}</span>
                <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                  {h.pages.length} page{h.pages.length === 1 ? "" : "s"} · {h.fetches}f
                </span>
              </div>
              {h.pages.map((url) => {
                const p = pageByUrl.get(url);
                if (!p) return null;
                const preview = (p.ops.find((o) => o.ok && o.content)?.content || "").split("\n").slice(0, 4).join("\n");
                return (
                  <button
                    key={url}
                    type="button"
                    ref={(el) => { if (el) pageRefs.current.set(url, el); else pageRefs.current.delete(url); }}
                    onClick={() => onOpenPage(p)}
                    className="block w-full min-w-0 border-b border-[color:var(--line-soft)] px-[12px] py-[7px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
                  >
                    <div className="flex w-full min-w-0 items-baseline gap-[8px]">
                      <span className="min-w-0 truncate text-[12px] text-[color:var(--ink)]">{p.title || url.replace(/^https?:\/\//, "")}</span>
                      {p.drift ? <span className="shrink-0 text-[10px] font-medium text-[color:var(--ink)]">changed ·</span> : null}
                      <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{p.fetches}f · {p.sessions}s</span>
                    </div>
                    {preview ? (
                      <div className="mt-[3px] line-clamp-2 text-[11px] leading-[1.5] text-[color:var(--muted)]">{preview}</div>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ))}
          {built.queries.length ? (
            <div className="min-w-0">
              <div className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[12px] py-[5px]">
                <span className="truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Searches</span>
                <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">{built.queries.length} quer{built.queries.length === 1 ? "y" : "ies"}</span>
              </div>
              {built.queries.map((q, i) => (
                <div key={i} className="border-b border-[color:var(--line-soft)] px-[12px] py-[6px]">
                  <div className="flex min-w-0 items-baseline gap-[8px]">
                    <span className="min-w-0 truncate text-[12px] text-[color:var(--ink)]">{q.query}</span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{q.engine} · {q.resultCount} result{q.resultCount === 1 ? "" : "s"}</span>
                  </div>
                  {q.leadTo.length ? (
                    <div className="mt-[2px] text-[11px] leading-[1.5] text-[color:var(--muted)]">
                      → fetched {q.leadTo.length} of its result{q.leadTo.length === 1 ? "" : "s"}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
        </ScrollArea>
      </div>
    </div>
  );
}

// The Lineage tab — deterministic, LLM-free provenance across the window's
// reconstructed artifacts (lib/traceProvenance). For each PRODUCED artifact (a
// file write/edit, a Linear write) it lists what fed it: derivation edges (an
// id/url/path transferred, a rare verbatim span, or a rare shared symbol) and
// consultation edges (a research fetch/search consumed earlier with no textual
// transfer — carrying the agent's stated intent, never claimed as derivation).
// Source and target rows open their raw trace.
const TIER_META = {
  cited: { label: "cited", derived: true },
  quoted: { label: "quoted", derived: true },
  entity: { label: "related", derived: true },
  adjacent: { label: "consulted", derived: false },
};
// Every provenance node kind, with the short TAG shown beside its label and
// the noun used in per-target summaries. A graph that mixes files, pages,
// tickets, PRs, datasets, recalls and artifacts is unreadable if they all
// render as bare strings — the tag is what makes a row's type legible at a
// glance, and a kind missing from here silently degrades to "source".
const KIND_META = {
  "file.write":      { tag: "file",     noun: "file" },
  "file.edit":       { tag: "file",     noun: "file" },
  "file.read":       { tag: "file",     noun: "file" },
  "linear.write":    { tag: "ticket",   noun: "ticket" },
  "linear.read":     { tag: "ticket",   noun: "ticket" },
  "web.fetch":       { tag: "page",     noun: "page" },
  "web.search":      { tag: "search",   noun: "search" },
  "forge.write":     { tag: "pr",       noun: "PR/issue" },
  "forge.read":      { tag: "pr",       noun: "PR/issue" },
  "query.run":       { tag: "query",    noun: "query" },
  "query.write":     { tag: "query",    noun: "query" },
  "recall.search":   { tag: "recall",   noun: "recall" },
  "artifact.publish": { tag: "artifact", noun: "artifact" },
};
const kindTag = (kind) => KIND_META[kind]?.tag || "other";
const KIND_NOUN = Object.fromEntries(Object.entries(KIND_META).map(([k, v]) => [k, v.noun]));

// The census line under the Lineage header: what the graph is MADE of. Reading
// it is how you tell "this extractor contributes nothing here" from "this
// extractor isn't wired" — the two look identical in the graph itself.
function KindCensus({ stats }) {
  const counts = stats?.nodesByKind || {};
  const linked = new Set(stats?.linkedKinds || []);
  const byTag = new Map();
  for (const [kind, n] of Object.entries(counts)) {
    const tag = kindTag(kind);
    const prev = byTag.get(tag) || { tag, n: 0, linked: false };
    prev.n += n;
    prev.linked = prev.linked || linked.has(kind);
    byTag.set(tag, prev);
  }
  const rows = [...byTag.values()].sort((a, b) => b.n - a.n);
  if (!rows.length) return null;
  return (
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-[8px] gap-y-[2px]">
      {rows.map((r) => (
        <span key={r.tag} className="shrink-0 whitespace-nowrap text-[10px] text-[color:var(--muted)]">
          <span className="font-mono tabular-nums text-[color:var(--ink)]">{r.n}</span>
          {" "}
          <span className={r.linked ? "" : "text-[color:var(--muted-soft)]"}>{r.tag}</span>
          {/* Extracted but connected to nothing — real, and worth seeing. */}
          {!r.linked ? <span className="text-[color:var(--muted-soft)]"> · unlinked</span> : null}
        </span>
      ))}
    </span>
  );
}

function plural(n, noun) { return `${n} ${noun}${n === 1 ? "" : "s"}`; }

// An artifact label: for a file, the parent dir (muted, truncates) then the
// basename (kept, never truncated) so identity survives a long path; for a
// ticket/page, the node's own short label.
// `className` lands on the flex/truncating element ITSELF (e.g. "flex-1
// min-w-0 text-[12px]") — the caller must NOT wrap this in a plain span, or the
// dir won't clip and the path overflows.
// ── Forge panel — PR/issue/CI work driven through the SHELL (curl, gh,
// forgejo-pr.py, tea, git push), the Linear tab's opaque-channel sibling. Same
// layout: Who rail + repo → target tree left, full op stream right.

const FORGE_CHANNEL_LABEL = { curl: "curl", gh: "gh", script: "forgejo-pr.py", tea: "tea", git: "git", python: "python" };

function ForgeOpRow({ op, traceOf, labelOf, onSelectTrace }) {
  const row = traceOf.get(op.promptId);
  const actor = labelOf({ id: op.endpointId, label: op.endpointName || "" });
  const write = op.kind === "write" || op.kind === "push";
  return (
    <div className="grid gap-[2px] border-l-2 pl-[8px]" style={{ borderColor: write ? "var(--green)" : "var(--line-soft)" }}>
      <button
        type="button"
        onClick={() => onSelectTrace(op.promptId)}
        className="flex items-baseline gap-[8px] rounded-[2px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
      >
        <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">{sampleWhen(row?.timestamp ?? op.ts)}</span>
        <span className="shrink-0 font-mono text-[10px] text-[color:var(--muted-soft)]">{FORGE_CHANNEL_LABEL[op.channel] || op.channel}</span>
        <span className="shrink-0 font-mono text-[10px]" style={write ? PLUS_STYLE : undefined}>
          {op.create ? "create" : op.action.replace(/_/g, " ")}
        </span>
        {actor ? <span className="truncate text-[10px] text-[color:var(--muted-soft)]">{actor}</span> : null}
        {!op.ok ? <span className="shrink-0 font-mono text-[10px]" style={MINUS_STYLE}>errored</span> : null}
        <span className="ml-auto shrink-0 text-[10px] text-[color:var(--muted-soft)]">open trace →</span>
      </button>
      <LinearFieldChips fields={op.fields} />
    </div>
  );
}

// A target's content through the SAME renderer as the Files tab — the whole
// point of the tab is seeing what the agent saw, not just that it looked:
// the DESCRIPTION as a reconstructed document (writes replay, GET-observed
// bodies are read observations, drift = the PR changed off-window), each
// comment as a one-version mini-document (posted in-window vs observed in a
// thread read), and every observed DIFF as a read-only document.
function ForgeTargetBody({ target, traceOf, labelOf, onSelectTrace, descCap = 0 }) {
  const { descRec, comments, diffs, observed } = useMemo(() => {
    const doc = targetDescDoc(target.ops);
    return {
      descRec: doc.length ? reconstructFile(doc) : null,
      comments: targetCommentDocs(target.ops).map((c) => ({ ...c, rec: reconstructFile([c.op]) })),
      diffs: targetDiffDocs(target.ops).map((d) => ({ ...d, rec: reconstructFile([d.op]) })),
      // jq-shaped read outputs — the raw text the agent's context received
      // when no structured body could be pinned.
      observed: target.ops
        .map((op) => ({ op, doc: opObservedDoc(op) }))
        .filter((o) => o.doc)
        .map((o) => ({ ...o, rec: reconstructFile([o.doc]) })),
    };
  }, [target]);
  return (
    <>
      <div className="grid gap-[4px]">
        {target.ops.map((op, i) => (
          <ForgeOpRow key={`${op.promptId}:${i}`} op={op} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
        ))}
      </div>
      {descRec && descRec.rows.length ? (
        <div className="grid gap-[3px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>Description</span>
          <AnnotatedFileBody rec={descRec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={descCap} bordered />
        </div>
      ) : null}
      {comments.map((c, i) => (
        <div key={i} className="grid gap-[3px]">
          <span className={NOTE_CLASS}>
            {c.written ? "comment posted" : c.state ? `review observed · ${c.state.toLowerCase().replace(/_/g, " ")}` : "comment observed"}
            {c.author ? ` · ${c.author}` : ""} · {sampleWhen(traceOf.get(c.op.promptId)?.timestamp ?? c.op.ts)}
          </span>
          <AnnotatedFileBody rec={c.rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={0} bordered />
        </div>
      ))}
      {diffs.map((d, i) => (
        <div key={i} className="grid gap-[3px]">
          <span className={NOTE_CLASS}>
            diff observed · {d.lines}{d.truncated ? "+" : ""} line{d.lines === 1 ? "" : "s"} · {FORGE_CHANNEL_LABEL[d.channel] || d.channel} · {sampleWhen(traceOf.get(d.op.promptId)?.timestamp ?? d.op.ts)}
          </span>
          <AnnotatedFileBody rec={d.rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={descCap} bordered />
        </div>
      ))}
      {observed.map((o, i) => (
        <div key={i} className="grid gap-[3px]">
          <span className={NOTE_CLASS}>
            output observed · {FORGE_CHANNEL_LABEL[o.op.channel] || o.op.channel} {o.op.action} · {sampleWhen(traceOf.get(o.op.promptId)?.timestamp ?? o.op.ts)}
          </span>
          <AnnotatedFileBody rec={o.rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={descCap > 0 ? Math.min(descCap, 14) : 0} bordered />
        </div>
      ))}
    </>
  );
}

// Full target timeline for the detail panel — the forge analog of
// LinearIssueDetail.
function ForgeTargetDetail({ target, traceOf, labelOf, onSelectTrace }) {
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]">
      <div className="grid gap-[3px]">
        <span className="flex items-baseline gap-[8px]">
          <span className="font-mono text-[12px] text-[color:var(--ink)]">{target.repo ? `${target.repo} ` : ""}{target.id}</span>
          <span className={NOTE_CLASS}>{target.res === "issue" ? "issue" : "pull request"}{target.repoAssumed ? " · repo assumed" : ""}</span>
        </span>
        {target.title ? <span className="text-[12px] leading-[1.4] text-[color:var(--ink)]">{target.title}</span> : null}
        <span className={NOTE_CLASS}>
          {target.reads} read{target.reads === 1 ? "" : "s"} · <span style={target.writes ? PLUS_STYLE : undefined}>{target.writes} write{target.writes === 1 ? "" : "s"}</span>
          {target.errored ? ` · ${target.errored} errored` : ""} · {target.sessions} session{target.sessions === 1 ? "" : "s"} · via {target.channels.map((c) => FORGE_CHANNEL_LABEL[c] || c).join(", ")}
        </span>
      </div>
      <ForgeTargetBody target={target} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
    </ScrollArea>
  );
}

function ForgeTargetSection({ target, refFn, traceOf, labelOf, onSelectTrace, onOpenTarget, actorName }) {
  return (
    <div ref={refFn} className="min-w-0 border-b border-[color:var(--line-soft)]">
      {/* Sticky like FileSection/LinearIssueSection — opaque plot-bg, hover on
          the inner text. */}
      <button
        type="button"
        onClick={() => onOpenTarget(target)}
        className="group sticky top-0 z-[2] flex w-full items-baseline gap-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px] text-left cursor-pointer"
      >
        <span className="shrink-0 font-mono text-[11.5px] text-[color:var(--ink)]">{target.id}</span>
        {target.res === "issue" ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">issue</span> : null}
        {target.title ? <span className="truncate text-[11.5px] text-[color:var(--muted)] group-hover:text-[color:var(--ink)]">{target.title}</span> : null}
        <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
          {target.reads} read{target.reads === 1 ? "" : "s"} · <span style={target.writes ? PLUS_STYLE : undefined}>{target.writes} write{target.writes === 1 ? "" : "s"}</span>
          {target.errored ? ` · ${target.errored} errored` : ""}
        </span>
        <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]">open →</span>
      </button>
      {(target.readers.length || target.writers.length) ? (
        <span className={`${NOTE_CLASS} block px-[12px] pb-[4px]`}>
          {target.writers.length ? <>written by {target.writers.map((w) => actorName(w)).join(", ")}</> : null}
          {target.writers.length && target.readers.length ? " · " : null}
          {target.readers.length ? <>read by {target.readers.map((r) => actorName(r)).join(", ")}</> : null}
        </span>
      ) : null}
      <div className="grid gap-[8px] px-[12px] pb-[10px] pt-[2px]">
        <ForgeTargetBody target={target} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} descCap={40} />
      </div>
    </div>
  );
}

// The Forge tab body — the same three-region layout as the Linear tab (Who
// rail as an actor FILTER, repo → target tree, full stream), over ops that
// never had a dedicated tool: shell commands parsed by traceForgeOps.
export function ForgePanel({ scan, built, actors, actorFilter, onFilterActor, traceOf, labelOf, onSelectTrace, onOpenTarget }) {
  const targetRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  // reads/writes included: a repo-level write with no numbered target (gh
  // release create) is still forge activity.
  if (!t.targets && !t.reads && !t.writes && !t.pushes && !t.browses && !t.ci && !actorFilter) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No Git, pull request, issue, or CI activity was recorded in this window.
      </span>
    );
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  const channelNote = Object.entries(t.channels).filter(([, n]) => n > 0)
    .map(([c, n]) => `${FORGE_CHANNEL_LABEL[c] || c} ${n}`).join(" · ");
  const jump = (key) => {
    targetRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocusKey(key);
  };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.prs} PR{t.prs === 1 ? "" : "s"}{t.issues ? ` + ${t.issues} issue${t.issues === 1 ? "" : "s"}` : ""}
          {t.repos ? ` across ${t.repos} repo${t.repos === 1 ? "" : "s"}` : ""} ·{" "}
          {t.reads} read{t.reads === 1 ? "" : "s"} · <span style={PLUS_STYLE}>{t.writes} write{t.writes === 1 ? "" : "s"}</span>
          {t.created ? ` (${t.created} created)` : ""}
          {t.pushes ? ` · ${t.pushes} push${t.pushes === 1 ? "" : "es"}` : ""}
          {t.ci ? ` · ${t.ci} CI call${t.ci === 1 ? "" : "s"}` : ""}
          {channelNote ? ` · via ${channelNote}` : ""}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.reads}r{" "}<span style={a.writes ? PLUS_STYLE : undefined}>{a.writes}w</span>{a.pushes ? ` ${a.pushes}p` : ""}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {built.repos.map((repo) => (
            <div key={repo.name || "(none)"} className="min-w-0 pb-[4px]">
              <div className="flex min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[4px]">
                <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                  {repo.name || "Repo unresolved"}{repo.name && repo.assumed ? " · assumed" : ""}
                </span>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                  {repo.reads}r <span style={repo.writes ? PLUS_STYLE : undefined}>{repo.writes}w</span>{repo.pushes ? ` ${repo.pushes}p` : ""}
                </span>
              </div>
              {repo.targets.map((target) => (
                <button
                  key={target.key}
                  type="button"
                  onClick={() => jump(target.key)}
                  className={[
                    "flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left cursor-pointer",
                    focusKey === target.key ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                  style={{ paddingLeft: "18px" }}
                >
                  <span className={[
                    "shrink-0 font-mono text-[11px] leading-[1.4]",
                    target.writes ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {target.id}
                  </span>
                  {target.title ? <span className="min-w-0 truncate text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]">{target.title}</span> : null}
                  <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {target.reads}r <span style={target.writes ? PLUS_STYLE : undefined}>{target.writes}w</span>
                  </span>
                </button>
              ))}
              {repo.stream.length ? (
                <button
                  type="button"
                  onClick={() => jump(`${repo.name || ""}!stream`)}
                  className={[
                    "flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left cursor-pointer",
                    focusKey === `${repo.name || ""}!stream` ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                  style={{ paddingLeft: "18px" }}
                >
                  <span className="min-w-0 truncate text-[10.5px] leading-[1.4] text-[color:var(--muted)]">CI · pushes · lists</span>
                  <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{repo.stream.length}</span>
                </button>
              ) : null}
            </div>
          ))}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {built.repos.map((repo) => (
            <div key={repo.name || "(none)"} className="min-w-0">
              <div className="flex items-baseline justify-between gap-[10px] border-b border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[12px] py-[5px]">
                <span className="truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                  {repo.name || "Repo unresolved"}{repo.name && repo.assumed ? " · assumed from window" : ""}
                </span>
                <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">
                  {repo.targets.length} target{repo.targets.length === 1 ? "" : "s"} · {repo.reads}r · <span style={repo.writes ? PLUS_STYLE : undefined}>{repo.writes}w</span>{repo.pushes ? ` · ${repo.pushes}p` : ""}
                </span>
              </div>
              {repo.targets.map((target) => (
                <ForgeTargetSection
                  key={target.key}
                  target={target}
                  refFn={(el) => {
                    if (el) targetRefs.current.set(target.key, el);
                    else targetRefs.current.delete(target.key);
                  }}
                  traceOf={traceOf}
                  labelOf={labelOf}
                  onSelectTrace={onSelectTrace}
                  onOpenTarget={onOpenTarget}
                  actorName={actorName}
                />
              ))}
              {repo.stream.length ? (
                <div
                  ref={(el) => {
                    if (el) targetRefs.current.set(`${repo.name || ""}!stream`, el);
                    else targetRefs.current.delete(`${repo.name || ""}!stream`);
                  }}
                  className="min-w-0 border-b border-[color:var(--line-soft)]"
                >
                  <div className="sticky top-0 z-[2] flex items-baseline gap-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px]">
                    <span className="font-mono text-[11.5px] text-[color:var(--ink)]">CI · pushes · lists</span>
                    <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">{repo.stream.length} op{repo.stream.length === 1 ? "" : "s"}</span>
                  </div>
                  <div className="grid gap-[4px] px-[12px] pb-[10px] pt-[6px]">
                    {repo.stream.map((op, i) => {
                      const logDoc = op.log ? logObsDoc(op) : null;
                      return (
                        <div key={`${op.promptId}:${i}`} className="grid gap-[3px]">
                          <ForgeOpRow op={op} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
                          {logDoc ? (
                            <AnnotatedFileBody rec={reconstructFile([logDoc])} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={14} bordered />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </ScrollArea>
      </div>
    </div>
  );
}

// ── Query panels (Grafana · Queries) — what the window ASKED of telemetry
// and data systems, and what came back. One component, two tabs: targets are
// the nouns (a Prometheus metric, a Loki selector, an analytics entity, a SQL
// table), each with its distinct query texts, runs, and the OBSERVED response
// through the annotated renderer.

function QueryRunRow({ run, traceOf, labelOf, onSelectTrace }) {
  const row = traceOf.get(run.promptId);
  const actor = labelOf({ id: run.endpointId, label: run.endpointName || "" });
  const write = run.kind === "write";
  return (
    <div className="grid gap-[2px] border-l-2 pl-[8px]" style={{ borderColor: write ? "var(--green)" : "var(--line-soft)" }}>
      <button
        type="button"
        onClick={() => onSelectTrace(run.promptId)}
        className="flex items-baseline gap-[8px] rounded-[2px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
      >
        <span className="shrink-0 text-[10px] tabular-nums text-[color:var(--muted)]">{sampleWhen(row?.timestamp ?? run.ts)}</span>
        <span className="shrink-0 font-mono text-[10px]" style={write ? PLUS_STYLE : undefined}>
          {write ? "write" : run.kind === "browse" ? "browse" : "query"}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-[color:var(--muted-soft)]">{run.channel}</span>
        {actor ? <span className="truncate text-[10px] text-[color:var(--muted-soft)]">{actor}</span> : null}
        {!run.ok ? <span className="shrink-0 font-mono text-[10px]" style={MINUS_STYLE}>errored</span> : null}
        <span className="ml-auto shrink-0 text-[10px] text-[color:var(--muted-soft)]">open trace →</span>
      </button>
      <LinearFieldChips fields={run.fields} />
    </div>
  );
}

// One query variant: its text (mono, as authored), the runs that issued it,
// and the latest observed response replayed through the annotated renderer.
function QueryBlock({ query, engine, traceOf, labelOf, onSelectTrace, resCap = 25, allResponses = false }) {
  const responses = useMemo(() => {
    const runs = allResponses ? query.runs.filter((r) => r.result?.text) : [];
    if (!allResponses && query.lastResult) {
      const owner = query.runs.find((r) => r.promptId === query.lastResult.promptId && r.result?.text) || null;
      if (owner) runs.push(owner);
    }
    return runs;
  }, [query, allResponses]);
  return (
    <div className="grid gap-[4px]">
      <div className="overflow-x-auto rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[5px]">
        <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[10.5px] leading-[1.55] text-[color:var(--ink)]">{query.text}</pre>
      </div>
      <div className="grid gap-[3px]">
        {query.runs.map((run, i) => (
          <QueryRunRow key={`${run.promptId}:${i}`} run={run} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
        ))}
      </div>
      {responses.map((run, i) => (
        <ResponseBlock key={i} run={run} engine={engine} traceOf={traceOf} onSelectTrace={onSelectTrace} resCap={resCap} />
      ))}
    </div>
  );
}

// Full target view for the detail sidebar: every query variant with EVERY
// observed response, uncapped.
export function QueryTargetDetail({ target, traceOf, labelOf, onSelectTrace }) {
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]">
      <div className="grid gap-[3px]">
        <span className="flex min-w-0 items-baseline gap-[8px]">
          <span className="min-w-0 truncate font-mono text-[12px] text-[color:var(--ink)]">{target.target}</span>
          <span className={`${NOTE_CLASS} shrink-0`}>{target.engine}{target.instance ? ` · ${target.instance}` : ""}</span>
        </span>
        <span className={NOTE_CLASS}>
          {target.reads} quer{target.reads === 1 ? "y" : "ies"} · <span style={target.writes ? PLUS_STYLE : undefined}>{target.writes} write{target.writes === 1 ? "" : "s"}</span>
          {target.browses ? ` · ${target.browses} browses` : ""}{target.errored ? ` · ${target.errored} errored` : ""} · {target.sessions} session{target.sessions === 1 ? "" : "s"}
        </span>
      </div>
      {target.queries.map((q, i) => (
        <QueryBlock key={i} query={q} engine={target.engine} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} resCap={0} allResponses />
      ))}
    </ScrollArea>
  );
}

function QueryTargetSection({ target, refFn, traceOf, labelOf, onSelectTrace, onOpenTarget }) {
  return (
    <div ref={refFn} className="min-w-0 border-b border-[color:var(--line-soft)]">
      <button
        type="button"
        onClick={() => onOpenTarget(target)}
        className="group sticky top-0 z-[2] flex w-full items-baseline gap-[8px] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px] text-left cursor-pointer"
      >
        <span className="min-w-0 truncate font-mono text-[11.5px] text-[color:var(--ink)]">{target.target}</span>
        <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">{target.engine}{target.instance ? ` · ${target.instance}` : ""}</span>
        <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
          {target.reads}q <span style={target.writes ? PLUS_STYLE : undefined}>{target.writes}w</span>
          {target.errored ? <span style={MINUS_STYLE}> {target.errored}✗</span> : null}
        </span>
        <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]">open →</span>
      </button>
      <div className="grid gap-[8px] px-[12px] pb-[10px] pt-[6px]">
        {target.queries.map((q, i) => (
          <QueryBlock key={i} query={q} engine={target.engine} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} resCap={25} />
        ))}
      </div>
    </div>
  );
}

export function QueryOpsPanel({ scan, built, actors, actorFilter, onFilterActor, traceOf, labelOf, onSelectTrace, onOpenTarget, emptyNote }) {
  const sectionRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  if (!t.runs && !actorFilter) {
    return <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>{emptyNote}</span>;
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  const engineNote = Object.entries(t.engines).filter(([, n]) => n > 0).map(([e, n]) => `${e} ${n}`).join(" · ");
  const jump = (key) => {
    sectionRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" });
    setFocusKey(key);
  };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.targets} target{t.targets === 1 ? "" : "s"} · {t.queries} distinct quer{t.queries === 1 ? "y" : "ies"} · {t.reads} run{t.reads === 1 ? "" : "s"}
          {t.writes ? <> · <span style={PLUS_STYLE}>{t.writes} write{t.writes === 1 ? "" : "s"}</span></> : null}
          {t.browses ? ` · ${t.browses} browses` : ""}
          {t.errored ? ` · ${t.errored} errored` : ""}
          {engineNote ? ` · via ${engineNote}` : ""}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.queries}q{a.writes ? <span style={PLUS_STYLE}> {a.writes}w</span> : null}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {built.groups.map((g) => (
            <div key={g.key} className="min-w-0 pb-[4px]">
              <div className="flex min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[4px]">
                <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                  {g.engine}{g.instance ? ` · ${g.instance}` : ""}
                </span>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                  {g.reads}q <span style={g.writes ? PLUS_STYLE : undefined}>{g.writes}w</span>
                </span>
              </div>
              {g.targets.map((target) => (
                <button
                  key={target.key}
                  type="button"
                  onClick={() => jump(target.key)}
                  className={[
                    "flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left cursor-pointer",
                    focusKey === target.key ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                  style={{ paddingLeft: "18px" }}
                >
                  <span className={[
                    "min-w-0 truncate font-mono text-[10.5px] leading-[1.5]",
                    target.writes ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {target.target}
                  </span>
                  <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {target.reads + target.browses}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {built.targets.map((target) => (
            <QueryTargetSection
              key={target.key}
              target={target}
              refFn={(el) => {
                if (el) sectionRefs.current.set(target.key, el);
                else sectionRefs.current.delete(target.key);
              }}
              traceOf={traceOf}
              labelOf={labelOf}
              onSelectTrace={onSelectTrace}
              onOpenTarget={onOpenTarget}
            />
          ))}
        </ScrollArea>
      </div>
    </div>
  );
}

// Artifact reconstruction reads BOTH file channels: the live tool_use ops, and
// the ops recoverable from compaction-summarized reads. The summary channel is
// opted into here and nowhere else yet — this substrate only ever feeds
// reconstruction, never a counting surface, so an op that can't dedup against
// its original tool_use id costs nothing. Summarized ops come first: they live
// in the system turn, ahead of the request's live blocks.
const artifactFileOpsOf = (turns) => [...extractSummaryFileOps(turns), ...extractFileOps(turns)];

// ── Artifacts panel — the published artifacts this window's agents SHIPPED.
// The tab itself is a light list, like the sibling X-rays: one row per
// artifact. The deliverable — rendered preview plus annotated source — opens
// in the EXPANDABLE detail sidebar (the same surface as the Files
// reconstruction), where it can take the full height on demand instead of
// living inline in the tab.

// How much of the artifact's own history the walk actually covered. Only claim
// a fraction when the session cap really dropped sessions — "1 of its 1 deploy
// session" reads like a truncation that never happened.
function coverageNote(gen) {
  const total = gen.walked + gen.skipped;
  const plural = total === 1 ? "" : "s";
  return gen.skipped
    ? ` ${gen.walked} of its ${total} deploy session${plural}`
    : ` its ${total} deploy session${plural}`;
}

// The sidebar deliverable view: identity, deploy chips, the rendered page
// (sandboxed — captured markup + inline CSS only; scripts never execute in
// the dashboard), then the source as written with line blame.
export function ArtifactDetail({ artifact, rec: windowRec, traceOf, labelOf, onSelectTrace, onBlameLine, deepSourcesFor }) {
  // The scan window is a SAMPLE, and compaction means a publish prompt's body
  // no longer re-carries the session's early writes — so a window-empty rec
  // is not the end: walk the deploy sessions' own prompts for the backing
  // file's ops (the same beyond-the-window over-fetch line blame uses), and
  // when nothing wrote the page itself, hop to whatever generated it.
  const windowHasContent = Boolean(windowRec && windowRec.rows.length);
  const [deep, setDeep] = useState({ status: windowHasContent || !deepSourcesFor ? "done" : "idle", src: null });
  useEffect(() => {
    if (windowHasContent || !deepSourcesFor) return undefined;
    let on = true;
    setDeep({ status: "scanning", src: null });
    deepSourcesFor(artifact)
      .then((s) => { if (on) setDeep({ status: "done", src: s }); })
      .catch(() => { if (on) setDeep({ status: "done", src: null }); });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.key]);
  const scanning = deep.status === "scanning";
  const rec = windowHasContent ? windowRec : deep.src?.rec || null;
  const via = windowHasContent ? "window" : deep.src?.via || null;
  const gen = windowHasContent ? null : deep.src?.generators || null;
  const html = useMemo(
    () => (rec ? rec.rows.filter((r) => !r.gap && !r.del).map((r) => r.text).join("\n") : null),
    [rec],
  );
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]" aria-busy={scanning || undefined}>
      <div className="grid gap-[4px]">
        <span className="flex min-w-0 items-baseline gap-[8px]">
          {artifact.favicon ? <span className="shrink-0 text-[14px] leading-none">{artifact.favicon}</span> : null}
          <span className="min-w-0 truncate text-[12px] leading-[1.4] text-[color:var(--ink)]">{artifact.title || "(untitled artifact)"}</span>
          {artifact.skill ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">under {artifact.skill}</span> : null}
        </span>
        {artifact.description ? (
          <span className="text-[11px] leading-[1.45] text-[color:var(--muted)]">{artifact.description}</span>
        ) : null}
        <div className="flex min-w-0 flex-wrap items-baseline gap-[6px]">
          <span className={`${NOTE_CLASS} shrink-0`}>
            {artifact.n} deploy{artifact.n === 1 ? "" : "s"}
            {artifact.path ? ` · ${artifact.path.split(/[\\/]/).pop()}` : ""}
          </span>
          {artifact.deploys.map((d, i) => (
            <ArtifactDeployChip key={`${d.promptId}:${i}`} deploy={d} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
          ))}
        </div>
      </div>
      {html ? (
        <div className="grid gap-[3px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>
            Preview · {describeArtifactSource(via, recSourceOps(rec))} · scripts disabled
          </span>
          {/* Viewport-relative height: the sidebar expands to near-fullscreen,
              and the preview grows with it. The iframe scrolls its own
              document. */}
          <iframe
            sandbox=""
            srcDoc={html}
            title={`Reconstructed artifact preview: ${artifact.title || "untitled"}`}
            loading="lazy"
            className="h-[68vh] w-full rounded-[2px] border border-[color:var(--line-soft)] bg-white"
          />
        </div>
      ) : scanning ? (
        <span className={`${NOTE_CLASS} animate-pulse`}>
          Searching deploy sessions for the backing file&apos;s authoring operations…
        </span>
      ) : gen?.scripts?.length ? null : (
        <span className={NOTE_CLASS}>
          No captured operation wrote {artifact.path ? artifact.path.split(/[\\/]/).pop() : "the backing file"} in this window or
          {gen ? coverageNote(gen) : " its deploy sessions"}
          , and no generator names it. The page was assembled outside the captured tools.
          {gen?.shellWrites?.length ? ` The shell redirected into it ${gen.shellWrites.length}×, but a program's stdout carries no recoverable bytes.` : ""}
        </span>
      )}
      {rec && rec.rows.length ? (
        <div className="grid gap-[3px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>
            Source as written · {describeArtifactSource(via, recSourceOps(rec))}
          </span>
          <AnnotatedFileBody rec={rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={60} bordered filePath={artifact.path} onBlameLine={onBlameLine} />
        </div>
      ) : null}
      {gen?.scripts?.length ? (
        <div className="grid gap-[6px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>
            {html ? "Generated by" : "Generator not captured"}
          </span>
          {gen.scripts.map((s) => (
            <ArtifactGeneratorBlock
              key={s.path || s.name}
              script={s}
              traceOf={traceOf}
              onSelectTrace={onSelectTrace}
              onBlameLine={onBlameLine}
            />
          ))}
        </div>
      ) : null}
    </ScrollArea>
  );
}

// One generator candidate: the script that writes the artifact's basename, the
// shell runs that executed it, and its own reconstructed source. This is the
// SOURCE of the page, never the page — the labels have to keep that straight.
const GEN_RUN_CHIP_CAP = 6;

// Every run command opens with `cd <absolute scratch path> && `, which is what
// a truncating chip would show — the same prefix on each row, with the part
// that identifies the run cut off. Drop it for display; the chip still links to
// the trace carrying the whole command.
const runLabel = (cmd) => String(cmd || "").replace(/^\s*cd\s+(?:"[^"]*"|'[^']*'|\S+)\s*&&\s*/, "");

function ArtifactGeneratorBlock({ script, traceOf, onSelectTrace, onBlameLine }) {
  const rec = useMemo(() => reconstructFile(script.ops), [script]);
  const runs = script.runs || [];
  return (
    <div className="grid min-w-0 gap-[3px]">
      <span className="flex min-w-0 items-baseline gap-[8px]">
        <span className="min-w-0 truncate font-mono text-[11px] leading-[1.4] text-[color:var(--ink)]">{script.name}</span>
        <span className={`${NOTE_CLASS} shrink-0`}>
          {runs.length ? <span style={PLUS_STYLE}>ran {runs.length}×</span> : "never seen run"}
          {script.writes ? ` · ${script.writes} write${script.writes === 1 ? "" : "s"} captured` : ""}
        </span>
      </span>
      {runs.length ? (
        <div className="flex min-w-0 flex-col gap-[2px]">
          {runs.slice(0, GEN_RUN_CHIP_CAP).map((r, i) => (
            <button
              key={`${r.promptId}:${i}`}
              type="button"
              onClick={() => onSelectTrace(r.promptId)}
              className="flex w-full min-w-0 items-baseline gap-[6px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-left cursor-pointer hover:border-[color:var(--line-strong)]"
            >
              <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                {sampleWhen(traceOf.get(r.promptId)?.timestamp ?? r.ts)}
              </span>
              <span className="min-w-0 truncate font-mono text-[10px] text-[color:var(--muted-soft)]">{runLabel(r.command)}</span>
            </button>
          ))}
          {runs.length > GEN_RUN_CHIP_CAP ? (
            <span className={NOTE_CLASS}>+{runs.length - GEN_RUN_CHIP_CAP} more run{runs.length - GEN_RUN_CHIP_CAP === 1 ? "" : "s"}</span>
          ) : null}
        </div>
      ) : null}
      {rec.rows.length ? (
        <AnnotatedFileBody
          rec={rec}
          traceOf={traceOf}
          onSelectTrace={onSelectTrace}
          cap={30}
          bordered
          filePath={script.path || undefined}
          onBlameLine={script.path ? onBlameLine : undefined}
        />
      ) : null}
    </div>
  );
}

function ArtifactDeployChip({ deploy, traceOf, labelOf, onSelectTrace }) {
  const row = traceOf.get(deploy.promptId);
  const actor = labelOf({ id: deploy.endpointId, label: deploy.endpointName || "" });
  return (
    <button
      type="button"
      onClick={(e) => {
        // The chip lives inside the row's whole-cell click target — a trace
        // jump must not also open the artifact.
        e.stopPropagation();
        onSelectTrace(deploy.promptId);
      }}
      onKeyDown={(e) => {
        // Keyboard mirror of the click guard: Enter/Space on a focused chip
        // must not bubble to the row's onKeyDown (which opens the artifact) —
        // the chip's own activation click follows natively.
        if (e.key === "Enter" || e.key === " ") e.stopPropagation();
      }}
      className="flex shrink-0 items-baseline gap-[6px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-left cursor-pointer hover:border-[color:var(--line-strong)]"
    >
      <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">{sampleWhen(row?.timestamp ?? deploy.ts)}</span>
      {actor ? <span className="max-w-[140px] truncate text-[10px] text-[color:var(--muted-soft)]">{actor}</span> : null}
      <span className="text-[10px] text-[color:var(--muted-soft)]">→</span>
    </button>
  );
}

// The Artifacts tab body — Who rail (filter) left, one ROW per artifact
// right; a row opens the deliverable in the expandable sidebar.
export function ArtifactsPanel({ scan, built, actors, actorFilter, onFilterActor, traceOf, labelOf, onSelectTrace, onOpenArtifact }) {
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  if (!t.artifacts && !actorFilter) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No artifacts were published in this window.
      </span>
    );
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.artifacts} artifact{t.artifacts === 1 ? "" : "s"} · <span style={PLUS_STYLE}>{t.deploys} deploy{t.deploys === 1 ? "" : "s"}</span>{" "}
          across {t.sessions} session{t.sessions === 1 ? "" : "s"}
          {actorFilter ? "" : ` · ${t.actors} endpoint${t.actors === 1 ? "" : "s"}`}
          {t.skills ? ` · under ${t.skills} skill${t.skills === 1 ? "" : "s"}` : ""}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.deploys}d · {a.artifacts}a
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {built.artifacts.map((artifact) => (
            /* The WHOLE cell opens the artifact — role="button" because the
               deploy chips inside are real buttons (they stopPropagation) and
               buttons can't nest. */
            <div
              key={artifact.key}
              role="button"
              tabIndex={0}
              onClick={() => onOpenArtifact(artifact)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenArtifact(artifact); }
              }}
              className="group min-w-0 cursor-pointer border-b border-[color:var(--line-soft)] hover:bg-[color:var(--control-bg)]"
            >
              <div className="flex w-full min-w-0 items-baseline gap-[8px] px-[12px] pt-[6px]">
                {artifact.favicon ? <span className="shrink-0 text-[12px] leading-none">{artifact.favicon}</span> : null}
                <span className="min-w-0 truncate font-mono text-[11.5px] text-[color:var(--ink)]">{artifact.title || "(untitled artifact)"}</span>
                {artifact.skill ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">under {artifact.skill}</span> : null}
                <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
                  {artifact.n} deploy{artifact.n === 1 ? "" : "s"}
                </span>
                <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]">open →</span>
              </div>
              {artifact.description ? (
                <span className="block min-w-0 px-[12px] pb-[3px] pt-[3px] text-[10.5px] leading-[1.45] text-[color:var(--muted)]">{artifact.description}</span>
              ) : null}
              <div className="flex min-w-0 flex-wrap items-baseline gap-[6px] px-[12px] pb-[7px] pt-[2px]">
                <span className={`${NOTE_CLASS} shrink-0`}>by {artifact.publishers.map((p) => actorName(p)).join(", ")}</span>
                {artifact.deploys.map((d, i) => (
                  <ArtifactDeployChip key={`${d.promptId}:${i}`} deploy={d} traceOf={traceOf} labelOf={labelOf} onSelectTrace={onSelectTrace} />
                ))}
              </div>
            </div>
          ))}
        </ScrollArea>
      </div>
    </div>
  );
}


// One prior-work consultation row's result note: what came back, in one line.
function recallOutcomeOf(c) {
  const v = c.view;
  if (!c.ok) return "errored";
  if (!v) return c.result?.text ? "unparsed result" : "no result captured";
  if (v.type === "none") return "no precedent";
  if (v.type === "spilled") return "result spilled to file";
  if (v.type === "map") return `map · ${v.topics.length} topic${v.topics.length === 1 ? "" : "s"}`;
  const n = v.candidates?.length ?? 0;
  return `${n} candidate${n === 1 ? "" : "s"} · ${v.topics.length} topic${v.topics.length === 1 ? "" : "s"}`;
}

// Files-parity section header: sticky against the panel's own scroll root so
// the section you're in stays named while its rows scroll (the same contract
// the file sections and forge targets use).
const RECALL_SECTION_HEAD = "sticky top-0 z-[2] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[12px] py-[6px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]";

// The Recall X-ray: what this window's agents asked ORIGIN ITSELF. A light
// list of prior-work consultations (the Artifacts pattern — full content
// lives in the expandable detail sidebar), with the window's routed-topic and
// cited-author rollups on top and the analytics-docs lookups below.
export function RecallPanel({ scan, built, actors, actorFilter, onFilterActor, labelOf, onOpenConsultation, onOpenSession = null }) {
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built) return null;
  const t = built.totals;
  if (!t.searches && !t.docs && !actorFilter) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No prior-work or analytics documentation lookups were recorded in this window.
      </span>
    );
  }
  const actorName = (a) => labelOf({ id: a.id, label: a.name });
  const filteredActor = actorFilter ? actors.find((a) => a.id === actorFilter) : null;
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-baseline gap-[10px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {t.searches} consultation{t.searches === 1 ? "" : "s"} · <span style={PLUS_STYLE}>{t.candidates} candidate{t.candidates === 1 ? "" : "s"} recalled</span>
          {t.noPrecedent ? ` · ${t.noPrecedent} no-precedent` : ""}
          {t.spilled ? ` · ${t.spilled} spilled` : ""}
          {t.docs ? ` · ${t.docs} docs lookup${t.docs === 1 ? "" : "s"}` : ""}
          {" "}across {t.sessions} session{t.sessions === 1 ? "" : "s"}
          {actorFilter ? "" : ` · ${t.actors} endpoint${t.actors === 1 ? "" : "s"}`}
        </span>
        {filteredActor ? (
          <FilterChip label={`${actorName(filteredActor)} ✕`} active onClick={() => onFilterActor(actorFilter)} />
        ) : null}
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          {actors.length ? (
            <div className="min-w-0 pb-[6px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Who{actorFilter ? " · filtered" : ""}
              </div>
              {actors.map((a) => (
                <button
                  key={a.id || a.name}
                  type="button"
                  onClick={() => onFilterActor(a.id)}
                  className={[
                    "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                    actorFilter === a.id ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                  ].join(" ")}
                >
                  <span className={[
                    "min-w-0 truncate text-[11px] leading-[1.4]",
                    actorFilter === a.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {actorName(a)}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {a.searches}s · {a.docs}d
                  </span>
                </button>
              ))}
            </div>
          ) : null}
          {built.citedAuthors.length ? (
            <div className="min-w-0 border-t border-[color:var(--line-soft)] pb-[6px] pt-[2px]">
              <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Whose work came back
              </div>
              {built.citedAuthors.slice(0, 10).map((a) => (
                <div key={a.name} className="flex min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px]">
                  <span className="min-w-0 truncate text-[11px] leading-[1.4] text-[color:var(--muted)]">{a.name}</span>
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">×{a.count}</span>
                </div>
              ))}
            </div>
          ) : null}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {built.topicRank.length ? (
            <div className="min-w-0 border-b border-[color:var(--line-soft)]">
              <div className={RECALL_SECTION_HEAD}>Asked about</div>
              <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[3px] px-[12px] py-[6px]">
                {built.topicRank.slice(0, 8).map((topic) => (
                  <span key={topic.name} className="min-w-0 truncate text-[10.5px] text-[color:var(--muted)]">
                    {topic.name} <span className="font-mono tabular-nums text-[color:var(--muted-soft)]">×{topic.count}</span>
                  </span>
                ))}
              </div>
            </div>
          ) : null}
          {(() => {
            // The window's greatest hits: sessions recalled by MORE THAN ONE
            // consultation. All-×1 would just be an arbitrary sample — hide it.
            const repeats = (built.candidateRank || []).filter((m) => m.count > 1).slice(0, 8);
            if (!repeats.length) return null;
            return (
              <div className="min-w-0 border-b border-[color:var(--line-soft)]">
                <div className={RECALL_SECTION_HEAD}>Most recalled</div>
                <div className="min-w-0 px-[12px] py-[4px]">
                  {/* Each row is its own button, so the count gutter gets a fixed
                      width to keep the columns aligned across rows. */}
                  {repeats.map((m) => (
                    <button
                      key={m.sessionId}
                      type="button"
                      disabled={!onOpenSession || !m.sessionId}
                      onClick={() => onOpenSession?.(m.sessionId)}
                      className={[
                        "group/hot grid w-full min-w-0 grid-cols-[34px_minmax(0,1fr)_auto] items-baseline gap-x-[8px] py-[1.5px] text-left",
                        onOpenSession && m.sessionId ? "cursor-pointer hover:bg-[color:var(--control-bg)]" : "",
                      ].join(" ")}
                    >
                      <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">×{m.count}</span>
                      <span className="min-w-0 truncate text-[11px] leading-[1.45] text-[color:var(--ink)] group-hover/hot:text-[color:var(--green)]">{m.summary || "(no summary)"}</span>
                      <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">
                        {m.author || "unknown"}{m.date ? ` · ${m.date.slice(0, 10)}` : ""}
                        <span className="ml-[6px] opacity-0 group-hover/hot:opacity-100">session →</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          <div className="min-w-0">
            <div className={RECALL_SECTION_HEAD}>Consultations</div>
            {built.consultations.map((c) => (
              <div
                key={`${c.sessionId}:${c.key}`}
                role="button"
                tabIndex={0}
                onClick={() => onOpenConsultation(c)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenConsultation(c); }
                }}
                className="group min-w-0 cursor-pointer border-b border-[color:var(--line-soft)] hover:bg-[color:var(--control-bg)]"
              >
                <div className="flex w-full min-w-0 items-baseline gap-[8px] px-[12px] pt-[6px]">
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{sampleWhen(c.ts)}</span>
                  {/* The endpoint label is the row's ONE flexible element —
                      unbounded hostnames must truncate, never push the row
                      wider than the column (that pan is how content ends up
                      sliding under the Who rail). */}
                  <span className="min-w-0 truncate text-[10px] text-[color:var(--muted)]">{labelOf({ id: c.endpointId, label: c.endpointName })}</span>
                  {c.mode === "map" ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">map</span> : null}
                  <span className={[
                    "ml-auto shrink-0 font-mono text-[10px] tabular-nums",
                    c.view?.type === "none" || !c.ok ? "text-[color:var(--muted-soft)]" : "text-[color:var(--muted)]",
                  ].join(" ")}>
                    {recallOutcomeOf(c)}
                  </span>
                  <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)] group-hover:text-[color:var(--ink)]">open →</span>
                </div>
                <span className="block min-w-0 px-[12px] pb-[3px] pt-[3px] text-[11.5px] leading-[1.5] text-[color:var(--ink)] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden">
                  {c.request || "(no request text)"}
                </span>
                {c.facets.length ? (
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-[8px] gap-y-[2px] px-[12px] pb-[7px] pt-[1px]">
                    {c.facets.slice(0, 4).map((f, i) => (
                      <span key={i} className="min-w-0 max-w-[260px] truncate text-[10px] text-[color:var(--muted-soft)]">· {f}</span>
                    ))}
                    {c.facets.length > 4 ? (
                      <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">+{c.facets.length - 4} facets</span>
                    ) : null}
                  </div>
                ) : <div className="pb-[7px]" />}
              </div>
            ))}
          </div>
          {built.docs.length ? (
            <div className="min-w-0">
              <div className={RECALL_SECTION_HEAD}>Manual lookups</div>
              <div className="min-w-0 px-[12px] py-[6px]">
                {built.docs.map((d) => (
                  <div key={d.tool} className="flex min-w-0 items-baseline gap-[8px] py-[2px]">
                    <span className="shrink-0 text-[11px] text-[color:var(--ink)]">{d.tool}</span>
                    <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted)]">×{d.count}</span>
                    {d.entities.length ? (
                      <span className="min-w-0 truncate font-mono text-[10px] text-[color:var(--muted-soft)]">{d.entities.join(", ")}</span>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </ScrollArea>
      </div>
    </div>
  );
}

function KindTag({ kind }) {
  return (
    <span className="mr-[5px] shrink-0 font-mono text-[9px] uppercase tracking-[0.04em] text-[color:var(--muted-soft)]">
      {kindTag(kind)}
    </span>
  );
}

function ArtifactLabel({ node, prefix, strong, className = "" }) {
  const tone = strong ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]";
  if (node.kind && node.kind.startsWith("file") && node.ref) {
    const rel = relOf(node.ref, prefix);
    const i = rel.lastIndexOf("/");
    const dir = i >= 0 ? rel.slice(0, i + 1) : "";
    const base = i >= 0 ? rel.slice(i + 1) : rel;
    return (
      <span className={`flex min-w-0 items-baseline ${className}`}>
        <KindTag kind={node.kind} />
        {dir ? <span className="min-w-0 truncate text-[color:var(--muted-soft)]">{dir}</span> : null}
        <span className={`shrink-0 ${tone}`}>{base}</span>
      </span>
    );
  }
  return (
    <span className={`flex min-w-0 items-baseline ${className}`}>
      <KindTag kind={node.kind} />
      <span className={`min-w-0 truncate ${tone}`}>{node.label}</span>
    </span>
  );
}

// One edge in the list view, on TWO lines so nothing floats into a phantom
// column: line 1 = the tier badge + the source artifact (clickable → its
// trace); line 2 = the evidence, framed by tier so it reads as a claim, not a
// loose string — a verbatim span in "quotes", a consultation's stated intent,
// the cited identity. (The related/entity tier is collapsed per-target
// upstream, so it never reaches an EdgeRow.)
function edgeEvidence(edge) {
  const first = edge.evidence[0];
  if (edge.class === "consultation") return first ? `looking for: ${first}` : null;
  if (edge.tier === "quoted") return first ? `quoted “${first}”` : null;
  if (edge.tier === "cited") return first ? `shares ${first}` : null;
  return edge.evidence.length ? edge.evidence.join(", ") : null;
}

function EdgeRow({ edge, prefix, onSelectTrace }) {
  const meta = TIER_META[edge.tier] || { label: edge.tier, derived: edge.class === "derivation" };
  const why = edgeEvidence(edge);
  return (
    <div className="min-w-0 py-[3px]">
      <div className="flex min-w-0 items-baseline gap-[8px]">
        <span
          className="shrink-0 rounded-[2px] border px-[5px] py-[0.5px] text-[9px] uppercase tracking-[0.06em]"
          style={{ borderColor: meta.derived ? "var(--green)" : "var(--line)", color: meta.derived ? "var(--green)" : "var(--muted)" }}
        >
          {meta.label}
        </span>
        <button
          type="button"
          onClick={() => edge.source.promptId && onSelectTrace?.(edge.source.promptId)}
          className="flex min-w-0 flex-1 text-left hover:opacity-80 cursor-pointer"
        >
          <ArtifactLabel node={edge.source} prefix={prefix} className="min-w-0 flex-1 text-[11px] leading-[1.4]" />
          {/* One source making the same claim across several ops is one claim,
              counted — not the same row printed N times. */}
          {edge.times > 1 ? (
            <span className="ml-[6px] shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">×{edge.times}</span>
          ) : null}
        </button>
      </div>
      {why ? (
        <div className="mt-[1px] truncate pl-[8px] text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]">{why}</div>
      ) : null}
    </div>
  );
}

function LineageFlow({ built, prefix, onSelectTrace }) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [hover, setHover] = useState(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((es) => { const cr = es[0]?.contentRect; if (cr) setSize({ w: Math.floor(cr.width), h: Math.floor(cr.height) }); });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const W = Math.max(640, size.w || 760);
  const lay = useMemo(() => computeFlowLayout(built, W, size.h), [built, W, size.h]);

  const activeKeys = useMemo(() => {
    if (!hover) return null;
    const set = new Set([hover]);
    for (const e of lay.edges) { if (e.sk === hover) set.add(e.tk); if (e.tk === hover) set.add(e.sk); }
    return set;
  }, [hover, lay]);
  const isOn = (k) => !activeKeys || activeKeys.has(k);
  const hoverNode = hover ? (lay.leftPos.get(hover)?.node || lay.rightPos.get(hover)?.node) : null;
  const midX = (lay.leftX + lay.rightX) / 2;
  const maxChars = Math.max(6, Math.floor((lay.LABEL_W - 12) / 6));
  // The kind tag rides in the same text run, so it spends part of the label's
  // width budget — charge it, or a long label overruns the gutter and clips.
  const clipFor = (kind, s) => {
    const budget = Math.max(6, maxChars - Math.ceil((kindTag(kind).length + 1) * 0.78));
    s = String(s || "");
    return s.length > budget ? `${s.slice(0, budget - 1)}…` : s;
  };
  const strokeOf = (e) => (e.class === "consultation" ? "var(--muted-soft)" : "var(--green)");
  const baseOp = (e) => (e.class === "consultation" ? 0.34 : e.tier === "entity" ? 0.14 : 0.5);
  const edgeOp = (e) => (activeKeys ? ((e.sk === hover || e.tk === hover) ? Math.min(0.9, baseOp(e) + 0.35) : 0.05) : baseOp(e));

  return (
    <div className="grid min-h-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 items-center gap-[14px] border-b border-[color:var(--line-soft)] px-[12px] py-[4px] text-[10px]">
        {hoverNode ? (
          <span className="min-w-0 truncate text-[color:var(--ink)]">{nodeShortLabel(hoverNode, prefix)}</span>
        ) : (
          <>
            <span className="flex items-center gap-[5px] text-[color:var(--muted)]"><span className="inline-block h-[2px] w-[16px]" style={{ background: "var(--green)" }} />derived</span>
            <span className="flex items-center gap-[5px] text-[color:var(--muted)]"><span className="inline-block h-[2px] w-[16px]" style={{ background: "var(--muted-soft)" }} />consulted</span>
            <span className="text-[color:var(--muted-soft)]">sources → produced · hover to isolate · click to open the trace{lay.dropped ? ` · +${lay.dropped} nodes hidden` : ""}</span>
          </>
        )}
      </div>
      <div ref={wrapRef} className="min-h-0 min-w-0">
        {size.w === 0 ? null : (
          <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
            <svg width={W} height={lay.svgH} viewBox={`0 0 ${W} ${lay.svgH}`} style={{ display: "block" }}>
              {lay.edges.map((e, i) => {
                const a = lay.leftPos.get(e.sk);
                const b = lay.rightPos.get(e.tk);
                return (
                  <path
                    key={i}
                    d={`M ${a.x},${a.y} C ${midX},${a.y} ${midX},${b.y} ${b.x},${b.y}`}
                    fill="none"
                    stroke={strokeOf(e)}
                    strokeWidth={e.tier === "cited" ? 1.8 : 1.3}
                    strokeOpacity={edgeOp(e)}
                  />
                );
              })}
              {[["left", lay.leftPos, "end"], ["right", lay.rightPos, "start"]].map(([side, posMap, anchor]) =>
                [...posMap.entries()].map(([k, p]) => {
                  const tx = side === "left" ? p.x - 8 : p.x + 8;
                  return (
                    <g
                      key={`${side}:${k}`}
                      style={{ cursor: p.node.promptId ? "pointer" : "default", opacity: isOn(k) ? 1 : 0.22 }}
                      onMouseEnter={() => setHover(k)}
                      onMouseLeave={() => setHover(null)}
                      onClick={() => p.node.promptId && onSelectTrace?.(p.node.promptId)}
                    >
                      <circle cx={p.x} cy={p.y} r={3} fill={hover === k ? "var(--ink)" : "var(--muted)"} />
                      {/* SVG text can't wrap, so the kind rides as a small
                          leading tag inside the same run — without it a PR, a
                          dataset and a file are three identical strings. */}
                      <text x={tx} y={p.y + 3.5} textAnchor={anchor} fontSize="11" fill={hover === k ? "var(--ink)" : "var(--muted)"}>
                        <tspan fontSize="8.5" fill="var(--muted-soft)">{`${kindTag(p.node.kind)} `}</tspan>
                        {clipFor(p.node.kind, nodeShortLabel(p.node, prefix))}
                      </text>
                    </g>
                  );
                }),
              )}
            </svg>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}

// Same graph, one edge class at a time. With consulted edges outnumbering
// derived ones ~2:1, showing both at once is what turns the flow into a
// hairball — and the two are different claims anyway, so reading them apart is
// usually what you want.
function groupBy(items, keyFn) {
  const m = new Map();
  for (const it of items) {
    const k = keyFn(it);
    const arr = m.get(k);
    if (arr) arr.push(it); else m.set(k, [it]);
  }
  return m;
}

function filterEdgeClass(built, cls) {
  if (cls === "all") return built;
  const want = cls === "derived" ? "derivation" : "consultation";
  const edges = built.edges.filter((e) => e.class === want);
  const idx = (keyFn) => {
    const m = new Map();
    for (const e of edges) {
      const k = keyFn(e);
      const arr = m.get(k) || [];
      arr.push(e);
      m.set(k, arr);
    }
    return m;
  };
  return { ...built, edges, byTarget: idx((e) => e.target.id), bySource: idx((e) => e.source.id) };
}

// The scan's per-lens accumulators, one per lens that DECLARES a lineage
// projection. Driven off the registry so a new lens joins the graph by declaring
// `lineage` in its manifest and touching nothing here — the hand-picked id list
// this replaced was the host deciding what a lens is for on the lens's behalf.
//
// Exported with the pair below because nothing mounts this component in a test,
// so an identifier only the scan effect touches would go unverified until it
// threw in a browser.
export const emptyFactoryBuckets = (registry) =>
  Object.fromEntries((registry?.lineageSources?.() || []).map((s) => [s.id, []]));

// A copy for the state update, matching how the sibling streams are snapshotted:
// the scan mutates its accumulators and hands React a new object each tick.
export const snapshotFactoryBuckets = (buckets) =>
  Object.fromEntries(Object.entries(buckets || {}).map(([id, rows]) => [id, [...rows]]));

// One prompt's turns → each lineage-declaring lens's ops, keyed by lens id.
//
// Through the REGISTRY, never a direct import of the extractors: the manifest
// stays the one place a lens is declared. A lens the registry dropped — it
// discards an invalid manifest by design — is skipped silently, because turning a
// bad manifest into a broken Lineage tab is the worse failure.
export function factoryScanOps(registry, turns) {
  const out = {};
  for (const { id } of registry?.lineageSources?.() || []) {
    const lens = registry.lens(id);
    if (typeof lens?.extract !== "function") continue;
    // Same rule the single-lens scan above states: a lens whose extractor throws
    // must not take the panel down with it. It matters more here — this pass runs
    // ELEVEN lens-authored extractors over every prompt, and its only boundary is
    // the app root, so one bad grammar blanks the dashboard rather than the tab.
    let ops;
    try { ops = lens.extract(turns); }
    catch (err) { console.warn(`[xray] ${id} extractor threw`, err); continue; }
    if (ops?.length) out[id] = ops;
  }
  return out;
}

// The window's provenance nodes, from every op stream the scan collected.
//
// Exported and pure so the assembly can be tested without mounting the view: a
// node builder that is wired but unreachable looks exactly like one that found
// nothing, and that is the failure this seam exists to make visible.
//
// Each lens collects with its OWN `collect`, which wraps collectWindowOps in a
// lens-specific `informedness`. Substituting the generic one would dedupe on the
// wrong signal — for `secrets` a harness-blocked read deliberately outranks a
// granted one, so the generic collect would discard the finding and keep the
// boring sighting.
export function lineageNodes(scan, registry) {
  const sources = registry?.lineageSources?.() || [];
  const factoryOps = {};
  for (const { id } of sources) {
    const rows = scan.factory?.[id];
    const lens = registry.lens(id);
    if (!rows?.length || typeof lens?.collect !== "function") continue;
    // As above: lens-authored, unvalidated beyond "is a function", and one throw
    // here reaches the app root through the provenance memo.
    try { factoryOps[id] = lens.collect(rows); }
    catch (err) { console.warn(`[xray] ${id} collect threw`, err); }
  }
  return [
    ...fileNodes(collectWindowOps(scan.file)),
    ...linearNodes(collectWindowLinearOps(scan.linear)),
    ...webResearchNodes(collectWindowWebResearchOps(scan.web)),
    ...forgeNodes(collectWindowForgeOps(scan.forge)),
    ...queryNodes(collectWindowQueryOps(scan.dataq)),
    ...queryNodes(collectWindowQueryOps(scan.grafana)),
    ...recallNodes(collectWindowRecallOps(scan.recall)),
    ...artifactNodes(collectWindowSkillActivity(scan.marks).marks),
    ...factoryNodes(sources, factoryOps),
  ];
}

// The views this panel can draw the same graph in. Each exists because the
// original bipartite flow cannot answer something — the hint is what it answers,
// and it is worth reading before adding a seventh.
//
// Order is the strip order: flow first because it is what shipped, then the two
// that fix its structural gaps (a chain, and the unlinked half), then the
// aggregate reads, then the two text-bearing views.
export const LINEAGE_MODES = [
  { id: "flow", label: "flow", hint: "Sources on the left, what they produced on the right" },
  { id: "arc", label: "arc", hint: "One time axis with visible chains and unlinked nodes" },
  { id: "depth", label: "depth", hint: "Columns are steps back from something produced" },
  { id: "lanes", label: "lanes", hint: "Time across, sessions down, with edges inside each lane" },
  { id: "arch", label: "arch", hint: "Terms arch over artifacts, with provenance shown as ribbons" },
  { id: "matrix", label: "grid", hint: "Artifact families by source family, grouped by tier" },
  { id: "list", label: "list", hint: "Every output with its feeders and the evidence text" },
];

// Exported for the lineage typing test (kind tags, the census, and the
// edge-class filter).
export function LineagePanel({ scan, built, onSelectTrace }) {
  const [mode, setMode] = useState("flow");
  const [edgeClass, setEdgeClass] = useState("all");
  const targetRefs = useRef(new Map());
  const [focusKey, setFocusKey] = useState(null);
  const prefix = useMemo(() => {
    if (!built) return "";
    // FILE refs only — a web-fetch URL shares no leading '/'-segment with a
    // filesystem path, so feeding URLs collapses the common prefix to "" and
    // defeats path-stripping for every file in a window that mixes the two.
    const refs = [];
    for (const e of built.edges) {
      if (e.source.kind?.startsWith("file") && e.source.ref) refs.push(e.source.ref);
      if (e.target.kind?.startsWith("file") && e.target.ref) refs.push(e.target.ref);
    }
    return commonPathPrefix(refs);
  }, [built]);
  if (scan.status !== "done") {
    return (
      <div className="grid gap-[8px] p-[12px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          {scan.total ? `Hydrating raw payloads ${scan.done}/${scan.total}…` : "Waiting for the prompt window…"}
        </span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (!built || !built.edges.length) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        No provenance links were found between this window&apos;s inputs and outputs.
      </span>
    );
  }
  const s = built.stats;
  const view = filterEdgeClass(built, edgeClass);
  // The graph is node-per-OP, so one artifact edited three times is three
  // targets and one page fetched five times is five sources. Reading it that
  // way repeats the same file and the same evidence over and over. Collapse to
  // the ARTIFACT here, so the header's output count counts distinct things
  // produced rather than production events.
  const identityOf = (n) => `${n.kind.split(".")[0]}:${n.ref || n.label}`;

  const groups = [...groupBy([...view.byTarget.values()].flat(), (e) => identityOf(e.target)).entries()]
    .map(([id, edges]) => {
      // Within one artifact, the same source saying the same thing is one
      // claim however many ops carried it.
      const seen = new Map();
      for (const e of edges) {
        const k = `${identityOf(e.source)}|${e.tier}|${(e.evidence || []).join("␟")}`;
        const prev = seen.get(k);
        if (prev) prev.times += 1;
        else seen.set(k, { ...e, times: 1 });
      }
      const uniq = [...seen.values()];
      const strong = uniq
        .filter((e) => e.tier !== "entity")
        .sort((a, b) => (a.class === b.class ? b.score - a.score : a.class === "derivation" ? -1 : 1));
      const related = uniq.filter((e) => e.tier === "entity");
      const nouns = {};
      for (const e of uniq) { const n = KIND_NOUN[e.source.kind] || "source"; nouns[n] = (nouns[n] || 0) + 1; }
      // Prefer a target node that can open a trace.
      const node = edges.find((e) => e.target.promptId)?.target || edges[0].target;
      return { id, node, count: uniq.length, ops: edges.length, strong, related, nouns };
    })
    .sort((a, b) => b.count - a.count);

  // Left rail: the most influential SOURCES — what fed the most productions.
  // Same collapse on the rail: one page fetched five times is one source that
  // fed N productions, not five rows repeating themselves.
  const hubs = [...groupBy([...view.bySource.values()].flat(), (e) => identityOf(e.source)).entries()]
    .map(([id, edges]) => ({
      id,
      node: edges.find((e) => e.source.promptId)?.source || edges[0].source,
      fed: new Set(edges.map((e) => identityOf(e.target))).size,
    }))
    .filter((h) => h.node.ref || h.node.label)
    .sort((a, b) => b.fed - a.fed)
    .slice(0, 40);

  const jump = (key) => { targetRefs.current.get(key)?.scrollIntoView({ block: "start", behavior: "smooth" }); setFocusKey(key); };
  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="grid min-w-0 gap-[3px] border-b border-[color:var(--line-soft)] px-[12px] py-[6px]">
        <div className="flex min-w-0 items-baseline gap-[10px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {plural(groups.length, "output")} · <span style={PLUS_STYLE}>{s.derivation} derived</span> · {s.consultation} consulted
          {prefix ? <span className="text-[color:var(--muted-soft)]">{"  ·  paths under "}{prefix.split("/").filter(Boolean).slice(-1)[0] || "/"}</span> : null}
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-[2px]">
            {[["all", "both"], ["derived", "derived"], ["consulted", "consulted"]].map(([v, label]) => (
              <button
                key={v}
                type="button"
                onClick={() => setEdgeClass(v)}
                className={[
                  "rounded-[2px] px-[6px] py-[1px] text-[10px] cursor-pointer",
                  edgeClass === v ? "bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]",
                ].join(" ")}
              >
                {label}
              </button>
          ))}
        </div>
        <div className="flex shrink-0 items-center gap-[2px]">
          {LINEAGE_MODES.map(({ id, label, hint }) => (
            <HoverTooltip key={id} label={hint} placement="top">
              <button
                type="button"
                onClick={() => setMode(id)}
                aria-pressed={mode === id}
                className={[
                  "rounded-[2px] px-[6px] py-[1px] text-[10px] uppercase tracking-[0.06em] cursor-pointer",
                  mode === id ? "bg-[color:var(--green-soft)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                ].join(" ")}
              >
                {label}
              </button>
            </HoverTooltip>
          ))}
        </div>
        </div>
        {/* What the graph is made of — the only way to tell an extractor that
            contributes nothing here from one that isn't wired at all. */}
        <KindCensus stats={s} />
      </div>
      {mode === "flow" ? (
        <LineageFlow built={view} prefix={prefix} onSelectTrace={onSelectTrace} />
      ) : mode === "arc" ? (
        <LineageArc built={view} prefix={prefix} onSelectTrace={onSelectTrace} />
      ) : mode === "depth" ? (
        <LineageDepth built={view} prefix={prefix} onSelectTrace={onSelectTrace} />
      ) : mode === "lanes" ? (
        <LineageLanes built={view} prefix={prefix} onSelectTrace={onSelectTrace} />
      ) : mode === "arch" ? (
        <LineageArch built={view} prefix={prefix} onSelectTrace={onSelectTrace} />
      ) : mode === "matrix" ? (
        <LineageMatrix built={view} />
      ) : (
      <div className="grid min-h-0 min-w-0 grid-cols-[236px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Fed the most</div>
          {hubs.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => h.node.promptId && onSelectTrace?.(h.node.promptId)}
              className="flex w-full min-w-0 items-baseline gap-[6px] px-[10px] py-[2px] text-left hover:bg-[color:var(--control-bg)] cursor-pointer"
            >
              <ArtifactLabel node={h.node} prefix={prefix} className="min-w-0 flex-1 text-[11px] leading-[1.4]" />
              <span className="ml-auto shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">→{h.fed}</span>
            </button>
          ))}
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="min-w-0">
          {groups.map((g) => (
            <div
              key={g.id}
              ref={(el) => { if (el) targetRefs.current.set(g.id, el); else targetRefs.current.delete(g.id); }}
              className="border-b border-[color:var(--line-soft)] px-[12px] py-[7px]"
            >
              <button
                type="button"
                onClick={() => g.node.promptId && onSelectTrace?.(g.node.promptId)}
                className="flex w-full min-w-0 items-baseline gap-[8px] text-left cursor-pointer"
              >
                <ArtifactLabel node={g.node} prefix={prefix} strong className="min-w-0 flex-1 text-[12px]" />
                {g.ops > g.count ? (
                  <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
                    {g.ops} ops
                  </span>
                ) : null}
                <span className="ml-auto shrink-0 text-[10px] tracking-[0.04em] text-[color:var(--muted-soft)]">
                  drew on {Object.entries(g.nouns).map(([n, c]) => plural(c, n)).join(" · ")}
                </span>
              </button>
              <div className="mt-[3px] grid gap-[1px]">
                {g.strong.map((e, i) => <EdgeRow key={i} edge={e} prefix={prefix} onSelectTrace={onSelectTrace} />)}
                {g.related.length ? (
                  <div className="flex min-w-0 items-baseline gap-[8px] py-[2px]">
                    <span className="shrink-0 rounded-[2px] border border-[color:var(--line)] px-[5px] py-[0.5px] text-[9px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">related</span>
                    <span className="min-w-0 truncate text-[10.5px] leading-[1.4] text-[color:var(--muted-soft)]">
                      {plural(g.related.length, "source")} sharing {[...new Set(g.related.flatMap((e) => e.evidence))].slice(0, 6).join(", ")}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </ScrollArea>
      </div>
      )}
    </div>
  );
}

// The detail-panel result of "blame this line": look up the file's fs.write
// provenance by TAG (rpath(path)) and pick the first event whose write/edit
// authored the line. One indexed signal_tags read — no event_data ILIKE, no
// hydration. Tenant-wide over the signal's retention window so the author is
// found even when it's outside the current scope/time window.
// Exported for tests (XRayBody.filesGate.test.jsx drives it directly).
export function LineBlamePanel({ line, filePath, traceOf, onSelectTrace }) {
  const [state, setState] = useState({ status: "running", scanned: 0, hit: undefined });
  useEffect(() => {
    let cancelled = false;
    setState({ status: "running", scanned: 0, hit: undefined });
    (async () => {
      // An explicit {from, to} pair — toApiRange passes those through
      // verbatim, whereas a {relative:…} shape falls through its preset
      // lookup and silently becomes the 1M default (the bug that made
      // FS_BLAME_SECONDS dead code).
      const to = new Date();
      const from = new Date(to.getTime() - FS_BLAME_SECONDS * 1000);
      const res = await query("getFsSignalOps", {
        scopePath: [],
        range: { from: from.toISOString(), to: to.toISOString() },
        namespaces: ["fs.write"],
        tag: rpath(filePath),
      });
      if (cancelled) return;
      if (!res?.ok) { setState({ status: "error", error: "fs provenance lookup failed" }); return; }
      const { hit, scanned } = pickFsAuthor(res.rows, line, filePath);
      setState({ status: "done", scanned, hit });
    })();
    return () => { cancelled = true; };
  }, [line, filePath]);

  const hitRow = state.hit ? traceOf.get(state.hit.eventId) : null;
  const view = blameViewKind(state); // pure branch selector (tested in traceLineBlame.test)
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]">
      <div className="grid gap-[4px]">
        <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>Line</span>
        <pre className="m-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[10px] py-[6px] font-mono text-[11px] leading-[1.5] text-[color:var(--ink)]">{line}</pre>
        <span className={NOTE_CLASS}>in …/{targetSuffixLabel(filePath)} · based on fs.write provenance</span>
      </div>
      {view === "running" ? (
        <span className={`${NOTE_CLASS} animate-pulse`}>reading fs provenance…</span>
      ) : view === "error" ? (
        <span className="font-mono text-[11px] leading-[1.4] text-[color:var(--red,#c0392b)] break-words">{state.error}</span>
      ) : view === "hit" ? (
        <div className="grid gap-[6px]">
          <span className={`${NOTE_CLASS} uppercase tracking-[0.06em] text-[color:var(--eyebrow)]`}>Authored by</span>
          <button
            type="button"
            onClick={() => onSelectTrace(state.hit.eventId)}
            className="grid w-full gap-[3px] rounded-[2px] border border-[color:var(--line-soft)] px-[10px] py-[7px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
          >
            <span className="flex items-baseline gap-[8px]">
              <span className="text-[10px] tabular-nums text-[color:var(--muted)]">{sampleWhen(hitRow?.timestamp ?? state.hit.ts)}</span>
              <span className="font-mono text-[10px]" style={PLUS_STYLE}>{state.hit.kind}</span>
              <span className="ml-auto text-[10px] text-[color:var(--muted-soft)]">open trace →</span>
            </span>
            <span className="truncate text-[11.5px] leading-[1.35] text-[color:var(--ink)]">{hitRow?.summary || state.hit.eventId}</span>
            <span className="truncate font-mono text-[10px] text-[color:var(--muted-soft)]">{state.hit.path}</span>
          </button>
          <span className={NOTE_CLASS}>
            found in the fs.write history of this file · session {String(state.hit.sessionId).slice(0, 8)}
          </span>
        </div>
      ) : (
        <span className={NOTE_CLASS}>
          No fs.write operation authored this line within the {Math.round(FS_BLAME_SECONDS / 86400)}-day retention period. It may predate that period or come from an uncaptured channel.
        </span>
      )}
    </ScrollArea>
  );
}

function targetSuffixLabel(p) {
  const segs = String(p || "").replace(/\\/g, "/").split("/").filter(Boolean);
  return segs.slice(-2).join("/");
}

function FileReconstruction({ file, endpointLabel, traceOf, onSelectTrace, onBlameLine }) {
  const rec = useMemo(() => reconstructFile(file.ops), [file]);
  const coverage = rec.total ? Math.round((100 * rec.known) / rec.total) : 0;
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid gap-[10px] px-[2px] py-[8px]">
      <div className="grid gap-[4px]">
        <span className="max-w-full break-all font-mono text-[11.5px] leading-[1.5] text-[color:var(--ink)]">{file.path}</span>
        <span className={NOTE_CLASS}>
          {endpointLabel ? `${endpointLabel} · ` : ""}
          {rec.known} of {rec.lengthKnown ? rec.total : `≥${rec.total}`} lines observed ({coverage}%) ·{" "}
          <span style={PLUS_STYLE}>+{file.plus}</span> <span style={MINUS_STYLE}>−{file.minus}</span> effective ·{" "}
          {file.reads} read{file.reads === 1 ? "" : "s"} · {file.edits} edit{file.edits === 1 ? "" : "s"} · {file.writes} write{file.writes === 1 ? "" : "s"}
          {rec.blindEdits ? ` · ${rec.blindEdits} edit${rec.blindEdits === 1 ? "" : "s"} landed outside observed regions` : ""}
        </span>
        <span className={NOTE_CLASS}>Hover a gutter chip for that line's version history and the traces that touched it.</span>
        <ProvenanceLegend />
      </div>
      <AnnotatedFileBody rec={rec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={0} bordered filePath={file.path} onBlameLine={onBlameLine} />
    </ScrollArea>
  );
}

// The Describe tab body — run-gated LLM read of the window's summaries.
function ScopeDescription({ ai, summariesCount, windowKey, onRun, modelControl = null }) {
  const stale = ai.text != null && ai.forKey !== windowKey;
  if (ai.status === "running") {
    return (
      <div className="grid gap-[6px] py-[4px]">
        <span className={`${NOTE_CLASS} animate-pulse`}>
          Reading {summariesCount} summaries with {ai.model || "the chat's task model"}…
        </span>
      </div>
    );
  }
  if (ai.status === "error") {
    return (
      <div className="grid justify-items-start gap-[8px] py-[4px]">
        <span className="text-[12.5px] leading-[1.5] text-[color:var(--red,#c0392b)] break-words">{ai.error}</span>
        {ai.errorDetail ? (
          <span className={`${NOTE_CLASS} font-mono break-words`}>{ai.errorDetail}</span>
        ) : null}
        <div className="flex items-center gap-[8px]">
          <FilterChip label="Retry" active={false} onClick={onRun} />
          {modelControl}
        </div>
      </div>
    );
  }
  if (ai.text != null) {
    return (
      <div className="grid gap-[10px] py-[4px]">
        <div className="whitespace-pre-wrap text-[12.5px] leading-[1.6] text-[color:var(--ink)]">{ai.text}</div>
        <div className="flex items-center gap-[8px] flex-wrap">
          <FilterChip label={stale ? "Describe current window" : "Re-describe"} active={false} onClick={onRun} />
          {modelControl}
          {stale ? <span className={NOTE_CLASS}>written for a previous window</span> : null}
        </div>
      </div>
    );
  }
  return (
    <LaunchCard
      eyebrow="Describe"
      description={`Summarize the main activity, subtopics, and anomalies across ${summariesCount} prompts in this window.`}
      controls={modelControl ? (
        <div className="flex items-center gap-[8px] min-w-0">
          <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted-soft)]">Model</span>
          {modelControl}
        </div>
      ) : null}
      ctaLabel={`Describe · ${summariesCount} summaries`}
      onLaunch={onRun}
    />
  );
}

// The selected pattern, final form: the IN-GROUP ARCH plus a table.
// The dome carries only this pattern's topics — segments (span ∝
// √sessions) indexed 1..n, ribbons = their shared sessions, tint =
// lift — the same visual grammar as the Terms tab beside it. The
// table below owns the names (full horizontal room), session counts,
// each topic's strongest link, and click-for-evidence; row hover and
// arch hover highlight each other. The out-group is one honest line
// in the footer (measured ~10x the in-group), not a diagram — every
// attempt to draw it (rings, matrix, sankey, nested arches, force)
// buried the signal it was meant to reveal.
const IN_ARCH_H = 420;
// Radial label clearance above the dome (Terms-arch treatment: each topic
// owns its outward ray). The clip length and this pad are a PAIR: an
// 18-char label + count tspan at 9.5px spans ~130px along its ray, so it
// must stay under the pad or apex labels clip at the SVG edge.
const IN_ARCH_PAD_TOP = 140;
const clipLabel = (s, n = 18) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

// log-scaled lift → green mix (min gate ~4; ~64x saturates).
function liftFill(lift) {
  const t = Math.max(0, Math.min(1, (Math.log(lift ?? 4) - Math.log(4)) / (Math.log(64) - Math.log(4))));
  return `color-mix(in srgb, var(--green) ${Math.round(22 + 66 * t)}%, var(--line-soft))`;
}

function PatternArch({ pattern, flows, nameOf, rawNameOf, openTopic, evidence, onToggleTopic, endpointMix, hostnameOf, onSelectTrace }) {
  const hostRef = useRef(null);
  const [size, setSize] = useState({ w: 0 });
  const [hover, setHover] = useState(null); // {kind:"edge",a,b,...} | {kind:"node",id} | null
  useEffect(() => {
    const node = hostRef.current;
    if (!node) return undefined;
    const measure = () => setSize({ w: Math.max(560, Math.round(node.getBoundingClientRect().width)) });
    measure();
    return observeSettledSize(node, measure);
  }, []);

  const model = useMemo(() => {
    const memberSet = new Set(pattern.leaves);
    // members ordered by session volume — the table order AND the index
    // numbers on the dome (buildArch itself lays segments alphabetically
    // by key, so index-prefixed keys carry the volume order onto the arc).
    const members = pattern.leaves.slice().sort(
      (x, y) => (flows.topicSessions.get(y) ?? 0) - (flows.topicSessions.get(x) ?? 0),
    );
    const keyOf = new Map(members.map((m, k) => [m, String(k + 1).padStart(2, "0")]));
    const idOf = new Map(members.map((m, k) => [String(k + 1).padStart(2, "0"), m]));
    const terms = members.map((m) => ({ term: keyOf.get(m), count: Math.sqrt(flows.topicSessions.get(m) ?? 1) }));
    const edges = pattern.edges.map((e) => ({ ...e, a: keyOf.get(e.a), b: keyOf.get(e.b), weight: e.support }));
    const arch = buildArch(terms, edges);
    // full adjacency for the table: every link, listed under both ends
    const linksOf = new Map(members.map((m) => [m, []]));
    for (const e of pattern.edges) {
      linksOf.get(e.a)?.push({ other: e.b, support: e.support, lift: e.lift });
      linksOf.get(e.b)?.push({ other: e.a, support: e.support, lift: e.lift });
    }
    for (const arr of linksOf.values()) arr.sort((x, y) => y.support - x.support);
    // out-group accounting for the footer line only
    let internalAll = 0;
    let externalAll = 0;
    const partners = new Set();
    for (const [key, support] of flows.pairSupports ?? new Map()) {
      const [a, b] = key.split("|");
      const aIn = memberSet.has(a);
      const bIn = memberSet.has(b);
      if (!aIn && !bIn) continue;
      if (aIn && bIn) internalAll += support;
      else { externalAll += support; partners.add(aIn ? b : a); }
    }
    const concentration = internalAll + externalAll > 0 ? internalAll / (internalAll + externalAll) : 0;
    return { members, keyOf, idOf, arch, linksOf, concentration, outsidePartners: partners.size };
  }, [pattern, flows]);

  const { members, idOf, arch, linksOf, concentration, outsidePartners } = model;
  if (members.length < 2) return null;

  const W = size.w || 720;
  const H = IN_ARCH_H;
  // Keep the dome ROUND on wide panels: cap rx at ~1.2x the available
  // height instead of stretching to the panel edges — a 3:1 ellipse
  // reads as a flat smear, not an arch. padX floors at the radial-label
  // budget so side labels don't clip.
  const availY = H - IN_ARCH_PAD_TOP - 22;
  const padX = Math.max(150, W / 2 - availY * 1.2);
  const { cx, cy, rx, ry, psi } = archFrame(W, H, { padX, padTop: IN_ARCH_PAD_TOP, padBottom: 22, minRx: 150, minRy: 70 });
  const sweep = Math.PI + 2 * psi;
  const P = (t, dr = 0) => {
    const th = -psi + (t / Math.PI) * sweep;
    const px = cx + rx * Math.cos(th);
    const py = cy - ry * Math.sin(th);
    if (!dr) return [px, py];
    const vx = px - cx;
    const vy = py - cy;
    const n = Math.sqrt(vx * vx + vy * vy) || 1;
    return [px + (vx / n) * dr, py + (vy / n) * dr];
  };
  const segPath = (a, b, dr = 0) => {
    const steps = Math.max(3, Math.ceil((a - b) * (sweep / Math.PI) * 24));
    let d = "";
    for (let k = 0; k <= steps; k++) {
      const [x, y] = P(a + (b - a) * (k / steps), dr);
      d += `${k ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)} `;
    }
    return d;
  };
  const hoverId = hover?.kind === "node" ? hover.id : null;
  const litKey = (k) => {
    const id = idOf.get(k);
    if (hoverId) {
      if (id === hoverId) return true;
      return arch.ribbons.some((r) => {
        const ra = idOf.get(r.a);
        const rb = idOf.get(r.b);
        return (ra === hoverId && rb === id) || (rb === hoverId && ra === id);
      });
    }
    return hover?.kind === "edge" && (hover.a === id || hover.b === id);
  };
  const dim = (lit) => (hover ? (lit ? 1 : 0.15) : 1);
  const ev = openTopic ? evidence.get(openTopic) : null;

  return (
    <div className="min-w-0">
      <div ref={hostRef} className="min-w-0 overflow-hidden">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          width="100%"
          height={H}
          role="img"
          aria-label="Pattern arch. Numbered segments are topics; ribbons show shared sessions."
          onMouseLeave={() => setHover(null)}
        >
          {arch.ribbons
            .slice()
            .sort((x, y) => x.weight - y.weight)
            .map((r) => {
              if (!r.spanA || !r.spanB) return null;
              const ma = P((r.spanA[0] + r.spanA[1]) / 2);
              const mb = P((r.spanB[0] + r.spanB[1]) / 2);
              const k = 0.58;
              const ctrl = [((ma[0] + mb[0]) / 2) * (1 - k) + cx * k, ((ma[1] + mb[1]) / 2) * (1 - k) + cy * k];
              const a = idOf.get(r.a);
              const b = idOf.get(r.b);
              const lit = hover?.kind === "edge"
                ? hover.a === a && hover.b === b
                : hoverId != null && (a === hoverId || b === hoverId);
              return (
                <path
                  key={`${r.a}|${r.b}`}
                  d={archRibbonPath(P, r.spanA, r.spanB, ctrl)}
                  fill={liftFill(r.lift)}
                  opacity={dim(lit) * 0.62}
                  onMouseEnter={() => setHover({ kind: "edge", a, b, lift: r.lift, support: r.support })}
                />
              );
            })}
          {arch.order.map((k) => {
            const id = idOf.get(k);
            const { a, b } = arch.segments.get(k);
            const open = openTopic === id;
            const lit = litKey(k) || open;
            const [lx, ly] = P((a + b) / 2, 13);
            // Radial labels — the Terms-arch treatment: each topic owns its
            // outward ray, so labels can't collide however round the dome
            // gets; left-half labels flip to stay right-side-up.
            const phi = (Math.atan2(ly - cy, lx - cx) * 180) / Math.PI;
            const flip = phi > 90 || phi < -90;
            const rot = flip ? phi + 180 : phi;
            return (
              <g
                key={k}
                style={{ cursor: "pointer" }}
                opacity={dim(lit)}
                onMouseEnter={() => setHover({ kind: "node", id })}
                onClick={() => onToggleTopic(id)}
              >
                <path d={segPath(a, b)} fill="none"
                  stroke={open ? "var(--green)" : "color-mix(in srgb, var(--green) 72%, var(--line-soft))"}
                  strokeWidth={lit ? 11 : 8} />
                <text
                  x={lx.toFixed(1)}
                  y={(ly + 3).toFixed(1)}
                  textAnchor={flip ? "end" : "start"}
                  fontSize={9.5}
                  fill={open ? "var(--green)" : lit ? "var(--ink)" : "var(--muted)"}
                  fontWeight={lit ? 500 : 400}
                  transform={`rotate(${rot.toFixed(1)} ${lx.toFixed(1)} ${ly.toFixed(1)})`}
                >
                  {clipLabel(nameOf(id))}
                  <tspan fill="var(--muted-soft)" fontSize="8">{` ${flows.topicSessions.get(id) ?? 0}`}</tspan>
                </text>
                <path d={segPath(a, b)} fill="none" stroke="transparent" strokeWidth={22} />
              </g>
            );
          })}
        </svg>
      </div>
      <div className="border-b border-[color:var(--line-soft)] px-[10px] py-[4px]">
        {hover?.kind === "edge" ? (
          <span className={NOTE_CLASS}>
            <span className="text-[color:var(--ink)]">{nameOf(hover.a)}</span>
            {" ↔ "}
            <span className="text-[color:var(--ink)]">{nameOf(hover.b)}</span>
            {` · ${plural(hover.support, "shared session")} · ~${Math.round(hover.lift)}× chance`}
          </span>
        ) : hoverId ? (
          <span className={NOTE_CLASS}>
            <span className="text-[color:var(--ink)]">{nameOf(hoverId)}</span>
            {` · ${plural(flows.topicSessions.get(hoverId) ?? 0, "session")} · click for evidence`}
          </span>
        ) : (
          <span className={NOTE_CLASS}>
            Ribbons show shared sessions within the pattern. Greener ribbons are further above chance. This pattern contains{" "}
            {Math.round(concentration * 100)}% of recurring co-occurrence; the rest spans{" "}
            {outsidePartners.toLocaleString("en-US")} outside topics.
          </span>
        )}
      </div>
      {endpointMix?.total ? (
        <div className="border-b border-[color:var(--line-soft)] px-[10px] py-[4px]">
          <span className={NOTE_CLASS}>
            {plural(endpointMix.total, "co-occurring session")} on {plural(endpointMix.top.length, "endpoint")} ·{" "}
            {endpointMix.top.slice(0, 3).map(([ep, n], i) => (
              <span key={ep ?? "unknown"}>
                {i ? " · " : ""}
                <span className="text-[color:var(--ink)]">{hostnameOf(ep)}</span>
                {` ${Math.round((100 * n) / endpointMix.total)}%`}
              </span>
            ))}
            {endpointMix.top.length > 3 ? ` · +${endpointMix.top.length - 3} more` : ""}
          </span>
        </div>
      ) : null}
      {/* one table: topic on the left, its full link stack on the right */}
      <div className="grid px-[10px] pt-[4px]" style={{ gridTemplateColumns: "minmax(0, 1.15fr) 64px minmax(0, 1.5fr)" }}>
        <div className={`${NOTE_CLASS} leading-[20px]`}>topic</div>
        <div className={`${NOTE_CLASS} text-right leading-[20px] pr-[12px]`}>sessions</div>
        <div className={`${NOTE_CLASS} leading-[20px]`}>links · shared sessions</div>
        {members.map((m) => {
          const open = openTopic === m;
          const lit = hoverId === m || open;
          const links = linksOf.get(m) ?? [];
          const cellBase = "border-t border-[color:var(--line-soft)] py-[3px]";
          const rowBg = open ? "bg-[color:var(--green-soft)]" : lit ? "bg-[color:var(--control-bg)]" : "";
          const pretty = nameOf(m);
          const raw = rawNameOf(m);
          return [
            <div
              key={`${m}-n`}
              className={`${cellBase} ${rowBg} min-w-0 cursor-pointer pr-[8px]`}
              onMouseEnter={() => setHover({ kind: "node", id: m })}
              onClick={() => onToggleTopic(m)}
            >
              {/* `block` is load-bearing: OverflowTooltip renders an inline
                  span, and CSS overflow (→ truncate) is ignored on inline
                  boxes — without it the name spills across the sessions
                  column instead of ellipsising. */}
              <OverflowTooltip
                text={pretty}
                tooltipContent={raw !== pretty ? raw : pretty}
                className={`block min-w-0 truncate text-[11.5px] leading-[20px] ${lit ? "text-[color:var(--ink)]" : ""}`}
                lines={1}
              />
            </div>,
            <div
              key={`${m}-s`}
              className={`${cellBase} ${rowBg} cursor-pointer pr-[12px] text-right font-mono text-[10px] tabular-nums leading-[20px] text-[color:var(--muted-soft)]`}
              onMouseEnter={() => setHover({ kind: "node", id: m })}
              onClick={() => onToggleTopic(m)}
            >
              {flows.topicSessions.get(m) ?? 0}
            </div>,
            <div key={`${m}-l`} className={`${cellBase} ${rowBg} min-w-0`}>
              {links.length ? links.map((ln) => {
                const litLn = hover?.kind === "edge"
                  && ((hover.a === m && hover.b === ln.other) || (hover.a === ln.other && hover.b === m));
                return (
                  <div
                    key={ln.other}
                    className={`flex min-w-0 cursor-pointer items-baseline gap-[6px] rounded-[2px] px-[2px] text-[11px] leading-[20px] ${
                      litLn ? "bg-[color:var(--control-bg)] text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:bg-[color:var(--control-bg)]"
                    }`}
                    onMouseEnter={() => setHover({ kind: "edge", a: m, b: ln.other, lift: ln.lift, support: ln.support })}
                    onClick={() => onToggleTopic(ln.other)}
                  >
                    <span className="inline-block h-[8px] w-[14px] shrink-0 self-center rounded-[2px]" style={{ background: liftFill(ln.lift) }} />
                    <span className="min-w-0 truncate">{nameOf(ln.other)}</span>
                    <span className="shrink-0 font-mono text-[9.5px] tabular-nums text-[color:var(--muted-soft)]">
                      {ln.support} · ~{Math.round(ln.lift)}×
                    </span>
                  </div>
                );
              }) : (
                <span className={`${NOTE_CLASS} leading-[20px]`}>—</span>
              )}
            </div>,
            open ? (
              <div
                key={`${m}-ev`}
                className="mb-[6px]"
                style={{ gridColumn: "1 / -1" }}
                aria-busy={ev?.loading ? "true" : undefined}
              >
                {ev?.error ? (
                  <span className={`${NOTE_CLASS} block py-[4px]`}>
                    Couldn't load example prompts. Select the topic to retry.
                  </span>
                ) : (
                  <>
                    {ev?.capped ? (
                      <span className={`${NOTE_CLASS} block py-[2px]`}>
                        Examples sample {TOPIC_EVIDENCE_SESSION_CAP.toLocaleString("en-US")} of this topic's in-scope sessions; the rest are not searched.
                      </span>
                    ) : null}
                    <TraceTable
                      rows={ev?.rows ?? []}
                      loading={!ev || ev.loading}
                      showEndpoint
                      onSelectTrace={onSelectTrace}
                      emptyMessage="No prompts in this window."
                    />
                  </>
                )}
              </div>
            ) : null,
          ];
        })}
      </div>
    </div>
  );
}

// ── Topic patterns panel — session co-occurrence over the ILO taxonomy.
// Which topics share the same work sessions far more often than chance:
// semantically DISTANT pairs group into recurring cross-topic patterns
// (left rail),
// taxonomy-NEAR pairs are merge evidence (one activity the clusterer
// split). Computed over every clustered session in the dashboard's date
// range, CURRENT clustering generation — what this tab ignores (like
// Behaviours) is the X-ray scan window's sampled prompts, NOT the date
// picker; narrowing the picker narrows the co-occurrence corpus too.
// Deep leiden leaves carry machine names (underscore TF-IDF); display
// prettifies to words. Lift reuses the Terms arch's tint + label.

const topicWords = (name) => String(name ?? "").replace(/_/g, " ").trim();

function TopicLiftSwatch({ lift }) {
  return (
    <span className="flex shrink-0 items-baseline gap-[5px] self-center">
      <span className="h-[9px] w-[18px] rounded-[2px]" style={{ background: archTint(lift) }} />
      <span className="w-[34px] font-mono text-[10px] tabular-nums text-[color:var(--muted)]">{liftLabel(lift)}</span>
    </span>
  );
}

function TopicEdgeRow({ edge, nameOf }) {
  return (
    <div className="flex min-w-0 items-baseline gap-[8px] px-[10px] py-[2px]">
      <TopicLiftSwatch lift={edge.lift} />
      <span className="min-w-0 flex-1 truncate text-[11px] leading-[1.5]">
        {nameOf(edge.a)}
        <span className="px-[5px] text-[color:var(--muted-soft)]">↔</span>
        {nameOf(edge.b)}
      </span>
      <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
        {plural(edge.support, "session")}{edge.relation !== "unrelated" ? ` · ${edge.relation}` : ""}
      </span>
    </div>
  );
}

function TopicFlowsPanel({ range, scopePath = [], onSelectTrace, steer = "", onSteerScope = null }) {
  const epoch = useDataEpoch();
  const rangeKey = JSON.stringify(range ?? null);
  const scopeKey = JSON.stringify(scopePath ?? []);
  const steering = Boolean(steer && steer.trim());
  // How this scope reaches the corpus: endpoint/session steps push down into
  // the pair query; other supported dims ride the session-set intersection;
  // inexpressible dims are disclosed in the header (topicScopePlan).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scopePlan = useMemo(() => topicScopePlan(scopePath), [scopeKey]);
  const [minSupport, setMinSupport] = useState(TOPIC_FLOW_DEFAULTS.minSupport);
  const [minLift, setMinLift] = useState(TOPIC_FLOW_DEFAULTS.minLift);
  const [state, setState] = useState({ loading: true, error: null, tree: null, rows: null, truncated: false });
  const [selected, setSelected] = useState(null); // arch id | "merge"
  const [openTopic, setOpenTopic] = useState(null);
  const [evidence, setEvidence] = useState(new Map());
  const [hostnames, setHostnames] = useState(new Map());

  useEffect(() => {
    const abort = new AbortController();
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    setSelected(null); setOpenTopic(null); setEvidence(new Map());
    Promise.all([loadClusterTree(), fetchSessionTopicPairs(range, { signal: abort.signal, scope: scopePlan.ecFilter })])
      .then(([tree, pairs]) => {
        if (!alive) return;
        setState({ loading: false, error: null, tree, rows: pairs.rows, truncated: pairs.truncated });
      })
      .catch((err) => {
        if (!alive || abort.signal.aborted) return;
        setState({ loading: false, error: err, tree: null, rows: null, truncated: false });
      });
    return () => { alive = false; abort.abort(); };
    // rangeKey/scopeKey are the stable identities of `range`/`scopePath`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rangeKey, scopeKey, epoch]);

  // The pair query can't carry a PROMPT-level steer (it roots at the event-
  // cluster entity) or a non-endpoint/session scope dim, so both resolve the
  // SESSION set separately and intersect client-side: sessions with at least
  // one in-scope (and, when steering, steered) prompt. The resolution
  // carries the scope's parentScopePath, so a steered set is already
  // steer AND scope. The intersection + cap semantics are pure
  // (steeredPairRows, tested in topicFlows.test.js); this block only feeds it.
  const resolving = steering || scopePlan.needsResolution;
  const { data: scopeSessionsData } = useQuery("getDimensionEntries", {
    // labels:false is load-bearing: this resolution only needs session IDS,
    // and the sessions label overlay is one query PER SESSION — a full page
    // here would fan out into thousands of requests.
    dimKey: "sessions", parentScopePath: scopePlan.resolutionScope, range,
    limit: STEER_SESSION_CAP, sparkline: false, labels: false, offset: 0,
    ...(steering ? { query: steer } : {}),
  }, { staleMs: Infinity, enabled: resolving });
  const { rows, capped: sessionSetCapped } = useMemo(() => {
    if (!resolving) return { rows: state.rows, capped: false };
    return steeredPairRows(state.rows, scopeSessionsData?.entries);
  }, [state.rows, resolving, scopeSessionsData]);

  // The threshold-independent pairing pass is the expensive half — memo it
  // on the row set alone so a threshold chip click only re-runs the cheap
  // filter/grouping stage, not the O(sessions × topics²) count.
  const flowIndex = useMemo(() => (rows ? indexTopicPairs(rows) : null), [rows]);
  const flows = useMemo(
    () => (flowIndex && state.tree ? buildTopicFlows(rows, state.tree, { minSupport, minLift, index: flowIndex }) : null),
    [flowIndex, rows, state.tree, minSupport, minLift],
  );
  const rawNameOf = useMemo(() => {
    const byId = state.tree?.byId;
    return (id) => byId?.get(String(id))?.name ?? `topic #${id}`;
  }, [state.tree]);
  const nameOf = useMemo(() => (id) => topicWords(rawNameOf(id)) || `topic #${id}`, [rawNameOf]);

  const arch = flows && selected !== "merge"
    ? flows.archetypes.find((a) => a.id === selected) ?? flows.archetypes[0] ?? null
    : null;
  const showMerge = selected === "merge" || (flows && !flows.archetypes.length);
  // Which endpoints this pattern's co-occurring sessions ran on: a session
  // counts when it touches ≥2 member topics (i.e. contributes at least one
  // in-group pair, thresholds aside).
  const sessionEndpoint = useMemo(() => sessionEndpoints(rows), [rows]);
  const endpointMix = useMemo(() => {
    if (!arch || !flows?.sessionTopics) return null;
    const members = new Set(arch.leaves);
    const counts = new Map();
    const sessions = [];
    for (const [session, set] of flows.sessionTopics) {
      let hits = 0;
      for (const id of set) {
        if (members.has(id) && (hits += 1) >= 2) break;
      }
      if (hits < 2) continue;
      sessions.push(session);
      const ep = sessionEndpoint.get(session) ?? null;
      counts.set(ep, (counts.get(ep) ?? 0) + 1);
    }
    return { total: sessions.length, sessions, top: [...counts.entries()].sort((x, y) => y[1] - x[1]) };
  }, [arch, flows, sessionEndpoint]);
  const patternSteer = onSteerScope && endpointMix?.sessions?.length
    ? sessionSteer(endpointMix.sessions)
    : null;

  // Resolve hostnames only for the ids the strip actually displays (top 3
  // of the selected pattern) — a whole-fleet page would silently overflow
  // on a Large tenant. Misses are cached as null so a nameless endpoint
  // doesn't refetch on every render; labels are decoration, so failures
  // degrade to id prefixes.
  const displayedEndpointIds = useMemo(
    () => (endpointMix?.top ?? []).slice(0, 3).map(([ep]) => ep).filter(Boolean),
    [endpointMix],
  );
  const missingHostnameKey = displayedEndpointIds.filter((id) => !hostnames.has(id)).join(",");
  useEffect(() => {
    if (!missingHostnameKey) return undefined;
    const missing = missingHostnameKey.split(",");
    const abort = new AbortController();
    fetchEndpointHostnames({ ids: missing, signal: abort.signal })
      .then((got) => {
        setHostnames((prev) => {
          const next = new Map(prev);
          for (const id of missing) next.set(id, got.get(id) ?? null);
          return next;
        });
      })
      .catch(() => {});
    return () => abort.abort();
  }, [missingHostnameKey]);
  const hostnameOf = (ep) =>
    (ep ? hostnames.get(ep) ?? `${String(ep).slice(0, 8)}…` : "unattributed");

  const toggleTopic = (id) => {
    setOpenTopic((cur) => (cur === id ? null : id));
    // A failed fetch stays retryable — clicking the topic again refetches.
    const cached = evidence.get(id);
    if (cached && !cached.error) return;
    setEvidence((prev) => new Map(prev).set(id, { loading: true, rows: [] }));
    // Evidence = the real trace listing scoped to this topic, so the rows
    // carry the full trace-log treatment and click through to the trace
    // detail. A sensitive-prompt denial retries with the redacted
    // projection (rows render "Redacted prompt — click to view") instead
    // of dead-ending.
    // Scoped runs fetch the topic's examples from the CORPUS sessions that
    // contain it — never by prefixing the ambient scope steps, which breaks
    // at cluster scope: the topic step and the scope's own cluster step AND
    // into two subtree predicates on the same event-cluster join, which no
    // prompt satisfies for unrelated topics. The corpus rows are already
    // scope-enforced, so their sessions ARE the scope at this lens's grain.
    const topicStep = { dim: "clusters", id: String(id), ids: [String(id)] };
    const scopedEvidence = scopePlan.scoped ? topicEvidenceSessions(rows, id) : null;
    if (scopedEvidence && !scopedEvidence.sessions.length) {
      setEvidence((prev) => new Map(prev).set(id, { loading: false, rows: [] }));
      return;
    }
    const params = {
      scopePath: scopedEvidence
        ? [{ dim: "sessions", id: scopedEvidence.sessions[0], ids: scopedEvidence.sessions }, topicStep]
        : [topicStep],
      limit: 4, range,
      ...(steering ? { query: steer } : {}),
    };
    query("getTraces", params, { cache: true })
      .catch((err) => {
        if (!(err instanceof AnalyticsDenialError)) throw err;
        return query("getTraces", { ...params, canReadSensitivePrompts: false }, { cache: true });
      })
      .then((rows) => setEvidence((prev) => new Map(prev).set(id, { loading: false, rows, capped: Boolean(scopedEvidence?.capped) })))
      .catch(() => setEvidence((prev) => new Map(prev).set(id, { loading: false, rows: [], error: true })));
  };

  if (state.loading || (!state.error && !flows)) {
    return (
      <div className="grid gap-[8px] p-[12px]" aria-busy="true">
        <span className={`${NOTE_CLASS} animate-pulse`}>Fetching session–topic pairs…</span>
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="h-[14px] rounded-[2px] bg-[color:var(--line-soft)]" style={{ opacity: 0.7 - i * 0.18 }} />
        ))}
      </div>
    );
  }
  if (state.error) {
    return (
      <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
        Topic patterns unavailable: {String(state.error?.message ?? state.error)}
      </span>
    );
  }
  if (!flows) return null;

  return (
    <div className="grid h-full min-w-0 grid-rows-[auto_minmax(0,1fr)]">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-[10px] gap-y-[4px] border-b border-[color:var(--line-soft)] px-[12px] py-[7px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          {plural(flows.stats.sessionsUsed, "session")} (2–40 topics each) · {plural(flows.stats.workflowCount, "cross-topic link")} ·{" "}
          {plural(flows.stats.mergeCount, "merge signal")} · {plural(flows.archetypes.length, "pattern")} ·{" "}
          {steering
            ? (scopePlan.scoped ? "steered sessions in this scope, in range" : "steered sessions in range")
            : scopePlan.scoped ? "this scope's sessions in range" : "whole tenant in range"}, current generation
          {scopePlan.unappliedDims.length ? ` · scope filter not applied: ${scopePlan.unappliedDims.join(", ")}` : ""}
          {sessionSetCapped ? ` · limited to the ${STEER_SESSION_CAP.toLocaleString("en-US")} most active sessions; totals may be low` : ""}
          {state.truncated ? " · pair limit reached; totals may be low" : ""}
        </span>
        <span className="ml-auto flex shrink-0 items-center gap-[4px]">
          <span className={NOTE_CLASS}>sessions</span>
          {[4, 6, 10].map((c) => (
            <FilterChip key={c} label={`≥${c}`} active={minSupport === c} onClick={() => setMinSupport(c)} />
          ))}
          <span className={`${NOTE_CLASS} ml-[6px]`}>lift</span>
          {[4, 8, 15].map((c) => (
            <FilterChip key={c} label={`≥${c}`} active={minLift === c} onClick={() => setMinLift(c)} />
          ))}
        </span>
      </div>
      <div className="grid min-h-0 min-w-0 grid-cols-[232px_minmax(0,1fr)]">
        <ScrollArea className="min-h-0 h-full border-r border-[color:var(--line-soft)]" contentClassName="py-[4px]">
          <div className="truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
            Patterns
          </div>
          {flows.archetypes.map((a) => {
            const active = !showMerge && arch?.id === a.id;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => { setSelected(a.id); setOpenTopic(null); }}
                className={[
                  "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
                  active ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
                ].join(" ")}
              >
                <span className={`min-w-0 truncate text-[11px] leading-[1.5] ${active ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
                  {nameOf(a.leaves[0])} + {a.leaves.length - 1}
                </span>
                <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{a.support}</span>
              </button>
            );
          })}
          {!flows.archetypes.length ? (
            <span className={`${NOTE_CLASS} block px-[10px] py-[2px]`}>none at these thresholds</span>
          ) : null}
          <div className="mt-[6px] truncate px-[10px] py-[4px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
            Topic model feedback
          </div>
          <button
            type="button"
            onClick={() => { setSelected("merge"); setOpenTopic(null); }}
            className={[
              "flex w-full min-w-0 items-baseline justify-between gap-[8px] px-[10px] py-[2px] text-left cursor-pointer",
              showMerge ? "bg-[color:var(--green-soft)]" : "hover:bg-[color:var(--control-bg)]",
            ].join(" ")}
          >
            <span className={`min-w-0 truncate text-[11px] leading-[1.5] ${showMerge ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>
              Merge evidence
            </span>
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{flows.stats.mergeCount}</span>
          </button>
        </ScrollArea>
        <ScrollArea className="min-h-0 h-full" contentClassName="py-[6px]">
          {showMerge ? (
            <>
              <span className={`${NOTE_CLASS} block px-[10px] pb-[4px]`}>
                Nearby taxonomy pairs with unusually high shared-session counts. The topic model may have split one activity.
              </span>
              {flows.mergeEdges.slice(0, 30).map((e) => (
                <TopicEdgeRow key={`${e.a}|${e.b}`} edge={e} nameOf={nameOf} />
              ))}
              {!flows.mergeEdges.length ? (
                <span className={`${NOTE_CLASS} block px-[10px]`}>none at these thresholds</span>
              ) : null}
            </>
          ) : arch ? (
            <>
              <div className="flex items-baseline gap-[8px] px-[10px] py-[4px]">
                <span className="min-w-0 truncate text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                  {plural(arch.leaves.length, "topic")} · {plural(arch.support, "co-occurring session")}
                </span>
                {patternSteer ? (
                  <button
                    type="button"
                    onClick={() => onSteerScope(patternSteer.query)}
                    className="ml-auto shrink-0 cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-[10px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]"
                  >
                    {patternSteer.label}
                  </button>
                ) : null}
              </div>
              <PatternArch
                pattern={arch}
                flows={flows}
                nameOf={nameOf}
                rawNameOf={rawNameOf}
                openTopic={openTopic}
                evidence={evidence}
                onToggleTopic={toggleTopic}
                endpointMix={endpointMix}
                hostnameOf={hostnameOf}
                onSelectTrace={onSelectTrace}
              />
            </>
          ) : null}
        </ScrollArea>
      </div>
    </div>
  );
}

export default function XRayBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const scopeLabel = terminal ? scopeStepLabel(terminal) : "Organization";
  const scopeKey = encodeScopePath(scopePath) || "org";

  // ── Substrate windows — each X-ray tab samples prompts that actually
  // carry its substrate (see lib/xraySubstrates): the scope's tool entries
  // resolve the tab's tool ids, and the window query composes the scope with
  // a tools step over them. Tabs without a substrate keep the plain window.
  const [view, setView] = useState("map"); // "map" | "behaviours" | "files" | "linear" | "research" | "forge" | "grafana" | "dataq" | "recall" | "artifacts" | "topiclinks" | "lineage" | "describe" | "workflows" | "wiki" | "prompts"
  const steerQuery = ctx?.query || "";
  const { data: toolEntriesData, loading: toolEntriesLoading } = useQuery("getDimensionEntries", {
    dimKey: "tools", parentScopePath: scopePath, range: ctx?.range,
    limit: 400, sparkline: false, offset: 0,
    ...(steerQuery ? { query: steerQuery } : {}),
  }, { staleMs: Infinity });
  const substrate = useMemo(
    () => substrateStepFor(view, toolEntriesData?.entries),
    [view, toolEntriesData],
  );
  // A substrate tab with the entries still IN FLIGHT must NOT fetch the
  // unfiltered window (it would double-fetch + double-scan on arrival). A
  // settled miss — a denied role or persistent error leaves data undefined
  // with loading false — is an ANSWER: degrade to the plain unfiltered
  // window (substrateStepFor returns null) rather than resolve forever.
  const substrateResolving = Boolean(xraySubstrates()[view]) && !toolEntriesData && toolEntriesLoading;
  const windowScopePath = useMemo(
    () => (substrate?.step ? [...scopePath, substrate.step] : scopePath),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [encodeScopePath(scopePath), substrate],
  );
  const { ids, idsKey, windowTraces, scrubber, windowPending: windowPendingRaw } = useScanWindow({
    scopePath: windowScopePath,
    range: ctx?.range,
    limit: SCAN_LIMIT,
    scopeTotal: ctx?.traces?.total,
    enabled: !substrateResolving && !substrate?.empty,
    query: steerQuery,
  });
  const windowPending = windowPendingRaw || substrateResolving;
  const windowKey = `${scopeKey}:${idsKey}`;

  const [term, setTerm] = useState(null);
  useEffect(() => { setTerm(null); }, [scopeKey]);

  // The prompts takeover is toggled from the scrubber bar, not the tab
  // strip — remember the analysis tab underneath it so closing the
  // listing lands back where the user was.
  const lastAnalysisView = useRef("map");
  useEffect(() => { if (view !== "prompts") lastAnalysisView.current = view; }, [view]);
  // Prompts replaces the analysis body, so Files' two independent
  // OverlayScrollbars viewports are destroyed during the takeover. Keep their
  // offsets at the host that survives it, and key the snapshot to the exact
  // Explore scope/window so obsolete file content never inherits positions.
  const fileScrollInstances = useRef({ nav: null, content: null });
  const fileScrollSnapshot = useRef({ windowKey, nav: 0, content: 0 });
  const previousScrollView = useRef(view);
  const setFileScrollInstance = useCallback((owner, instance) => {
    fileScrollInstances.current[owner] = instance;
    const viewport = scrollViewportOf(instance);
    if (!viewport) return;
    const saved = fileScrollSnapshot.current;
    viewport.scrollTop = saved.windowKey === windowKey ? saved[owner] : 0;
  }, [windowKey]);
  const setFileNavScrollInstance = useCallback(
    (instance) => setFileScrollInstance("nav", instance),
    [setFileScrollInstance],
  );
  const setFileContentScrollInstance = useCallback(
    (instance) => setFileScrollInstance("content", instance),
    [setFileScrollInstance],
  );
  const rememberFileScroll = useCallback(() => {
    const saved = fileScrollSnapshot.current;
    fileScrollSnapshot.current = {
      windowKey,
      nav: scrollViewportOf(fileScrollInstances.current.nav)?.scrollTop ?? (saved.windowKey === windowKey ? saved.nav : 0),
      content: scrollViewportOf(fileScrollInstances.current.content)?.scrollTop ?? (saved.windowKey === windowKey ? saved.content : 0),
    };
  }, [windowKey]);
  useLayoutEffect(() => {
    const previousView = previousScrollView.current;
    previousScrollView.current = view;
    // `windowKey` is view-dependent: substrate tabs (Files included) sample
    // their own prompt set, while Prompts deliberately switches back to the
    // unfiltered set. That temporary takeover is not a Files-window change and
    // must not invalidate the offsets we just captured. Wait for the Files
    // window to settle before deciding whether its actual data changed.
    if (!shouldResetFilesScrollSnapshot({
      view,
      previousView,
      windowPending,
      savedWindowKey: fileScrollSnapshot.current.windowKey,
      windowKey,
    })) return;
    fileScrollSnapshot.current = { windowKey, nav: 0, content: 0 };
    const navViewport = scrollViewportOf(fileScrollInstances.current.nav);
    const contentViewport = scrollViewportOf(fileScrollInstances.current.content);
    if (navViewport) navViewport.scrollTop = 0;
    if (contentViewport) contentViewport.scrollTop = 0;
  }, [view, windowPending, windowKey]);

  // The Files tab reads the `fs` signal directly (manifest deploy_to: none, so
  // per-tenant) with NO hydration fallback, so — exactly like the Memories dim —
  // it stays hidden until we confirm this tenant has fs data; otherwise it would
  // render an empty tree. undefined (loading) / false (absent) → hidden; true →
  // shown. Self-corrects once the signal is deployed for the tenant. If the tab
  // is open when availability flips away, fall back to Terms rather than strand.
  const { data: fsAvailable, loading: fsChecking } = useQuery("getFsSignalAvailable", {});
  useEffect(() => {
    if (view === "files" && fsAvailable !== true) setView("map");
  }, [fsAvailable, view]);

  // Through the HOOK, not the plain accessor: a mount changes the catalog and this
  // surface owns the tab strip, so it has to re-render rather than wait for a
  // reload. `xrayRegistry()` elsewhere in this file is module scope or inside a
  // scan effect, where there is nothing to re-render.
  const registry = useXrayRegistry();

  // Window row by prompt id — every per-prompt scan below stamps provenance
  // (session, endpoint, timestamp) onto its ops from here.
  const rowOf = useMemo(() => new Map(windowTraces.map((t) => [t.id, t])), [windowTraces]);

  // ── Behaviours — the X-ray discovery pipeline over this scope's own traffic.
  //
  // The ONE tab that does not scan the shared window. Every other tab wants the
  // newest N prompts here; the factory's discover gate refuses that sample
  // outright, because a uniform draw of recent requests is dominated by short
  // sessions and makes every candidate look like a singleton. So it re-selects
  // the window's deepest sessions and hydrates those instead — same fetch
  // mechanic as Templates was, different ids.
  const factorySelection = useMemo(() => pickDeepSessions(windowTraces), [windowTraces]);
  // How much window a zero was measured over. Appended to every lens tab's
  // empty note, because "no X work in this window" over half an hour of fresh
  // traffic reads as a dead lens when it is a rolled window.
  const windowExtentNote = useMemo(() => windowExtentNoteOf(windowTraces), [windowTraces]);
  const factoryIdsKey = factorySelection.ids.join(",");

  const [scan, setScan] = useState({ status: "idle", done: 0, total: 0, bodies: [], forKey: null });
  const scanToken = useRef(0);

  useEffect(() => {
    if (view !== "behaviours") return undefined;
    if (windowPending || factorySelection.ids.length === 0) {
      setScan({ status: "idle", done: 0, total: 0, bodies: [], forKey: null });
      return undefined;
    }
    // Cached for this exact window — skip. (Only a COMPLETED scan
    // skips: a "scanning" state here means a tab flip killed the
    // workers mid-run, so start over.)
    if (scan.forKey === windowKey && scan.status === "done") return undefined;
    const selected = factorySelection.ids;
    const token = ++scanToken.current;
    setScan({ status: "scanning", done: 0, total: selected.length, bodies: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const bodies = [];
    const metadata = new Map();
    const refsReady = (async () => {
      const res = await query("getPromptMetadata", { eventIds: selected, range: ctx?.range }, { cache: true });
      if (res?.ok) for (const row of res.rows || []) metadata.set(String(row.event_id), row);
    })();

    async function worker() {
      await refsReady;
      while (next < selected.length) {
        if (scanToken.current !== token) return;
        const id = selected[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id, { metadata: metadata.get(String(id)) });
          if (ev?.parsedConversation) {
            const row = rowOf.get(id);
            entry = {
              promptId: id,
              sessionId: ev.session_id || row?.sessionId || "",
              endpointId: ev.endpoint_id || row?.endpointId || "",
              endpointName: row?.endpointName || "",
              ts: row?.timestamp ?? ev.event_time ?? null,
              parsed: ev.parsedConversation,
            };
          }
        } catch { /* skip a row that won't hydrate/parse */ } finally {
          // In a `finally` because the loop above can `continue` past a prompt that
          // resolved to nothing, and skipping the tally on that path left `done` short
          // of the total forever — the tab sat on "Hydrating…" with no way forward. A
          // prompt that yielded no body still counts as looked at.
          if (scanToken.current === token) {
            done += 1;
            if (entry) bodies.push(entry);
            setScan({ status: done < selected.length ? "scanning" : "done", done, total: selected.length, bodies, forKey: windowKey });
          }
        }
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, selected.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { scanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, factoryIdsKey, windowPending, windowKey]);

  // ── Files — the reconstructed file tree. Same hydration mechanic as the
  // Factory scan (own scan so either tab can be visited first; each caches
  // per window), but the body is dropped as soon as its file ops are
  // extracted — only the op records (with their diff payloads) accumulate.

  const [fileScan, setFileScan] = useState({ status: "idle", prompts: [], forKey: null });
  const fileScanToken = useRef(0);
  useEffect(() => {
    if (view !== "files") return undefined;
    if (windowPending || ids.length === 0) {
      setFileScan({ status: "idle", prompts: [], forKey: null });
      return undefined;
    }
    if (fileScan.forKey === windowKey && fileScan.status === "done") return undefined;
    const token = ++fileScanToken.current;
    setFileScan({ status: "scanning", prompts: [], forKey: windowKey });
    // One indexed fs.write/fs.read read over the window's events replaces the
    // per-prompt hydration + client re-extraction: the signal value JSON already
    // carries the effective diff / numbered observations, so each row maps
    // straight onto a file op. The availability gate already confirmed the tenant
    // has fs data; a query failure (or a genuinely file-op-free window) resolves
    // to an empty tree rather than a partial one.
    (async () => {
      // eventIds IN already pins the result to this window's exact events (the
      // same ids the hydration path walked), so scope is left empty — the window
      // already encodes the scope, and adding the scope predicate would only risk
      // an unsupported-dim fail-closed. range still prunes signal_tags partitions.
      const res = await query(
        "getFsSignalOps",
        { scopePath: [], range: ctx?.range, namespaces: ["fs.write", "fs.read"], eventIds: ids },
        { cache: true },
      );
      if (fileScanToken.current !== token) return;
      const prompts = res?.ok ? fsRowsToPromptEvents(res.rows, (id) => rowOf.get(id)) : [];
      setFileScan({ status: "done", prompts, forKey: windowKey });
    })();
    return () => { fileScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const fileBuilt = useMemo(
    () => (fileScan.status === "done" && fileScan.forKey === windowKey
      ? buildFileTree(collectWindowOps(fileScan.prompts))
      : null),
    [fileScan, windowKey],
  );

  // ── Linear — who reads and writes Linear issues. Same hydration mechanic
  // as Files (own scan so any tab can be visited first; each caches per
  // window); the body is dropped once its Linear ops are extracted.
  const [linearScan, setLinearScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const linearScanToken = useRef(0);
  useEffect(() => {
    if (view !== "linear") return undefined;
    if (windowPending || ids.length === 0) {
      setLinearScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (linearScan.forKey === windowKey && linearScan.status === "done") return undefined;
    const token = ++linearScanToken.current;
    setLinearScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (linearScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractLinearOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (linearScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setLinearScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { linearScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const linearFlat = useMemo(
    () => (linearScan.status === "done" && linearScan.forKey === windowKey
      ? collectWindowLinearOps(linearScan.prompts)
      : null),
    [linearScan, windowKey],
  );
  // The full-window build feeds the Who rail (every actor, full counts); the
  // DISPLAYED build narrows to one actor's own ops when a Who row is active,
  // so the tree + stream answer "what did this machine read and write".
  const [linearActor, setLinearActor] = useState(null);
  useEffect(() => { setLinearActor(null); }, [windowKey]);
  const linearBuiltAll = useMemo(
    () => (linearFlat ? buildLinearActivity(linearFlat) : null),
    [linearFlat],
  );
  const linearBuilt = useMemo(() => {
    if (!linearFlat) return null;
    if (!linearActor) return linearBuiltAll;
    return buildLinearActivity(linearFlat.filter((op) => op.endpointId === linearActor));
  }, [linearFlat, linearActor, linearBuiltAll]);

  // ── Research — what this window's agents READ from the open web. Same
  // hydration mechanic as Files/Linear (own scan; caches per window); the body
  // is dropped once its WebFetch/WebSearch ops are extracted.
  const [researchScan, setResearchScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const researchScanToken = useRef(0);
  useEffect(() => {
    if (view !== "research") return undefined;
    if (windowPending || ids.length === 0) {
      setResearchScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (researchScan.forKey === windowKey && researchScan.status === "done") return undefined;
    const token = ++researchScanToken.current;
    setResearchScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (researchScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractWebResearchOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (researchScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setResearchScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { researchScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const researchFlat = useMemo(
    () => (researchScan.status === "done" && researchScan.forKey === windowKey
      ? collectWindowWebResearchOps(researchScan.prompts)
      : null),
    [researchScan, windowKey],
  );
  const [researchActor, setResearchActor] = useState(null);
  useEffect(() => { setResearchActor(null); }, [windowKey]);
  const researchBuiltAll = useMemo(
    () => (researchFlat ? buildWebResearchActivity(researchFlat) : null),
    [researchFlat],
  );
  const researchBuilt = useMemo(() => {
    if (!researchFlat) return null;
    if (!researchActor) return researchBuiltAll;
    return buildWebResearchActivity(researchFlat.filter((op) => op.endpointId === researchActor));
  }, [researchFlat, researchActor, researchBuiltAll]);

  // ── Forge — PR/issue/CI work driven through the SHELL (curl, gh,
  // forgejo-pr.py, tea, git push). Same hydration mechanic as Linear/Research
  // (own scan; caches per window); the body is dropped once its shell
  // commands are parsed into forge ops.
  const [forgeScan, setForgeScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const forgeScanToken = useRef(0);
  useEffect(() => {
    if (view !== "forge") return undefined;
    if (windowPending || ids.length === 0) {
      setForgeScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (forgeScan.forKey === windowKey && forgeScan.status === "done") return undefined;
    const token = ++forgeScanToken.current;
    setForgeScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (forgeScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractForgeOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (forgeScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setForgeScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { forgeScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const forgeFlat = useMemo(
    () => (forgeScan.status === "done" && forgeScan.forKey === windowKey
      ? collectWindowForgeOps(forgeScan.prompts)
      : null),
    [forgeScan, windowKey],
  );
  const [forgeActor, setForgeActor] = useState(null);
  useEffect(() => { setForgeActor(null); }, [windowKey]);
  const forgeBuiltAll = useMemo(
    () => (forgeFlat ? buildForgeActivity(forgeFlat) : null),
    [forgeFlat],
  );
  const forgeBuilt = useMemo(() => {
    if (!forgeFlat) return null;
    if (!forgeActor) return forgeBuiltAll;
    return buildForgeActivity(forgeFlat.filter((op) => op.endpointId === forgeActor));
  }, [forgeFlat, forgeActor, forgeBuiltAll]);

  // ── Grafana + Queries — telemetry and data access, same hydration mechanic
  // as the sibling tabs (one lib, two extractors — see traceQueryOps).
  const [grafanaScan, setGrafanaScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const grafanaScanToken = useRef(0);
  useEffect(() => {
    if (view !== "grafana") return undefined;
    if (windowPending || ids.length === 0) {
      setGrafanaScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (grafanaScan.forKey === windowKey && grafanaScan.status === "done") return undefined;
    const token = ++grafanaScanToken.current;
    setGrafanaScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (grafanaScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractGrafanaOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (grafanaScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setGrafanaScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { grafanaScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const grafanaFlat = useMemo(
    () => (grafanaScan.status === "done" && grafanaScan.forKey === windowKey
      ? collectWindowQueryOps(grafanaScan.prompts)
      : null),
    [grafanaScan, windowKey],
  );
  const [grafanaActor, setGrafanaActor] = useState(null);
  useEffect(() => { setGrafanaActor(null); }, [windowKey]);
  const grafanaBuiltAll = useMemo(
    () => (grafanaFlat ? buildQueryActivity(grafanaFlat) : null),
    [grafanaFlat],
  );
  const grafanaBuilt = useMemo(() => {
    if (!grafanaFlat) return null;
    if (!grafanaActor) return grafanaBuiltAll;
    return buildQueryActivity(grafanaFlat.filter((op) => op.endpointId === grafanaActor));
  }, [grafanaFlat, grafanaActor, grafanaBuiltAll]);

  const [dataqScan, setDataqScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const dataqScanToken = useRef(0);
  useEffect(() => {
    if (view !== "dataq") return undefined;
    if (windowPending || ids.length === 0) {
      setDataqScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (dataqScan.forKey === windowKey && dataqScan.status === "done") return undefined;
    const token = ++dataqScanToken.current;
    setDataqScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (dataqScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractDataQueryOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (dataqScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setDataqScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { dataqScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const dataqFlat = useMemo(
    () => (dataqScan.status === "done" && dataqScan.forKey === windowKey
      ? collectWindowQueryOps(dataqScan.prompts)
      : null),
    [dataqScan, windowKey],
  );
  const [dataqActor, setDataqActor] = useState(null);
  useEffect(() => { setDataqActor(null); }, [windowKey]);
  const dataqBuiltAll = useMemo(
    () => (dataqFlat ? buildQueryActivity(dataqFlat) : null),
    [dataqFlat],
  );
  const dataqBuilt = useMemo(() => {
    if (!dataqFlat) return null;
    if (!dataqActor) return dataqBuiltAll;
    return buildQueryActivity(dataqFlat.filter((op) => op.endpointId === dataqActor));
  }, [dataqFlat, dataqActor, dataqBuiltAll]);

  // ── Manifest lenses — ONE scan, one build, one panel, for every lens declared
  // in xray-factory/xrays. Before this existed a manifest lens got a tab and a
  // substrate and then fell through the dispatch to the default body, so selecting
  // it showed the Terms diagram — the status line said the right thing, which made
  // it read as the lens being broken rather than absent.
  //
  // Generic by design: the manifest already declares extract/collect/group, so the
  // host needs no per-lens code. That is the whole point of the seam — adding a
  // lens stops being an edit here.
  const manifestLens = useMemo(() => registry.lens(view), [view, registry]);
  const [mScan, setMScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null, forLens: null });
  const mScanToken = useRef(0);
  useEffect(() => {
    if (!manifestLens) return undefined;
    if (windowPending || ids.length === 0) {
      setMScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null, forLens: null });
      return undefined;
    }
    if (mScan.forKey === windowKey && mScan.forLens === manifestLens.id && mScan.status === "done") return undefined;
    const token = ++mScanToken.current;
    setMScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey, forLens: manifestLens.id });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (mScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            // A lens whose extractor throws must not take the panel down with it —
            // one bad grammar degrades to an empty window, not a blank view.
            let ops = [];
            try { ops = manifestLens.extract(ev.turns) || []; }
            catch (err) { console.warn(`[xray] ${manifestLens.id} extractor threw`, err); }
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that will not hydrate or parse */ }
        if (mScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setMScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey, forLens: manifestLens.id });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { mScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestLens, idsKey, windowPending, windowKey]);

  const mBuilt = useMemo(() => {
    if (!manifestLens) return null;
    if (mScan.status !== "done" || mScan.forKey !== windowKey || mScan.forLens !== manifestLens.id) return null;
    try {
      return manifestLens.group(manifestLens.collect(mScan.prompts));
    } catch (err) {
      console.warn(`[xray] ${manifestLens.id} collect/group threw`, err);
      return null;
    }
  }, [manifestLens, mScan, windowKey]);

  // The structural reading, for a lens that declares one. Built from the same
  // collected ops as `mBuilt` rather than a second scan — the two are projections of
  // one set, and a second fetch would let them disagree.
  const mStructure = useMemo(() => {
    if (!manifestLens?.structure) return null;
    if (mScan.status !== "done" || mScan.forKey !== windowKey || mScan.forLens !== manifestLens.id) return null;
    try {
      return manifestLens.structure(manifestLens.collect(mScan.prompts));
    } catch (err) {
      // Contained like the flat path: one bad lens must not take the tab down.
      console.warn(`[xray] ${manifestLens.id} structure threw`, err);
      return null;
    }
  }, [manifestLens, mScan, windowKey]);

  // ── Recall — what this window's agents asked Origin itself (prior-work
  // retrievals + analytics-docs lookups).
  const [recallScan, setRecallScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const recallScanToken = useRef(0);
  useEffect(() => {
    if (view !== "recall") return undefined;
    if (windowPending || ids.length === 0) {
      setRecallScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (recallScan.forKey === windowKey && recallScan.status === "done") return undefined;
    const token = ++recallScanToken.current;
    setRecallScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (recallScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const ops = extractRecallOps(ev.turns);
            if (ops.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                ops,
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (recallScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setRecallScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { recallScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const recallFlat = useMemo(
    () => (recallScan.status === "done" && recallScan.forKey === windowKey
      ? collectWindowRecallOps(recallScan.prompts)
      : null),
    [recallScan, windowKey],
  );
  const [recallActor, setRecallActor] = useState(null);
  useEffect(() => { setRecallActor(null); }, [windowKey]);
  const recallBuiltAll = useMemo(
    () => (recallFlat ? buildRecallActivity(recallFlat) : null),
    [recallFlat],
  );
  const recallBuilt = useMemo(() => {
    if (!recallFlat) return null;
    if (!recallActor) return recallBuiltAll;
    return buildRecallActivity(recallFlat.filter((op) => op.endpointId === recallActor));
  }, [recallFlat, recallActor, recallBuiltAll]);

  // ── Artifacts — the published artifacts this window shipped. The scan
  // keeps every prompt's tool-use marks (publishes ride them), skill
  // invocations (the "under <skill>" label), and file ops (the backing-page
  // reconstruction substrate).
  const [artifactScan, setArtifactScan] = useState({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
  const artifactScanToken = useRef(0);
  useEffect(() => {
    if (view !== "artifacts") return undefined;
    if (windowPending || ids.length === 0) {
      setArtifactScan({ status: "idle", done: 0, total: 0, prompts: [], forKey: null });
      return undefined;
    }
    if (artifactScan.forKey === windowKey && artifactScan.status === "done") return undefined;
    const token = ++artifactScanToken.current;
    setArtifactScan({ status: "scanning", done: 0, total: ids.length, prompts: [], forKey: windowKey });
    let next = 0;
    let done = 0;
    const prompts = [];
    async function worker() {
      while (next < ids.length) {
        if (artifactScanToken.current !== token) return;
        const id = ids[next++];
        let entry = null;
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const { invocations, marks } = extractArtifactSignals(ev.turns);
            if (invocations.length || marks.length) {
              const row = rowOf.get(id);
              entry = {
                promptId: id,
                sessionId: ev.session_id || row?.sessionId || "",
                endpointId: ev.endpoint_id || row?.endpointId || "",
                endpointName: row?.endpointName || "",
                ts: row?.timestamp ?? ev.event_time ?? null,
                summary: row?.summary || "",
                invocations,
                marks,
                // File ops ride along so a run's PUBLISHED artifacts can
                // reconstruct their backing HTML (Artifact input names the
                // path; the Writes/Edits are in the same bodies).
                fileOps: artifactFileOpsOf(ev.turns),
              };
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (artifactScanToken.current !== token) return;
        done += 1;
        if (entry) prompts.push(entry);
        setArtifactScan({ status: done < ids.length ? "scanning" : "done", done, total: ids.length, prompts, forKey: windowKey });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { artifactScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const artifactFlat = useMemo(
    () => (artifactScan.status === "done" && artifactScan.forKey === windowKey
      ? collectWindowSkillActivity(artifactScan.prompts)
      : null),
    [artifactScan, windowKey],
  );
  // The same scan's file ops, window-deduped — the reconstruction substrate
  // for published artifacts.
  const artifactFileFlat = useMemo(
    () => (artifactScan.status === "done" && artifactScan.forKey === windowKey
      ? collectWindowOps(artifactScan.prompts.map((p) => ({
        promptId: p.promptId, sessionId: p.sessionId, endpointId: p.endpointId,
        endpointName: p.endpointName, ts: p.ts, ops: p.fileOps || [],
      })))
      : []),
    [artifactScan, windowKey],
  );
  const [artifactActor, setArtifactActor] = useState(null);
  useEffect(() => { setArtifactActor(null); }, [windowKey]);
  const artifactBuiltAll = useMemo(
    () => (artifactFlat ? buildArtifactActivity(artifactFlat) : null),
    [artifactFlat],
  );
  const artifactBuilt = useMemo(() => {
    if (!artifactFlat) return null;
    if (!artifactActor) return artifactBuiltAll;
    // Runs stay whole — attribution is session-local, and sessions belong to
    // one endpoint anyway; only the publish marks narrow to the actor.
    return buildArtifactActivity({
      runs: artifactFlat.runs,
      marks: artifactFlat.marks.filter((m) => m.endpointId === artifactActor),
    });
  }, [artifactFlat, artifactActor, artifactBuiltAll]);
  // Reconstruction substrate: every deploy's own backing ops (multi-path,
  // multi-session — see traceArtifacts.artifactBackingOps).
  const artifactRecFor = useMemo(() => {
    const cache = new Map();
    return (artifact) => {
      if (cache.has(artifact.key)) return cache.get(artifact.key);
      const ops = artifactBackingOps(artifact, artifactFileFlat);
      const rec = ops.length ? reconstructFile(ops) : null;
      cache.set(artifact.key, rec);
      return rec;
    };
  }, [artifactFileFlat]);

  // ── Lineage — deterministic provenance across the window's artifacts. Unlike
  // the other tabs (one lib each), this needs every op stream together, so it
  // runs ONE hydration pass and extracts all of them per prompt. Widening this
  // set is what lets the graph span the work rather than three-tenths of it —
  // and forge/query/artifact ops bring join keys the cross-session pass needs.
  const EMPTY_LINEAGE_SCAN = { status: "idle", done: 0, total: 0, file: [], linear: [], web: [], forge: [], dataq: [], grafana: [], recall: [], marks: [], factory: emptyFactoryBuckets(xrayRegistry()), forKey: null };
  const [lineageScan, setLineageScan] = useState(EMPTY_LINEAGE_SCAN);
  const lineageScanToken = useRef(0);
  useEffect(() => {
    if (view !== "lineage") return undefined;
    if (windowPending || ids.length === 0) {
      setLineageScan(EMPTY_LINEAGE_SCAN);
      return undefined;
    }
    if (lineageScan.forKey === windowKey && lineageScan.status === "done") return undefined;
    const token = ++lineageScanToken.current;
    setLineageScan({ ...EMPTY_LINEAGE_SCAN, status: "scanning", total: ids.length, forKey: windowKey });
    let next = 0;
    let done = 0;
    const file = [];
    const linear = [];
    const web = [];
    const forge = [];
    const dataq = [];
    const grafana = [];
    const recall = [];
    const marks = [];
    const factory = emptyFactoryBuckets(xrayRegistry());
    async function worker() {
      while (next < ids.length) {
        if (lineageScanToken.current !== token) return;
        const id = ids[next++];
        try {
          const ev = await hydratePrompt(id);
          if (ev?.turns?.length) {
            const row = rowOf.get(id);
            const meta = {
              promptId: id,
              sessionId: ev.session_id || row?.sessionId || "",
              endpointId: ev.endpoint_id || row?.endpointId || "",
              endpointName: row?.endpointName || "",
              ts: row?.timestamp ?? ev.event_time ?? null,
            };
            const fops = extractFileOps(ev.turns); if (fops.length) file.push({ ...meta, ops: fops });
            const lops = extractLinearOps(ev.turns); if (lops.length) linear.push({ ...meta, ops: lops });
            const wops = extractWebResearchOps(ev.turns); if (wops.length) web.push({ ...meta, ops: wops });
            const gops = extractForgeOps(ev.turns); if (gops.length) forge.push({ ...meta, ops: gops });
            const dops = extractDataQueryOps(ev.turns); if (dops.length) dataq.push({ ...meta, ops: dops });
            const graf = extractGrafanaOps(ev.turns); if (graf.length) grafana.push({ ...meta, ops: graf });
            const rops = extractRecallOps(ev.turns); if (rops.length) recall.push({ ...meta, ops: rops });
            const { marks: aMarks } = extractArtifactSignals(ev.turns);
            if (aMarks.length) marks.push({ ...meta, invocations: [], marks: aMarks });
            for (const [id, ops] of Object.entries(factoryScanOps(xrayRegistry(), ev.turns))) {
              factory[id].push({ ...meta, ops });
            }
          }
        } catch { /* skip a row that won't hydrate/parse */ }
        if (lineageScanToken.current !== token) return;
        done += 1;
        setLineageScan({
          status: done < ids.length ? "scanning" : "done", done, total: ids.length,
          file: [...file], linear: [...linear], web: [...web], forge: [...forge],
          dataq: [...dataq], grafana: [...grafana], recall: [...recall], marks: [...marks],
          factory: snapshotFactoryBuckets(factory),
          forKey: windowKey,
        });
      }
    }
    const n = Math.min(HYDRATE_CONCURRENCY, Math.max(1, ids.length));
    Promise.all(Array.from({ length: n }, () => worker()));
    return () => { lineageScanToken.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, idsKey, windowPending, windowKey]);

  const provenance = useMemo(() => {
    if (lineageScan.status !== "done" || lineageScan.forKey !== windowKey) return null;
    return buildProvenance(lineageNodes(lineageScan, xrayRegistry()));
  }, [lineageScan, windowKey]);

  // Resolve friendly endpoint names for the tree/stream group headers —
  // the scan rows only carry id-prefix fallbacks (same lazy label-cache
  // mechanic as TraceTable's endpoint column). One combined step list
  // covers the Files tree's endpoints AND the Linear actors.
  const endpointLabelSteps = useMemo(
    () => {
      const ids2 = new Set();
      for (const ep of fileBuilt?.endpoints || []) if (ep.id) ids2.add(ep.id);
      for (const a of linearBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      for (const a of researchBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      for (const a of forgeBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      for (const a of artifactBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      for (const a of grafanaBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      for (const a of dataqBuiltAll?.actors || []) if (a.id) ids2.add(a.id);
      return [...ids2].map((id) => ({ dim: "endpoints", id }));
    },
    [fileBuilt, linearBuiltAll, researchBuiltAll, forgeBuiltAll, artifactBuiltAll, grafanaBuiltAll, dataqBuiltAll],
  );
  useLabels(endpointLabelSteps);
  const endpointLabelOf = (ep) => {
    const label = ep.id ? scopeStepLabel({ dim: "endpoints", id: ep.id }) : null;
    return label && label !== ep.id ? label : ep.label;
  };

  // Blame ONE line across the whole corpus (not just the window): an
  // event_data ILIKE over-fetch + client-side filter to the authoring Write,
  // opened in the detail panel. Bounded window; distinctive lines keep the
  // candidate set tiny. See lib/traceLineBlame.
  function blameLineAction(line, filePath) {
    ctx?.onOpenDetail?.({
      eyebrow: "Line blame",
      title: "Who wrote this line",
      content: (
        <LineBlamePanel
          line={line}
          filePath={filePath}
          traceOf={rowOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
        />
      ),
      detailKey: `xray-blame:${windowKey}:${filePath}:${line.slice(0, 40)}`,
    });
    slot?.onExpandDetail?.();
  }

  function openReconstruction(file, endpointLabel) {
    ctx?.onOpenDetail?.({
      eyebrow: "Reconstructed file",
      title: file.name,
      content: (
        <FileReconstruction
          file={file}
          endpointLabel={endpointLabel}
          traceOf={rowOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
          onBlameLine={blameLineAction}
        />
      ),
      detailKey: `xray-file:${windowKey}:${file.path}`,
    });
    slot?.onExpandDetail?.();
  }

  // Full issue timeline in the detail panel — the Linear analog of the Files
  // tab's full reconstruction.
  function openIssueDetail(issue) {
    ctx?.onOpenDetail?.({
      eyebrow: "Linear issue",
      title: issue.id,
      content: (
        <LinearIssueDetail
          issue={issue}
          traceOf={rowOf}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
        />
      ),
      detailKey: `xray-linear:${windowKey}:${issue.id}`,
    });
    slot?.onExpandDetail?.();
  }

  // Full target timeline in the detail panel — the forge analog of the Linear
  // issue timeline.
  function openForgeTarget(target) {
    ctx?.onOpenDetail?.({
      eyebrow: target.res === "issue" ? "Issue" : "Pull request",
      title: `${target.repo ? `${target.repo} ` : ""}${target.id}`,
      content: (
        <ForgeTargetDetail
          target={target}
          traceOf={rowOf}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
        />
      ),
      detailKey: `xray-forge:${windowKey}:${target.key}`,
    });
    slot?.onExpandDetail?.();
  }

  // The fs signal, per backing path — one indexed tag lookup over the whole
  // range, so it is immune to both limits the session walk lives under (the
  // window's 120-prompt sample and the walk's own per-session row page). It is
  // tried FIRST for exactly that reason: a long session whose authoring writes
  // sit outside the page the walk fetches would otherwise report "nothing wrote
  // this" while the signal has had the ops indexed all along.
  async function fsSignalEventsFor(path) {
    const res = await query(
      "getFsSignalOps",
      { scopePath: [], range: ctx?.range, namespaces: ["fs.write", "fs.read"], tag: rpath(path) },
      { cache: true },
    );
    // ok:false is a scope the PROMPT root can't carry or a failed query — not
    // "this file has no ops" — so it must not read as an answer.
    return res?.ok ? fsRowsToPromptEvents(res.rows, (id) => rowOf.get(id)) : [];
  }

  // Deep reconstruction wrapper — the source ORDER and both hops live (tested)
  // in traceArtifacts.resolveArtifactSources; this only wires the live queries
  // and hydrate it injects.
  function deepArtifactSources(artifact) {
    return resolveArtifactSources(artifact, {
      fetchSignalEvents: fsSignalEventsFor,
      windowOps: artifactBackingOps(artifact, artifactFileFlat),
      fetchSessionRows: async (sess) => {
        const res = await query(
          "getTraces",
          { scopePath: [{ dim: "sessions", id: sess }], range: ctx?.range, limit: 250, sort: "newest", offset: 0, canReadSensitivePrompts },
          { cache: true },
        );
        return Array.isArray(res) ? res : res?.rows || [];
      },
      hydrate: hydratePrompt,
      extractOps: artifactFileOpsOf,
      extractShell: extractShellOps,
      concurrency: HYDRATE_CONCURRENCY,
    });
  }

  // The artifact's whole deliverable — rendered preview + annotated,
  // blame-capable source — in the expandable detail sidebar.
  function openArtifactDetail(artifact) {
    ctx?.onOpenDetail?.({
      eyebrow: "Published artifact",
      title: artifact.title || (artifact.path ? artifact.path.split(/[\\/]/).pop() : "artifact"),
      content: (
        <ArtifactDetail
          artifact={artifact}
          rec={artifactRecFor(artifact)}
          traceOf={rowOf}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
          onBlameLine={blameLineAction}
          deepSourcesFor={deepArtifactSources}
        />
      ),
      detailKey: `xray-artifact:${windowKey}:${artifact.key}`,
    });
    slot?.onExpandDetail?.();
  }

  // Full query-target view in the detail sidebar — every query variant with
  // every observed response, uncapped.
  function openQueryTarget(target) {
    ctx?.onOpenDetail?.({
      eyebrow: `${target.engine}${target.instance ? ` · ${target.instance}` : ""}`,
      title: String(target.target),
      content: (
        <QueryTargetDetail
          target={target}
          traceOf={rowOf}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
        />
      ),
      detailKey: `xray-query:${windowKey}:${target.key}`,
    });
    slot?.onExpandDetail?.();
  }

  // A recalled candidate IS a session pointer (candidate session_id =
  // notification_id, the sessions dim's id space). Navigate to it at ROOT —
  // recalled sessions are org-wide precedent, and appending to the current
  // scope could intersect to an empty view (the precedent usually belongs to
  // someone else's endpoint).
  function openRecalledSession(sessionId) {
    if (!sessionId) return;
    ctx?.onNavigatePath?.([{ dim: "sessions", ids: [String(sessionId)], id: String(sessionId) }]);
  }

  // The whole prior-work consultation in the detail sidebar — request, facets,
  // routed topics, and every recalled candidate with its author pointer.
  function openRecallConsultation(consultation) {
    // Word-boundary head for the sidebar title — a hard slice cuts mid-word.
    const req = String(consultation.request || "consultation").trim();
    const head = req.length <= 72 ? req : (() => {
      const cut = req.slice(0, 72);
      const sp = cut.lastIndexOf(" ");
      return `${cut.slice(0, sp > 40 ? sp : 72)}…`;
    })();
    ctx?.onOpenDetail?.({
      eyebrow: "Prior-work consultation",
      title: head,
      content: (
        <RecallConsultationDetail
          consultation={consultation}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
          counts={recallBuiltAll?.recallCounts || {}}
          onOpenSession={openRecalledSession}
        />
      ),
      detailKey: `xray-recall:${windowKey}:${consultation.sessionId}:${consultation.key}`,
    });
    slot?.onExpandDetail?.();
  }

  // Full page reconstruction in the detail panel — what the agent saw at this
  // URL (the web-research analog of the Files reconstruction).
  function openPageDetail(page) {
    ctx?.onOpenDetail?.({
      eyebrow: "Fetched page",
      title: page.title || page.host || page.url,
      content: (
        <WebPageReconstruction
          page={page}
          labelOf={endpointLabelOf}
          onSelectTrace={(id) => ctx?.traceLog?.onSelectTrace?.(id)}
        />
      ),
      detailKey: `xray-page:${windowKey}:${page.url}`,
    });
    slot?.onExpandDetail?.();
  }

  const summaries = useMemo(
    () => windowTraces.map((t) => String(t.summary || "").trim()).filter(Boolean),
    [windowTraces],
  );

  // Baseline for the ranked-terms contrast: the newest org-wide page over
  // the same range. Only fetched when the scope is actually narrower than
  // the org (minus signal steps, which have no server-side scope predicate
  // — the same filter useScanWindow applies to the window query); at org
  // scope the ranking falls back to plain frequency. Mirrors the window
  // query's sensitive-column gating so both hit the same column policy.
  const caps = useMyPermissions();
  const canReadSensitivePrompts = hasPermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);
  const scoped = (scopePath || []).some((s) => s.dim !== "signals" && s.dim !== "signal");
  const { data: baselineTraces = [], loading: baselineLoading } = useQuery("getTraces", {
    scopePath: [], range: ctx?.range, limit: SCAN_LIMIT, sort: "newest", offset: 0, canReadSensitivePrompts,
  }, { staleMs: Infinity, enabled: scoped });
  const baselineSummaries = useMemo(
    () => (scoped ? baselineTraces.map((t) => String(t.summary || "").trim()).filter(Boolean) : null),
    [scoped, baselineTraces],
  );
  const ranked = useMemo(() => buildTermRank(summaries, baselineSummaries), [summaries, baselineSummaries]);
  const termsPending = windowPending || (scoped && baselineLoading);

  const sessions = useMemo(() => new Set(windowTraces.map((t) => t.sessionId)).size, [windowTraces]);

  const rows = useMemo(() => {
    if (!term) return windowTraces;
    return windowTraces.filter((t) => summaryHasTerm(t.summary, term));
  }, [windowTraces, term]);

  // ── Describe (LLM) — available only when the tenant's chat LLM is
  // configured; the lens never auto-runs it (explicit click = explicit
  // spend, mirroring lookup signals).
  // undefined = still checking (the tab strip reads as busy until every
  // availability gate has an answer); any settlement — creds, null, or a
  // failed lookup — is an answer, and the tabs stay hidden on a falsy one.
  const [creds, setCreds] = useState(undefined);
  useEffect(() => {
    let on = true;
    getChatCreds()
      .then((c) => { if (on) setCreds(c ?? false); })
      .catch(() => { if (on) setCreds(false); });
    return () => { on = false; };
  }, []);
  const llmReady = Boolean(creds?.provider && creds?.model && isChatConfigured(creds));

  // Model picker for the describe run — defaults to the task model
  // (cheap), but a big window can overflow a small model's context, so
  // the provider's catalog is one click away. Labels carry the context
  // window because that's exactly what you're picking for.
  const defaultModelId = creds
    ? (creds.taskModel || defaultTaskModelFor(creds.provider) || creds.model)
    : "";
  const [modelChoice, setModelChoice] = useState("");
  const describeModel = modelChoice || defaultModelId;
  const modelOptions = useMemo(() => {
    if (!creds?.provider) return [];
    const ranked = getRankedModels(creds.provider).map(({ model }) => ({
      value: model.id,
      label: model.contextWindow
        ? `${model.name} · ${Math.round(model.contextWindow / 1000)}k`
        : model.name,
    }));
    // A configured model outside the catalog (custom ids) still has to
    // be selectable — surface it verbatim at the top.
    return ranked.some((o) => o.value === describeModel) || !describeModel
      ? ranked
      : [{ value: describeModel, label: describeModel }, ...ranked];
  }, [creds?.provider, describeModel]);

  // The describe model picker now lives in the describe panel body (the
  // LaunchCard + the re-describe/retry rows) for parity with the influence
  // trace, instead of the X-ray hero. Slim chip styling matches that card.
  const describeModelSelect = modelOptions.length ? (
    <Select
      value={describeModel}
      onChange={(v) => setModelChoice(String(v))}
      options={modelOptions}
      ariaLabel="Model for the describe run"
      align="left"
      triggerStyle={{
        // Pin to the LaunchCard CTA's fixed 30px height so the trigger and
        // button line up exactly (inline-flex items-center centers the label).
        height: 30, minHeight: 0,
        fontSize: 11,
        backgroundColor: "var(--bg)",
        backgroundImage: "linear-gradient(var(--control-bg),var(--control-bg))",
      }}
      className="px-[9px]"
    />
  ) : null;

  const [ai, setAi] = useState({ status: "idle", text: null, forKey: null, error: null });
  const abortRef = useRef(null);

  // ── Workflows (LLM) — mine recurring agent workflows from the window's
  // full session bodies and suggest a tool per recurring one. Far heavier
  // than Describe (one reader sub-agent per session, then cluster/verify/
  // suggest), so it's run-gated like Describe and cached per window.
  const [wf, setWf] = useState({ status: "idle", data: null, forKey: null, error: null, progress: null });
  const wfAbortRef = useRef(null);

  // ── Factory › judge. The one model call in the pipeline, run-gated like
  // Describe. Its verdicts are scoped to the window they judged: a verdict set
  // ranked against one scope's candidates says nothing about another's, so it
  // is dropped on a scope change rather than left to read as current.
  const [judge, setJudge] = useState({ status: "idle", data: null, forKey: null, error: null });
  const judgeAbortRef = useRef(null);
  const refineAbortRef = useRef(null);
  // A SET, not a single controller: drafts run per-verdict-card and may run
  // concurrently. A shared ref made the second card's draft abort the first,
  // which then reported "the scope changed" to a reader whose scope had not.
  const artifactAbortsRef = useRef(new Set());

  // ── Wiki. Run-gated like the judge, but the RESULT is durable (wikiStore),
  // so this state is only the run's lifecycle — the panel reads pages from
  // the store. Keyed on scopeKey, not windowKey: the wiki's corpus is chosen
  // by its scout, not by the scrubber, so scrubbing must not mark it stale.
  const [wikiRun, setWikiRun] = useState({ status: "idle", progress: null, forKey: null, error: null, stage: null });
  const wikiAbortRef = useRef(null);
  const wikiSteerRef = useRef(steerQuery);

  // A steered preview belongs to the exact query that generated it. Clear the
  // transient run before painting a different steer; durable pages live in the
  // store and return without being rewritten.
  useLayoutEffect(() => {
    if (wikiSteerRef.current === steerQuery) return;
    wikiSteerRef.current = steerQuery;
    wikiAbortRef.current?.abort();
    setWikiRun({ status: "idle", progress: null, forKey: null, error: null, stage: null });
  }, [steerQuery]);

  // Cancel an in-flight description when the scope changes or the lens
  // unmounts; scrubbing keeps it running (the result just labels itself
  // stale for the new window).
  useEffect(() => {
    setAi({ status: "idle", text: null, forKey: null, error: null });
    setWf({ status: "idle", data: null, forKey: null, error: null, progress: null });
    setJudge({ status: "idle", data: null, forKey: null, error: null });
    setWikiRun({ status: "idle", progress: null, forKey: null, error: null, stage: null });
    setView("map");
    return () => {
      abortRef.current?.abort();
      wfAbortRef.current?.abort();
      judgeAbortRef.current?.abort();
      refineAbortRef.current?.abort();
      for (const ctrl of artifactAbortsRef.current) ctrl.abort();
      artifactAbortsRef.current.clear();
      wikiAbortRef.current?.abort();
    };
  }, [scopeKey]);

  // One model call that reads a candidate's raw instances and says where the judge's
  // field names live. Separate from the judge because it is a different question
  // asked of different evidence, and because a lens is worth refining only after it
  // is mounted and found thin.
  // `verdictKey` leads because the panel owns the per-card mount state and passes it
  // through; this side owns only the model call and ignores it. Taking three
  // parameters against the panel's four shifted every argument one place left —
  // refineRuntimeLens got the key as its spec and the spec as its cluster, so the
  // request carried no evidence and no field list, and the flow was silently asking
  // the model to locate nothing.
  async function runRefine(verdictKey, spec, cluster, wanted) {
    void verdictKey;
    // Aborted by the [scopeKey] cleanup, like the judge and the description. A refine
    // that lands late does not merely write stale state: on success the panel PERSISTS
    // the returned spec, so an abandoned run would store a lens refined against a
    // window the reader has already left.
    refineAbortRef.current?.abort();
    const ctrl = new AbortController();
    refineAbortRef.current = ctrl;
    try {
      const r = await refineRuntimeLens(spec, cluster, {
        wanted,
        model: describeModel || null,
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) {
        // Reported as a refusal rather than silently dropped: the card stays mounted
        // with its draft and a way to ask again, which is the honest outcome.
        return { ok: false, problems: ["The scope changed while the model was running. Nothing was saved."], spec: null };
      }
      return r;
    } catch (err) {
      if (ctrl.signal.aborted) {
        return { ok: false, problems: ["The scope changed while the model was running. Nothing was saved."], spec: null };
      }
      return { ok: false, problems: [err?.message || String(err)], spec: null };
    }
  }

  // Draft an executable behaviour artifact from a judged verdict. The drafting
  // agent runs with generation-time tools (python over the evidence) and its
  // output is accepted or refused by the contract before the panel persists it.
  // Same abort discipline as refine: a scope change mid-draft must not persist
  // an artifact written against a window the reader has left.
  async function runArtifactDraft(verdict, cluster) {
    const ctrl = new AbortController();
    artifactAbortsRef.current.add(ctrl);
    try {
      const windows = windowsFromBodies(scan.bodies || []);
      const r = await draftBehaviourArtifact(verdict, cluster, windows, {
        model: describeModel || null,
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) {
        return { ok: false, problems: ["the scope changed while the agent was writing — nothing was saved"], artifact: null };
      }
      return r;
    } catch (err) {
      if (ctrl.signal.aborted) {
        return { ok: false, problems: ["the scope changed while the agent was writing — nothing was saved"], artifact: null };
      }
      return { ok: false, problems: [err?.message || String(err)], artifact: null };
    } finally {
      artifactAbortsRef.current.delete(ctrl);
    }
  }

  async function runJudge(clusters, covered) {
    if (!clusters.length) return;
    const key = windowKey;
    judgeAbortRef.current?.abort();
    const ctrl = new AbortController();
    judgeAbortRef.current = ctrl;
    setJudge({ status: "running", data: null, forKey: key, error: null });
    try {
      // Ranked against each other under a budget — never scored independently,
      // which yields a lens per cluster.
      const data = await judgeClusters(clusters.slice(0, 10), {
        covered,
        model: describeModel || null,
        signal: ctrl.signal,
      });
      if (ctrl.signal.aborted) return;
      setJudge({ status: "done", data, forKey: key, error: null });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setJudge({ status: "error", data: null, forKey: key, error: err?.message || String(err) });
    }
  }

  async function describe() {
    if (!summaries.length) return;
    const key = windowKey;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setAi({ status: "running", text: null, forKey: key, error: null, model: describeModel });
    const capped = summaries.slice(0, DESCRIBE_CAP);
    const r = await runSubAgent({
      role: "task",
      model: describeModel || null,
      signal: ctrl.signal,
      tools: [],
      systemPrompt:
        "You are summarizing one slice of LLM-prompt traffic captured by an AI-observability " +
        "tool. You receive one-line summaries of every prompt in the current window of the " +
        "selected scope. Write a natural, concrete account of what this traffic is about: the " +
        "dominant activity, the distinct sub-threads, and anything anomalous or surprising. " +
        "Reference actual topics from the summaries; never invent specifics that are not " +
        "present. 2–4 short paragraphs, plain prose, no headings, no preamble.",
      task:
        `Scope: ${scopeLabel}\n` +
        `${capped.length} prompt summaries${summaries.length > capped.length ? ` (first ${capped.length} of ${summaries.length})` : ""}:\n` +
        capped.map((s) => `- ${s}`).join("\n"),
    });
    if (ctrl.signal.aborted) return;
    if (r.status === "done" && typeof r.result === "string" && r.result.trim()) {
      setAi({ status: "done", text: r.result.trim(), forKey: key, error: null, model: describeModel });
    } else if (r.status === "aborted") {
      setAi({ status: "idle", text: null, forKey: null, error: null });
    } else {
      const { message, detail } = llmErrorCopy(r.error?.message || r.error);
      setAi({ status: "error", text: null, forKey: key, error: message, errorDetail: detail, model: describeModel });
    }
  }

  async function runWorkflows() {
    if (!windowTraces.length) return;
    const key = windowKey;
    wfAbortRef.current?.abort();
    const ctrl = new AbortController();
    wfAbortRef.current = ctrl;
    setWf({ status: "running", data: null, forKey: key, error: null, progress: { phase: "summarize", done: 0, total: sessions } });
    try {
      const data = await mineWorkflows({
        windowTraces,
        model: describeModel || null,
        signal: ctrl.signal,
        onProgress: (p) => setWf((w) => (w.forKey === key && w.status === "running" ? { ...w, progress: p } : w)),
      });
      if (ctrl.signal.aborted) return;
      setWf({ status: "done", data, forKey: key, error: null, progress: null });
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setWf({ status: "error", data: null, forKey: key, error: err?.message || String(err), progress: null });
    }
  }

  async function runWiki() {
    const key = scopeKey;
    wikiAbortRef.current?.abort();
    const ctrl = new AbortController();
    wikiAbortRef.current = ctrl;
    setWikiRun({ status: "running", progress: { stage: "scout" }, forKey: key, error: null, stage: null });
    // The scout gets the scope as text — dim, label, AND id, because its
    // analytics queries must filter on the id while its prose reasons about
    // the label. Same contract as the chat's <dashboard_context>, minus the
    // searchParams round-trip this lens never had. The text is intent only:
    // the pipeline also takes the scopePath itself and enforces it
    // mechanically (query guard + scope-carrying evidence pulls).
    const scopeText = scopePath.length
      ? scopePath.map((s) => `${s.dim}: ${scopeStepLabel(s)} (id: ${s.id})`).join(" › ")
      : "the entire organization";
    const steerText = steerQuery || null;
    const r = await runWikiGeneration({
      scopeText,
      rangeText: describeRange(ctx?.range),
      scopeKey: key,
      scopePath,
      range: ctx?.range,
      steer: steerText,
      model: describeModel || null,
      canReadSensitivePrompts,
      signal: ctrl.signal,
      onProgress: (p) => setWikiRun((w) => (w.forKey === key && w.status === "running" ? { ...w, progress: p } : w)),
    });
    if (ctrl.signal.aborted || r.status === "aborted") {
      // The panel's Stop lands here with this run still current — reset to
      // idle. A run superseded by a newer one (which aborted this ctrl) must
      // write nothing: the newer run owns the state now.
      if (wikiAbortRef.current === ctrl) {
        setWikiRun({ status: "idle", progress: null, forKey: null, error: null, stage: null });
      }
      return;
    }
    if (r.status === "done") {
      // A STEERED generation is ephemeral by contract (see runWikiGeneration):
      // persisting it would stale every durable page it didn't re-find. It
      // rides the run state instead; WikiPanel renders it as a labeled,
      // unsaved preview.
      if (r.generation?.meta?.steered) {
        setWikiRun({ status: "done", progress: null, forKey: key, error: null, stage: null, ephemeral: r.generation });
        return;
      }
      // A generation that ran but did not persist is a FAILED run, whatever
      // the pipeline says — the reader would see stale pages under a
      // "last run" line claiming this run happened.
      const persisted = applyGeneration(null, r.generation);
      if (persisted.ok) {
        setWikiRun({ status: "done", progress: null, forKey: key, error: null, stage: null });
      } else {
        setWikiRun({
          status: "error",
          progress: null,
          forKey: key,
          error: `The run finished, but its result could not be saved: ${persisted.problems.join("; ") || "the store rejected the write"}`,
          stage: "persist",
        });
      }
    } else {
      setWikiRun({ status: "error", progress: null, forKey: key, error: r.error?.message || String(r.error || "the run failed"), stage: r.stage || null });
    }
  }

  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };

  // The surface's only header line: the window vitals, once, slim. Values in
  // ink + tabular numerals, labels muted (the Sheet detail line's own color) —
  // the same value/label treatment the issue chips and file stats use, so the
  // line reads as data, not a caption.
  const vital = (n, label) => (
    <>
      <span className="font-medium tabular-nums text-[color:var(--ink)]">{n}</span> {label}
    </>
  );
  const status = substrate?.empty
    ? `No ${substrate.label}-bearing prompts in this scope and range.`
    : windowPending
    ? "loading prompt window…"
    : windowTraces.length === 0
      ? "No prompts in this window."
      : (
        // "Scanning a window of…" carries the mechanic (there's a WINDOW, it's
        // being SCANNED) without a tutorial tail — the tabs and the scrubber
        // teach themselves through their own hint lines and labels.
        <>
          Scanning a window of {vital(windowTraces.length, "prompts")} across {vital(sessions, `session${sessions === 1 ? "" : "s"}`)}
          {substrate?.step ? <> · {substrate.label} substrate</> : <>{" · "}{vital(ranked.distinct, "distinct terms")}</>}
        </>
      );

  return (
    // The X-ray surface fills the page with NO hero and no title bar —
    // the scope chip above the lens already names the scope, so the old
    // "X-RAY <scope>" hero + "What this traffic is about" title spent two
    // header bands restating context; the analyses get that space now.
    // With nothing left to collapse, the fullscreen expand toggle (and
    // this lens's ContentShell expand participation) is gone too — the
    // in-flow surface IS the full-height layout. The column never scrolls
    // itself; each tab panel owns its height and scrolls its own content,
    // and the scan-window scrubber rides the Sheet's foot so the window
    // control stays visible whichever tab is up. pt/pb keep the surface
    // clear of the tab rail above and the viewport bottom below (the
    // offsets the hero band and the expand rect used to provide).
    <div className="flex h-full min-h-0 flex-col pt-[12px] pb-[12px]">
      {/* Diagram-dot paper on the Sheet chrome, the vitals as its one slim
          detail line, a TabStrip riding the content panel's top edge, and
          the content itself on a SOLID plot-bg fill. */}
      <Sheet
        className="flex-1"
        detail={status}
        diagram
        tight
      >
        <div className="mt-[6px] flex min-h-0 flex-1 flex-col">
          {/* -mb-[1px] pulls the panel up so the active tab's bottom
              mask (drawn in --plot-bg at the strip's base) covers the
              panel's top border — same trick as ActivityChart.
              aria-busy while any availability gate is still resolving:
              gated tabs (Files ← fs signal, Describe/Workflows ← chat
              creds) are HIDDEN while their check pends, indistinguishable
              from absent to anything reading the DOM — the busy marker is
              the loading contract (dashboard/AGENTS.md → Loading & async
              state) that lets screen readers and the walkthrough recorder
              wait for the strip's final shape. The fs half keys on the
              query being genuinely IN FLIGHT with no answer yet: a denied
              role and a persistent fetch error both settle loading=false
              with data still undefined, and either must read as ANSWERED
              (tab hidden) — never as busy forever. */}
          <div
            className="-mb-[1px]"
            aria-busy={(fsChecking && fsAvailable === undefined) || creds === undefined ? "true" : undefined}
          >
            <TabStrip
              tabs={[
                { id: "map", label: "Terms", active: view === "map", onSelect: () => setView("map") },
                {
                  id: "topiclinks",
                  label: "Topic patterns",
                  title: "Which topics share sessions far more often than chance — recurring cross-topic patterns and merge evidence for the topic model, from the current topic generation",
                  active: view === "topiclinks",
                  onSelect: () => setView("topiclinks"),
                },
                ...(llmReady
                  ? [
                      {
                        id: "describe",
                        label: "Describe",
                        title: "Have a model read the window's summaries",
                        active: view === "describe",
                        onSelect: () => setView("describe"),
                      },
                      {
                        id: "workflows",
                        label: "Workflows",
                        title: "Mine recurring agent workflows in this scope and suggest tools",
                        active: view === "workflows",
                        onSelect: () => setView("workflows"),
                      },
                      {
                        id: "wiki",
                        label: "Wiki",
                        title: "A cited wiki of this scope's recurring workflows, subsystems, and tool practices",
                        active: view === "wiki",
                        onSelect: () => setView("wiki"),
                      },
                    ]
                  : []),
                {
                  id: "behaviours",
                  label: "Behaviours",
                  title: "Offered tools, recurring behaviours, and candidate X-rays",
                  active: view === "behaviours",
                  onSelect: () => setView("behaviours"),
                },
                {
                  id: "lineage",
                  label: "Lineage",
                  title: "Inputs and outputs connected by provenance in this window",
                  active: view === "lineage",
                  onSelect: () => setView("lineage"),
                },
                ...(fsAvailable === true
                  ? [{
                    id: "files",
                    label: "Files",
                    title: "The file tree this window's agents touched, from the fs signal (writes + reads)",
                    active: view === "files",
                    onSelect: () => setView("files"),
                  }]
                  : []),
                {
                  id: "linear",
                  label: "Linear",
                  title: "Who reads and writes Linear issues in this window (MCP Linear tool calls)",
                  active: view === "linear",
                  onSelect: () => setView("linear"),
                },
                {
                  id: "research",
                  label: "Research",
                  title: "Web pages and search results read in this window",
                  active: view === "research",
                  onSelect: () => setView("research"),
                },
                {
                  id: "forge",
                  label: "Git",
                  title: "Pull requests, issues, CI, and pushes recorded from shell activity",
                  active: view === "forge",
                  onSelect: () => setView("forge"),
                },
                {
                  id: "grafana",
                  label: "Grafana",
                  title: "Telemetry queries, captured results, and alert-rule changes",
                  active: view === "grafana",
                  onSelect: () => setView("grafana"),
                },
                {
                  id: "dataq",
                  label: "Queries",
                  title: "Analytics, Emissary, and SQL queries with captured responses",
                  active: view === "dataq",
                  onSelect: () => setView("dataq"),
                },
                {
                  id: "recall",
                  label: "Recall",
                  title: "Prior-work retrievals and analytics documentation lookups",
                  active: view === "recall",
                  onSelect: () => setView("recall"),
                },
                {
                  id: "artifacts",
                  label: "Artifacts",
                  title: "Published artifacts with deploy timelines, previews, and captured source",
                  active: view === "artifacts",
                  onSelect: () => setView("artifacts"),
                },
                // Manifest-declared lenses. Appended rather than interleaved: the
                // strip's order is meaningful (most-used first) and a manifest has
                // no business reordering lenses it did not author.
                ...registry.tabs().map((t) => ({
                  id: t.id,
                  label: t.label,
                  title: t.title,
                  active: view === t.id,
                  onSelect: () => setView(t.id),
                })),
              ]}
            />
          </div>
          {/* overflow-CLIP, deliberately not auto/hidden: every panel owns
              its scrolling (its own ScrollArea, or Files' internal split),
              and the wrapper must never become a competing scroll region —
              sticky file headers and sticky code gutters pin to the panels'
              own scroll roots, and even overflow:hidden can still be panned
              programmatically (the Files nav uses scrollIntoView). clip is
              a true non-scrollable crop. */}
          <div className="relative min-h-0 min-w-0 flex-1 overflow-clip rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
            {substrate?.empty ? (
              // The substrate resolver found NO matching tools in this scope
              // and range — the tab's answer is a definitive "none", with no
              // window fetch or scan spent proving it.
              <span className={`${NOTE_CLASS} block px-[12px] py-[10px]`}>
                No {substrate.label}-bearing prompts in this scope and range.
              </span>
            ) : view === "describe" && llmReady ? (
              <ScrollArea className="min-h-0 h-full" contentClassName="px-[12px] py-[10px]">
                <ScopeDescription
                  ai={ai}
                  summariesCount={summaries.length}
                  windowKey={windowKey}
                  onRun={describe}
                  modelControl={describeModelSelect}
                />
              </ScrollArea>
            ) : view === "workflows" && llmReady ? (
              <ScrollArea className="min-h-0 h-full">
                <WorkflowsPanel
                  run={wf}
                  sessionsCount={sessions}
                  windowKey={windowKey}
                  onRun={runWorkflows}
                  onSelectTrace={onSelectTrace}
                  modelControl={describeModelSelect}
                />
              </ScrollArea>
            ) : view === "wiki" && llmReady ? (
              // No outer ScrollArea: the panel owns a two-column split whose
              // index and page body scroll independently, like Files.
              <WikiPanel
                run={wikiRun}
                onRun={runWiki}
                onAbort={() => wikiAbortRef.current?.abort()}
                scopeKey={scopeKey}
                scopeLabel={scopeLabel}
                steer={steerQuery}
                modelControl={describeModelSelect}
                onSelectTrace={onSelectTrace}
              />
            ) : view === "behaviours" ? (
              <ScrollArea className="min-h-0 h-full">
                <XrayFactoryPanel
                  scan={scan}
                  selection={factorySelection}
                  judge={judge}
                  onRunJudge={runJudge}
                  onRefine={runRefine}
                  onDraftArtifact={runArtifactDraft}
                  windowKey={windowKey}
                  modelControl={llmReady ? describeModelSelect : null}
                  coveredLenses={coveredLensIdsOf(registry)}
                />
              </ScrollArea>
            ) : view === "files" ? (
              <FilesPanel
                scan={fileScan}
                built={fileBuilt}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenReconstruction={openReconstruction}
                onBlameLine={blameLineAction}
                navScrollInstanceRef={setFileNavScrollInstance}
                contentScrollInstanceRef={setFileContentScrollInstance}
              />
            ) : view === "linear" ? (
              <LinearPanel
                scan={linearScan}
                built={linearBuilt}
                actors={linearBuiltAll?.actors || []}
                actorFilter={linearActor}
                onFilterActor={(id) => setLinearActor((cur) => (cur === id ? null : id))}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenIssue={openIssueDetail}
              />
            ) : view === "research" ? (
              <ResearchPanel
                scan={researchScan}
                built={researchBuilt}
                actors={researchBuiltAll?.actors || []}
                actorFilter={researchActor}
                onFilterActor={(id) => setResearchActor((cur) => (cur === id ? null : id))}
                labelOf={endpointLabelOf}
                onOpenPage={openPageDetail}
              />
            ) : view === "forge" ? (
              <ForgePanel
                scan={forgeScan}
                built={forgeBuilt}
                actors={forgeBuiltAll?.actors || []}
                actorFilter={forgeActor}
                onFilterActor={(id) => setForgeActor((cur) => (cur === id ? null : id))}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenTarget={openForgeTarget}
              />
            ) : view === "grafana" ? (
              <QueryOpsPanel
                scan={grafanaScan}
                built={grafanaBuilt}
                actors={grafanaBuiltAll?.actors || []}
                actorFilter={grafanaActor}
                onFilterActor={(id) => setGrafanaActor((cur) => (cur === id ? null : id))}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenTarget={openQueryTarget}
                emptyNote="No Grafana queries or alert-rule changes were recorded in this window."
              />
            ) : view === "dataq" ? (
              <QueryOpsPanel
                scan={dataqScan}
                built={dataqBuilt}
                actors={dataqBuiltAll?.actors || []}
                actorFilter={dataqActor}
                onFilterActor={(id) => setDataqActor((cur) => (cur === id ? null : id))}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenTarget={openQueryTarget}
                emptyNote="No analytics, Emissary, or SQL queries were recorded in this window."
              />
            ) : manifestLens ? (
              // A lens that declares a STRUCTURE gets the structural panel; one that
              // does not keeps the flat floor. Both are real readings — the split is
              // what the lens can say about its own shape, not a quality tier.
              manifestLens?.structure ? (
                <StructuredXrayPanel
                  lens={manifestLens}
                  structure={mStructure}
                  scan={mScan}
                  labelOf={endpointLabelOf}
                  traceOf={rowOf}
                  onSelectTrace={onSelectTrace}
                  windowNote={windowExtentNote}
                />
              ) : (
                <ManifestXrayPanel
                  lens={manifestLens}
                  scan={mScan}
                  built={mBuilt}
                  labelOf={endpointLabelOf}
                  traceOf={rowOf}
                  onSelectTrace={onSelectTrace}
                  windowNote={windowExtentNote}
                />
              )
            ) : view === "topiclinks" ? (
              <TopicFlowsPanel range={ctx?.range} scopePath={scopePath} onSelectTrace={onSelectTrace} steer={steerQuery} onSteerScope={ctx?.onSteerScope} />
            ) : view === "recall" ? (
              <RecallPanel
                scan={recallScan}
                built={recallBuilt}
                actors={recallBuiltAll?.actors || []}
                actorFilter={recallActor}
                onFilterActor={(id) => setRecallActor((cur) => (cur === id ? null : id))}
                labelOf={endpointLabelOf}
                onOpenConsultation={openRecallConsultation}
                onOpenSession={openRecalledSession}
              />
            ) : view === "artifacts" ? (
              <ArtifactsPanel
                scan={artifactScan}
                built={artifactBuilt}
                actors={artifactBuiltAll?.actors || []}
                actorFilter={artifactActor}
                onFilterActor={(id) => setArtifactActor((cur) => (cur === id ? null : id))}
                traceOf={rowOf}
                labelOf={endpointLabelOf}
                onSelectTrace={onSelectTrace}
                onOpenArtifact={openArtifactDetail}
              />
            ) : view === "lineage" ? (
              <LineagePanel
                scan={lineageScan}
                built={provenance}
                onSelectTrace={onSelectTrace}
              />
            ) : view === "prompts" ? (
              // The trace listing scrolls (and virtualizes) against its OWN
              // scroll root — TraceTable resolves the nearest
              // [data-content-scroll] viewport for row virtualization and
              // its sticky toolbar/header, so the tab must provide one.
              <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="px-[10px] pt-[8px] pb-[10px]">
                <TraceTable
                  rows={windowPending ? [] : rows}
                  selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
                  onSelectTrace={onSelectTrace}
                  showEndpoint={ctx?.traceLog?.showEndpoint}
                  loading={windowPending}
                  emptyMessage={term ? `No prompts in this window mention “${term}”.` : "No prompts in this window."}
                />
              </ScrollArea>
            ) : (
              <TermArchPanel
                ranked={ranked}
                summaries={summaries}
                selected={term}
                onSelect={(t) => { setTerm(t); if (t) setTemplateSel(null); }}
                pending={termsPending}
              />
            )}
          </div>
          {/* The scrubber gets the same solid containment as the panel
              above (it read as floating on the Sheet's dotted paper), and
              carries the prompts toggle — the listing is exactly what the
              brush selects, so its entry point lives beside the count. */}
          <div className="mt-[8px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[10px] py-[8px]">
            <ScanWindowScrubber
              {...scrubber}
              filters={
                term ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-[4px]">
                    <FilterChip label={`${term} · ${rows.length} ×`} active onClick={() => setTerm(null)} />
                  </div>
                ) : null
              }
              actions={
                <button
                  type="button"
                  onClick={() => {
                    if (view === "prompts") {
                      setView(lastAnalysisView.current);
                      return;
                    }
                    if (view === "files") rememberFileScroll();
                    setView("prompts");
                  }}
                  className={[
                    "flex h-[24px] shrink-0 cursor-pointer items-center rounded-[2px] border px-[8px] text-[10px] uppercase tracking-[0.04em] transition-colors",
                    view === "prompts"
                      ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
                      : "border-[color:var(--line)] bg-[color:var(--control-bg)] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]",
                  ].join(" ")}
                >
                  Prompts
                  {windowTraces.length ? ` · ${term ? rows.length : windowTraces.length}` : ""}
                </button>
              }
            />
          </div>
        </div>
      </Sheet>
    </div>
  );
}
